export interface Product {
  id: string;
  slug: string;
  name: string;
  category: string;
  categorySlug: string;
  shortDescription: string;
  description: string;
  descriptionKo?: string;
  features: string[];
  image: string;
  gallery: string[];
}

export interface Category {
  id: string;
  slug: string;
  name: string;
  description: string;
  image: string;
  products: Product[];
  path?: string;
}

const veterinaryTrainingProducts: Product[] = [
  {
    id: "vt-1",
    slug: "canine-auscultation-trainer",
    name: "Canine Auscultation Trainer",
    category: "Veterinary Training",
    categorySlug: "veterinary-training",
    shortDescription: "Advanced canine simulator for mastering cardiac and respiratory auscultation in veterinary training.",
    description: `The Canine Auscultation Trainer is a high-fidelity simulator designed for veterinary students and professionals to practice cardiac and pulmonary auscultation. Its flexible chest material allows learners to palpate ribs, while internal lighting reveals rib shadows for precise anatomical orientation. Red indicator lights mark all heart and lung auscultation points, guiding accurate stethoscope placement.

Sound is delivered via NFC-based transfer and controlled through a Wi-Fi mobile app, providing high-fidelity audio that is far more precise than traditional speaker systems, ensuring realistic auscultation training.

The trainer includes extensive sound library:

Heart Sounds Module
• Atrial Fibrillation (AFib)
• Mitral Regurgitation (MR)
• Mitral Valve Click
• Normal Heartbeat
• Patent Ductus Arteriosus (PDA)
• Pulmonic Stenosis
• Respiratory Crackles and MR Murmurs
• Subaortic Stenosis (SAS)
• Ventricular Premature Contractions (VPC)
• Ventricular Septal Defect (VSD)

Lung Sounds Module
• Tracheal
• Vesicular
• Bronchial-Vesicular
• Wheezes
• Monophonic Wheeze
• Pleural Friction Rub
• Stridor
• Cavernous
• Crackles
• Pulmonary Edema
• Puppy Breath

This simulator provides a safe, repeatable, and hands-on learning environment, enabling learners to build confidence and competence before working with live patients.`,
    features: [
      "Flexible material over the chest allows rib palpation",
      "Red indicator lights mark all heart and lung auscultation points",
      "NFC-based audio and Wi-Fi mobile app control",
      "Accurate sound reproduction, superior to traditional speaker systems",
      "Durable design for repeated training"
    ],
    image: "/images/Canine Auscultation Trainer/Gemini_Generated_Image_hhc39thhc39thhc3.jpg",
    gallery: [
      "/images/Canine Auscultation Trainer/Gemini_Generated_Image_hhc39thhc39thhc3.jpg",
      "/images/Canine Auscultation Trainer/Gemini_Generated_Image_wy0rvawy0rvawy0r.jpg",
      "/images/Canine Auscultation Trainer/Gemini_Generated_Image_m0jw1lm0jw1lm0jw.jpg",
      "/images/Canine Auscultation Trainer/Gemini_Generated_Image_3s07vc3s07vc3s07.jpg"
    ]
  },
  {
    id: "vt-2",
    slug: "anatomical-udder-model",
    name: "Anatomical Udder Model",
    category: "Veterinary Training",
    categorySlug: "veterinary-training",
    shortDescription: "Precision educational model for studying bovine mammary gland anatomy and teat structure.",
    description: `The Anatomical Udder Model is a precision-crafted educational tool designed to give veterinary students an in-depth understanding of bovine mammary gland anatomy. The model provides a cross-sectional view of the udder, accurately depicting the glandular tissue, teat cistern, streak canal, and suspensory apparatus, allowing learners to study both macro- and micro-anatomical structures.

This model is particularly valuable for teaching dairy science, mastitis pathology, milking mechanics, and clinical procedures, offering hands-on insight into normal anatomy and practical examination techniques.`,
    features: [
      "Anatomically accurate representation of the bovine udder",
      "Cross-sectional view of internal mammary structures",
      "Detailed teat cistern, streak canal, and suspensory ligament anatomy",
      "Hand-painted, durable construction for long-term use",
      "Ideal for dairy science, mastitis pathology, and veterinary curriculum"
    ],
    image: "/images/Anatomical Udder Model/Gemini_Generated_Image_alw7ctalw7ctalw7.jpg",
    gallery: [
      "/images/Anatomical Udder Model/Gemini_Generated_Image_alw7ctalw7ctalw7.jpg",
      "/images/Anatomical Udder Model/Gemini_Generated_Image_haamlchaamlchaam.jpg",
      "/images/Anatomical Udder Model/Gemini_Generated_Image_mfqu9cmfqu9cmfqu.jpg",
      "/images/Anatomical Udder Model/Gemini_Generated_Image_pu3z49pu3z49pu3z.jpg"
    ]
  },
  {
    id: "vt-3",
    slug: "functional-udder-model",
    name: "Functional Udder Model",
    category: "Veterinary Training",
    categorySlug: "veterinary-training",
    shortDescription: "Hands-on functional udder simulator for practicing milking techniques and teat examination procedures.",
    description: "The Functional Udder Model is a realistic, hands-on training simulator that enables veterinary and agricultural students to practice milking techniques and teat examination in a controlled environment. Featuring four functional teats with a refillable fluid reservoir, the model closely mimics the feel and resistance of a real bovine udder during milking. Students can practice both hand milking and machine milking attachment, as well as teat dipping, infusion, and clinical examination procedures. The model is an essential tool for dairy husbandry training and pre-clinical veterinary education.",
    features: [
      "Four functional teats with realistic feel",
      "Refillable fluid reservoir system",
      "Practice hand and machine milking techniques",
      "Teat dipping and infusion simulation",
      "Soft, durable silicone construction",
      "Portable and easy to clean"
    ],
    image: "/images/Functional Udder Model/Gemini_Generated_Image_doijdvdoijdvdoij.jpg",
    gallery: [
      "/images/Functional Udder Model/Gemini_Generated_Image_doijdvdoijdvdoij.jpg",
      "/images/Functional Udder Model/Gemini_Generated_Image_9ctylc9ctylc9cty.jpg",
      "/images/Functional Udder Model/Gemini_Generated_Image_44k6qn44k6qn44k6.jpg",
      "/images/Functional Udder Model/Gemini_Generated_Image_g6c86hg6c86hg6c8.jpg",
      "/images/Functional Udder Model/Gemini_Generated_Image_pbrltrpbrltrpbrl.jpg"
    ]
  },
  {
    id: "vt-4",
    slug: "cattle-delivery-simulator",
    name: "Cattle Delivery Simulator",
    category: "Veterinary Training",
    categorySlug: "veterinary-training",
    shortDescription: "Life-size bovine calving simulator for hands-on obstetric training, featuring realistic fetal presentation options and anatomically accurate materials.",
    description: `The Cattle Delivery Simulator is a life-size (1:1 scale) replica of the rear section of a cow's body, with an internal fetal mock-up that enables comprehensive obstetric training. A removable access panel allows instructors and students to observe and carry out maternity care procedures while positioning the fetus within the simulator. The fetal layout is designed to support practice of calving with multiple presentation variants, preparing students for real-world delivery scenarios.

Constructed with high anatomical accuracy, the simulator uses materials that closely replicate the living tissue of an animal. For convenient classroom or lab placement, the unit is mounted on a mobile platform.

Equipment:
• Cattle delivery simulator
• Calf simulator
• Gloves (1 pair)
• Lubricant (1 bottle)

Documentation:
• Passport of product

Material:
5 mm polyvinyl chloride, two-component cold-cured plastic, silicone, dacron, polyurethane, 15 mm MDF platform

Dimensions: 1400 × 900 × 1700 mm
Weight: 50 kg`,
    features: [
      "Life-size 1:1 scale bovine rear-body replica",
      "Removable panel for direct observation of calving procedures",
      "Multiple fetal presentation options for varied training scenarios",
      "High anatomical accuracy with tissue-realistic materials",
      "Mounted on a mobile platform for easy repositioning",
      "Includes calf simulator, gloves, and lubricant"
    ],
    image: "/images/Cattle Delivery Simulator/b1001.jpg",
    gallery: [
      "/images/Cattle Delivery Simulator/b1001.jpg",
      "/images/Cattle Delivery Simulator/V1001-2-1.jpg",
      "/images/Cattle Delivery Simulator/V1001-3-1.jpg",
      "/images/Cattle Delivery Simulator/V1001-4-1.jpg",
      "/images/Cattle Delivery Simulator/V1001-5.jpg"
    ]
  },
  {
    id: "vt-5",
    slug: "gym-complex-injuries-and-injuries",
    name: "Gym Complex \"Injuries and Injuries\"",
    category: "Veterinary Training",
    categorySlug: "veterinary-training",
    shortDescription: "Life-size cow thigh simulator with a pressurized blood supply system for practicing wound management and simple surgical procedures on realistic skin lesions.",
    description: `The Gym Complex "Injuries and Injuries" is a 1:1 scale model of a cow's thigh that simulates a wide range of skin injuries, lesions, and diseases. To provide a visually and anatomically realistic representation, the simulator incorporates a built-in compressor that generates a pressurized blood supply system beneath the simulated skin surface.

The set includes special insert simulators that allow students to practice performing simple surgical operations and postoperative procedures in a controlled, repeatable training environment.

Equipment:
• Training complex "Injuries and injuries"
• Wounds (1 set)
• 220V power adapter

Documentation:
• Passport of product

Material:
Chipboard, polyurethane, ABS

Dimensions: 450 × 1400 × 300 mm
Weight: 15 kg`,
    features: [
      "1:1 scale cow thigh model with realistic skin tissue simulation",
      "Built-in compressor for pressurized blood supply simulation",
      "Anatomically realistic representation of injuries, lesions, and diseases",
      "Interchangeable wound inserts for varied surgical practice",
      "Supports simple surgical operations and postoperative procedures",
      "Durable chipboard, polyurethane, and ABS construction"
    ],
    image: "/images/Gym complex Injuries and Injuries/V1006-1.jpg",
    gallery: [
      "/images/Gym complex Injuries and Injuries/V1006-1.jpg",
      "/images/Gym complex Injuries and Injuries/V1006-2.jpg",
      "/images/Gym complex Injuries and Injuries/V1006-3.jpg",
      "/images/Gym complex Injuries and Injuries/V1006-4.jpg"
    ]
  },
  {
    id: "vt-6",
    slug: "model-hind-leg-dog-broken-femur",
    name: "Model of the Hind Leg of a Dog with a Broken Femur",
    category: "Veterinary Training",
    categorySlug: "veterinary-training",
    shortDescription: "Detailed canine pelvic limb simulator with a reproduced femoral fracture for practicing surgical reposition of bone fragments.",
    description: `This simulator is designed as a model of the pelvic limb of a dog with a reproduced femoral fracture. It is intended for working out the technique of surgical reposition of bone fragments. The simulator reproduces the detailed anatomy of the dog's hind leg, including the coat, skin and muscles, which allows students to go through all the stages of surgery in the correct sequence.

The simulator is designed for training students of educational institutions of veterinary profile.

Equipment:
• Model of the hind leg of a dog with a broken femur
• Imitation of the femur (5 pcs.)

Documentation:
• Passport of product
• User Manual

Material:
3 mm polyvinyl chloride, two-component cold-cured plastic, silicone

Dimensions: 500 × 260 × 110 mm
Weight: 2 kg`,
    features: [
      "Anatomically detailed canine pelvic limb with coat, skin, and muscles",
      "Reproduced femoral fracture for realistic surgical training",
      "Covers all stages of surgical reposition in correct sequence",
      "Includes 5 interchangeable femur imitations for repeated practice",
      "Designed for veterinary students and educational institutions",
      "Lightweight and compact for easy classroom use"
    ],
    image: "/images/Model of the hind leg of a dog with a broken femur/V1033-1-1.jpg",
    gallery: [
      "/images/Model of the hind leg of a dog with a broken femur/V1033-1-1.jpg",
      "/images/Model of the hind leg of a dog with a broken femur/V1033-2.jpg"
    ]
  },
  {
    id: "vt-7",
    slug: "simulator-rectal-examination-artificial-insemination-cattle",
    name: "Simulator \"Rectal Examination and Artificial Insemination of Cattle\"",
    category: "Veterinary Training",
    categorySlug: "veterinary-training",
    shortDescription: "Full-scale bovine rear-body simulator for practicing rectal examination and artificial insemination, featuring palpable internal organs and a pulsating uterine artery.",
    description: `This simulator is designed to practice the skills of rectal examination and artificial insemination of cattle by students of veterinary educational institutions. The simulator is a full-scale model of the rear part of the body of a cow with imitation of internal organs: the rectum and reproductive tract, as well as pelvic bones.

Various stages of fetal development are determined when conducting a palpatory examination. For a more realistic procedure, the model reproduces the uterine artery, which pulsates during the examination. There is an observation window in the side of the simulator to control the correctness of the procedure. For convenient placement in the classroom, the complex is installed on a mobile platform.

Recommended for equipping veterinary educational institutions, centers for training veterinarians, and large farms.

Equipment:
• Simulator "Rectal examination and artificial insemination of cattle"
• Uterus simulator
• Sexual organ simulator
• Gloves (1 pair)
• Lubricant (1 bottle)

Documentation:
• Passport of product

Dimensions: 1400 × 900 × 1700 mm
Weight: 50 kg`,
    features: [
      "Full-scale model of the bovine rear body with internal organ imitation",
      "Palpable rectum, reproductive tract, and pelvic bones",
      "Pulsating uterine artery for realistic procedural simulation",
      "Multiple fetal development stages detectable via palpation",
      "Observation window for instructor monitoring of technique",
      "Mounted on a mobile platform for easy classroom placement"
    ],
    image: "/images/Simulator Rectal examination and artificial insemination of cattle/V1002-3.jpg",
    gallery: [
      "/images/Simulator Rectal examination and artificial insemination of cattle/V1002-3.jpg",
      "/images/Simulator Rectal examination and artificial insemination of cattle/V1002-1.jpg",
      "/images/Simulator Rectal examination and artificial insemination of cattle/V1002-2.jpg",
      "/images/Simulator Rectal examination and artificial insemination of cattle/V1002-4.jpg",
      "/images/Simulator Rectal examination and artificial insemination of cattle/V1002-5.jpg",
      "/images/Simulator Rectal examination and artificial insemination of cattle/V1002-6.jpg"
    ]
  },
  {
    id: "vt-8",
    slug: "simulator-cpr-skills-dogs",
    name: "Simulator for Practicing CPR Skills in Dogs",
    category: "Veterinary Training",
    categorySlug: "veterinary-training",
    shortDescription: "Life-size anatomically correct canine simulator for cardiopulmonary resuscitation training, including intubation, auscultation, pleural puncture, and IV procedures.",
    description: `This simulator is an anatomically correct, life-size model of a dog designed for cardiopulmonary resuscitation (CPR) training. It supports tracheal intubation, pleural puncture procedures for hydro- and pneumothorax, auscultation of the heart and lungs, and intravenous procedures. The model is made of a material that imitates the animal's coat.

Anatomical landmarks available during CPR:
• Ribs
• Realistic oral cavity with all organs
• Mobile limbs and chest

The simulator is controlled from a multi-function controller that displays performance indicators including compression and decompression depth, ventilator volume, and heart and lung auscultation results. Correct lung ventilation is accompanied by visible chest rise, enabling evaluation of basic CPR technique.

Highly sensitive sensors integrated into anatomically correct auscultation points reproduce specific sounds when touched correctly with a stethoscope.

Heart Tones Module
• Diastolic murmur
• Ventricular septal defect
• Pendulum-like rhythm
• Normal heart tones
• Weakened first heart tone
• First heart tone split
• Gallop rhythm
• Physiological third heart tone
• Pericardial friction noise
• Extrasystole
• Atrial fibrillation

Breathing Sounds Module
• Wet wheezing
• Rough wet noises
• Rough breathing noises
• Crepitation
• Small bubbly wet wheezes
• Normal bronchial respiration
• Normal vesicular respiration
• Abnormal bronchial respiration
• Shortness of breath
• Whistling wheezes
• Pleural friction noise

Equipment:
• Dog model
• Control unit
• Replacement insert for intravenous injection
• Endotracheal tube
• 20 ml medical disposable injection syringe with needle (2 pcs.)
• Dry blood concentrate (5 g)
• Stand with artificial blood container

Documentation:
• Passport of product
• User Manual

Material:
Polyurethane, two-component plastic, synthetic material

Dimensions: 1270 × 500 × 250 mm
Weight: 11 kg`,
    features: [
      "Life-size anatomically correct canine model with coat-like material",
      "Supports CPR, tracheal intubation, pleural puncture, and IV procedures",
      "Multi-function controller with compression depth and ventilation volume feedback",
      "Visible chest rise confirms correct lung ventilation",
      "11 heart tone sounds and 11 breathing sounds via sensitive auscultation sensors",
      "Mobile limbs and chest with realistic anatomical landmarks"
    ],
    image: "/images/Simulator for practicing CPR skills in dogs/V1029-1.jpg",
    gallery: [
      "/images/Simulator for practicing CPR skills in dogs/V1029-1.jpg",
      "/images/Simulator for practicing CPR skills in dogs/V1029-2.jpg",
      "/images/Simulator for practicing CPR skills in dogs/V1029-3.jpg"
    ]
  }
];

const surgicalSkillsProducts: Product[] = [  {
    id: "ss-99",
    slug: "pelvic-cavity-skills-trainer",
    name: "Pelvic Cavity Skills Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "TMC-exclusive full female pelvic anatomy simulator featuring bone, muscle, vessels, nerves, lymph nodes, uterus, bladder, and rectum for comprehensive pelvic surgery and suturing training.",
    description: `The Pelvic Cavity Skills Trainer is a comprehensive simulation model replicating the complete anatomical structure of the female pelvis, including bone, muscle, vessels, nerves, lymph nodes, uterus, bladder, and rectum. Each organ and tissue component is crafted from differentiated materials to deliver realistic tactile response during surgical manipulation.

The entire female pelvic anatomy has been accurately reproduced, making the model compatible with a wide range of related surgical procedures. All materials are both suturable and highly durable, enabling extensive repeated practice without loss of fidelity.

Each component is independently replaceable, allowing training centers to purchase extra parts and continue reusing the remaining components for long-term, cost-efficient training. This simulator is well-suited for medical schools, gynecology and general surgery residency programs, and clinical skills laboratories requiring high-realism pelvic surgical training.`,
    features: [
      "Complete female pelvic anatomy: bone, muscle, vessels, nerves, lymph nodes, uterus, bladder, and rectum",
      "Differentiated materials for each organ to deliver realistic tissue response",
      "Suturable and high-durability construction for repeated use",
      "Anatomically accurate structure covering all major female pelvic procedures",
      "Individually replaceable components for long-term cost-effective training"
    ],
    image: "/images/P-PCS-01/P-PCS-01_Pelvic Cavity Skills Trainer 1.jpg",
    gallery: [
      "/images/P-PCS-01/P-PCS-01_Pelvic Cavity Skills Trainer 1.jpg",
      "/images/P-PCS-01/P-PCS-01_Pelvic Cavity Skills Trainer 2.jpg",
      "/images/P-PCS-01/P-PCS-01_Pelvic Cavity Skills Trainer 3.jpg",
      "/images/P-PCS-01/P-PCS-01_Pelvic Cavity Skills Trainer 4.jpg"
    ]
  },
  {
    id: "ss-88",
    slug: "gluteal-intramuscular-injection-trainer",
    name: "Gluteal Intramuscular Injection Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Four-layer anatomical gluteal trainer for IM injection landmark identification, needle-angle training, and depth-dependent tactile feedback simulation.",
    description: `The Gluteal Intramuscular Injection Trainer is designed to provide a realistic training platform for mastering the correct anatomical landmarks, needle angle, insertion depth, and medication delivery involved in intramuscular injections. Its four-layer structure—epidermis, dermis, subcutaneous tissue, and muscle—accurately reproduces the density, elasticity, and tactile transitions felt during actual IM procedures.

Trainees can feel each tissue layer as the needle advances, including realistic muscle resistance at the correct depth. The trainer supports actual fluid injection and includes a natural drainage pathway to maintain hygiene and usability.

Constructed from high-durability medical-grade silicone, the model maintains its structure and tactile fidelity even after extensive repeated practice, making it ideal for medical schools, nursing programs, hospital education centers, and clinical skills laboratories requiring reliable and realistic IM-injection simulation.`,
    features: [
      "Anatomically accurate gluteal structure modeled from real human morphology",
      "Four-layer construction: epidermis, dermis, subcutaneous fat, and muscle",
      "Provides lifelike tissue resistance during proper IM needle insertion",
      "Depth-dependent tactile feedback replicates real clinical sensation",
      "High-elasticity medical-grade silicone suitable for repeated use"
    ],
    image: "/images/P-IMG-01/P-IMG-01_Gluteal Intramuscular injection 1.jpg",
    gallery: [
      "/images/P-IMG-01/P-IMG-01_Gluteal Intramuscular injection 1.jpg",
      "/images/P-IMG-01/P-IMG-01_Gluteal Intramuscular injection 2.jpg",
      "/images/P-IMG-01/P-IMG-01_Gluteal Intramuscular injection spec.jpg"
    ]
  },
  {
    id: "ss-89",
    slug: "inguinal-hernia-model",
    name: "Inguinal Hernia Model",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Multilayer anatomical inguinal hernia simulator for direct/indirect hernia repair, dissection, mesh placement, and Lichtenstein repair training — open, laparoscopic, and robotic approaches.",
    description: `The Inguinal Hernia Model is engineered to provide a highly realistic and comprehensive environment for learning inguinal-hernia anatomy and surgical repair. Co-developed with Intuitive Surgical, the simulator replicates multilayer abdominal structures—including skin, fat, fascia, muscle, and peritoneum—along with accurate pathways of direct and indirect hernias.

The model enables trainees to practice identifying hernia sacs, performing safe dissection, exposing the defect, and completing reconstruction techniques such as mesh positioning and Lichtenstein repair. Lifelike tactile response during incision, separation, and suturing enhances the realism of both open and minimally invasive surgical training.

Constructed from durable medical-grade silicone, it withstands repeated use while retaining anatomical fidelity. Ideal for medical schools, surgical residency programs, laparoscopic and robotic-surgery centers, and hands-on hernia-repair workshops.`,
    features: [
      "Anatomically precise multilayer structure of the inguinal region",
      "Realistic reproduction of both direct and indirect inguinal hernias",
      "Detailed abdominal-wall layering: skin, fat, fascia, muscle, and peritoneum",
      "Provides lifelike resistance during incision, dissection, and suturing",
      "Supports mesh placement and hernia-defect repair, including Lichtenstein repair",
      "Durable medical-grade silicone suitable for repeated simulation"
    ],
    image: "/images/P-INH-01/P-INH-01_1.jpg",
    gallery: [
      "/images/P-INH-01/P-INH-01_1.jpg",
      "/images/P-INH-01/P-INH-01_2.jpg",
      "/images/P-INH-01/P-INH-01_3.jpg",
      "/images/P-INH-01/P-INH-01_4.jpg",
      "/images/P-INH-01/P-INH-01_5.jpg",
      "/images/P-INH-01/P-INH-01_Spec.png"
    ]
  },
  {
    id: "ss-79",
    slug: "shoulder-surgery-trainer",
    name: "Shoulder Surgery Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "High-fidelity multi-layer shoulder trainer with water-fillable module for open and arthroscopic surgery simulation, incision, suturing, dissection, and multi-approach training.",
    description: `The Shoulder Surgery Trainer provides a high-fidelity platform for learning open and arthroscopic shoulder procedures. It accurately replicates the anatomical structures of the human shoulder—including skin, fat, muscle, ligaments, and joint layers—delivering lifelike resistance during incision, dissection, and suturing.

The water-fillable shoulder module enables realistic arthroscopic simulation, including visualization and instrument handling under fluid-filled conditions. The multilayer structure supports full procedural workflow practice, from surface incision to deep joint access.

Constructed with premium silicone and reinforced composite materials, the trainer maintains its shape even with extensive repeated use. Its modular shoulder unit can be replaced, ensuring long-term durability for medical schools, orthopedic programs, surgical simulation centers, and device-training environments.`,
    features: [
      "Anatomically accurate reproduction of shoulder bones, ligaments, muscles, and joint structures",
      "Provides realistic tactile feedback that mimics tissue resistance during incision and suturing",
      "Supports both open and arthroscopic shoulder surgery training",
      "Water-fillable shoulder module enables realistic fluid-filled arthroscopic simulation",
      "High-durability medical-grade silicone withstands repeated cutting and suturing",
      "Modular design allows replacement of shoulder components for long-term cost-effective use"
    ],
    image: "/images/L-SHS-01/L-SHS-01_Shoulder Surgery Trainer 1.jpg",
    gallery: [
      "/images/L-SHS-01/L-SHS-01_Shoulder Surgery Trainer 1.jpg",
      "/images/L-SHS-01/L-SHS-01_Shoulder Surgery Trainer 2.jpg",
      "/images/L-SHS-01/L-SHS-01_Shoulder Surgery Trainer 3.jpg",
      "/images/L-SHS-01/L-SHS-01_Shoulder Surgery Trainer 4.jpg",
      "/images/L-SHS-01/L-SHS-01_Shoulder Surgery Trainer 5.jpg",
      "/images/L-SHS-01/L-SHS-01_Shoulder Surgery Trainer 6.jpg",
      "/images/L-SHS-01/L-SHS-01_Shoulder Surgery Trainer 7.jpg",
      "/images/L-SHS-01/L-SHS-01_Shoulder Surgery Trainer 8.jpg"
    ]
  },
  {
    id: "ss-72",
    slug: "intravenous-injection-arm-trainer-wearable",
    name: "Intravenous Injection Arm Trainer – Wearable",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Wearable multi-layer arm trainer with motorized blood circulation for realistic venipuncture, backflow confirmation, and ergonomic IV injection posture training.",
    description: `This wearable intravenous injection arm trainer provides a high-fidelity platform for practicing venipuncture, blood sampling, and IV infusion. The model replicates human arm anatomy with layered silicone that simulates skin, subcutaneous fat, muscle, and vein structures, offering realistic resistance during needle insertion.

The built-in motorized circulation system generates natural blood backflow when the vein is successfully punctured, helping learners confirm proper needle placement. The wearable design allows trainees to practice injections in a clinically accurate posture, enhancing realism and procedural confidence.

A detachable manikin arm model supports individual practice, while reinforced vein tubing maintains durability during repeated high-frequency use. Replaceable skin pads and modular components ensure hygienic training and reduce maintenance effort.

This model is widely used in hospitals, nursing schools, medical universities, and emergency education programs requiring dependable and realistic IV injection simulation.`,
    features: [
      "Anatomically accurate design replicating the shape and texture of a real human arm",
      "Multi-layer structure including skin, fat, muscle, and vein tubing for lifelike needle resistance",
      "Motor-driven blood circulation creates natural backflow upon successful puncture",
      "Wearable configuration enables practice in true clinical posture and ergonomic needle handling",
      "Reinforced internal vein tubing prevents tearing during repeated needle insertion",
      "Replaceable skin pad reduces maintenance cost and supports hygienic long-term use"
    ],
    image: "/images/L-IVW-01/L-IVW-01_Intravenous Injection Arm Trainer Wearable 1.jpg",
    gallery: [
      "/images/L-IVW-01/L-IVW-01_Intravenous Injection Arm Trainer Wearable 1.jpg",
      "/images/L-IVW-01/L-IVW-01_Intravenous Injection Arm Trainer Wearable 2.jpg",
      "/images/L-IVW-01/L-IVW-01_Intravenous Injection Arm Trainer Wearable 3.jpg",
      "/images/L-IVW-01/L-IVW-01_Intravenous Injection Arm Trainer Wearable 4.jpg",
      "/images/L-IVW-01/L-IVW-01_Intravenous Injection Arm Trainer Wearable 5.jpg",
      "/images/L-IVW-01/L-IVW-01_Intravenous Injection Arm Trainer Wearable 6.jpg",
      "/images/L-IVW-01/L-IVW-01_Intravenous Injection Arm Trainer Wearable 7.jpg",
      "/images/L-IVW-01/L-IVW-01_Intravenous Injection Arm Trainer Wearable spec.jpg"
    ]
  },
  {
    id: "ss-66",
    slug: "femoral-puncture-trainer",
    name: "Femoral Puncture Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "High-fidelity multi-layer femoral puncture trainer with motorized blood circulation, ultrasound guidance support, and replaceable skin and vessel components.",
    description: `This femoral puncture trainer provides a highly realistic platform for practicing interventional procedures and vascular access techniques. Its anatomical structure is modeled after the human femoral region, allowing trainees to learn accurate puncture site identification and to control needle angle and depth.

The multi-layer configuration—skin, fat, muscle, and artery—delivers lifelike tactile resistance during needle insertion. The motor-driven circulation system produces a natural blood backflow response when the vessel is correctly punctured, enhancing procedural realism.

The trainer supports ultrasound-guided access using external probes, enabling users to practice image interpretation and guided needle placement. Replaceable vessels and skin pads ensure long-term usability and reduce maintenance costs, even in high-frequency training settings.

Constructed from durable medical-grade silicone, this model maintains structural integrity over time and is suitable for medical schools, hospitals, emergency medicine courses, and interventional training programs requiring a reliable femoral access simulator.`,
    features: [
      "High-fidelity anatomical structure modeled from the human femoral region",
      "Multi-layer design including skin, fat, muscle, and femoral artery",
      "Motorized blood circulation system providing realistic backflow during vessel entry",
      "Supports ultrasound-guided puncture with real-time imaging",
      "Natural needle resistance and pressure feedback upon vessel penetration",
      "Replaceable vessel tubing and skin pads enable repeated, hygienic training",
      "Durable medical-grade silicone construction for extended use"
    ],
    image: "/images/L-FEP-01/L-FEP-01_Femoral Puncter Trainer 1.jpg",
    gallery: [
      "/images/L-FEP-01/L-FEP-01_Femoral Puncter Trainer 1.jpg",
      "/images/L-FEP-01/L-FEP-01_Femoral Puncter Trainer 2.jpg",
      "/images/L-FEP-01/L-FEP-01_Femoral Puncter Trainer 3.jpg",
      "/images/L-FEP-01/L-FEP-01_Femoral Puncter Trainer 4.jpg",
      "/images/L-FEP-01/L-FEP-01_Femoral Puncter Trainer 5.jpg"
    ]
  },
  {
    id: "ss-64",
    slug: "thyroidectomy-trainer-small",
    name: "Thyroidectomy Trainer – Small",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Compact multi-layer anatomical neck model for standard thyroidectomy training with replaceable thyroid, trachea, muscle, vessel, and nerve components.",
    description: `This small-sized thyroidectomy trainer provides a realistic platform for practicing standard thyroidectomy procedures. The anatomical configuration models human neck layers—including skin, cervical muscles, thyroid gland, parathyroid glands, and supporting tissues—allowing learners to develop precise surgical orientation.

Its multi-layer silicone construction delivers realistic tactile feedback during incision, dissection, and organ handling. Integrated vascular and nerve elements enable simulation of clinical scenarios such as delicate tissue preservation, controlled dissection, and safe gland removal.

Replaceable components, including the thyroid and surrounding structures, support varied procedural approaches and repeated training without loss of fidelity. This trainer is ideal for medical schools, residency programs, and simulation centers seeking a durable and anatomically accurate thyroidectomy training tool.`,
    features: [
      "Anatomically accurate neck structure for realistic surgical simulation",
      "Multi-layer silicone provides lifelike resistance during incision and dissection",
      "Includes thyroid, vessels, muscles, and nerves for comprehensive procedure practice",
      "Highly durable design supporting repeated incision and suturing sessions"
    ],
    image: "/images/H-THS-01/H-THS-01_1.jpg",
    gallery: [
      "/images/H-THS-01/H-THS-01_1.jpg",
      "/images/H-THS-01/H-THS-01_2.jpg",
      "/images/H-THS-01/H-THS-01_3.jpg",
      "/images/H-THS-01/H-THS-01_4.jpg",
      "/images/H-THS-01/H-THS-01_5.jpg",
      "/images/H-THS-01/H-THS-01_6.jpg",
      "/images/H-THS-01/H-THS-01_Spec.png"
    ]
  },
  {
    id: "ss-63",
    slug: "thyroidectomy-trainer",
    name: "Thyroidectomy Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "High-fidelity multi-layer neck model for endoscopic thyroidectomy training with replaceable thyroid, trachea, muscle, vessel, and nerve components.",
    description: `This thyroidectomy trainer provides a high-fidelity environment for practicing minimally invasive and endoscopic thyroid surgery. The model replicates anatomical layers of the human neck—including skin, muscle, thyroid, and parathyroid structures—allowing learners to understand depth, orientation, and tissue relationships.

The multi-layer silicone material provides realistic tactile resistance during incision, dissection, and ligation. Trainees can practice scope navigation, tissue handling, and procedural sequencing with a high degree of realism.

The replaceable thyroid module includes parathyroid structures, enabling repeated practice without loss of fidelity. Additional replaceable components such as the trachea, muscles, vessels, and nerves support varied surgical scenarios and reconstruction techniques.

This trainer is ideal for surgical residents, ENT specialists, and minimally invasive endocrine surgery programs. Its durability and anatomical precision make it suitable for simulation centers, medical schools, and professional skill-development courses requiring advanced thyroidectomy training.`,
    features: [
      "Realistic neck anatomy designed for surgical training",
      "Supports endoscopic visualization, tissue dissection, and thyroid removal",
      "Multi-layer silicone structure replicates lifelike resistance during incision",
      "Replaceable components allow repeated practice and multiple procedural scenarios"
    ],
    image: "/images/H-THR-01/H-THR-01_Thyroidectomy Trainer 1.jpg",
    gallery: [
      "/images/H-THR-01/H-THR-01_Thyroidectomy Trainer 1.jpg",
      "/images/H-THR-01/H-THR-01_Thyroidectomy Trainer 2.jpg",
      "/images/H-THR-01/H-THR-01_Thyroidectomy Trainer 3.jpg",
      "/images/H-THR-01/H-THR-01_Thyroidectomy Trainer 4.jpg",
      "/images/H-THR-01/H-THR-01_Thyroidectomy Trainer 5.jpg",
      "/images/H-THR-01/H-THR-01_Thyroidectomy Trainer 6.jpg",
      "/images/H-THR-01/H-THR-01_Thyroidectomy Trainer 7.jpg",
      "/images/H-THR-01/H-THR-01_Thyroidectomy Trainer 8.jpg",
      "/images/H-THR-01/H-THR-01_Thyroidectomy Trainer Spec.jpg"
    ]
  },
  {
    id: "ss-61",
    slug: "face-muscle-model",
    name: "Face Muscle Model",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Anatomically accurate layered facial muscle model in soft elastic silicone for studying muscle origins, insertions, fiber directions, and expression mechanics.",
    description: `This face muscle model provides a clear and realistic representation of the layered structure of human facial muscles. Based on anatomical data, it illustrates major muscle groups and their functional relationships, helping users understand expression mechanics and how muscles interact during movement.

The silicone material replicates the soft and elastic feel of real muscle tissue. Origins, insertions, and fiber directions are distinctly represented, offering intuitive visual guidance for anatomy learning and clinical applications.

The model is suitable for medical schools, dental programs, aesthetic medicine training, and anatomy education institutions. It supports detailed study of facial anatomy, expression analysis, surgical planning, and other clinical practice scenarios requiring precise anatomical visualization.`,
    features: [
      "Anatomically accurate reproduction of facial muscle layers",
      "Soft, elastic material mimicking real muscle texture and tension",
      "Clearly defined muscle origins, insertions, and fiber directions",
      "Enables intuitive observation of expression-related muscle arrangement and connections"
    ],
    image: "/images/H-FAM-01/H-FAM-01_Face Muscle Model 1.jpg",
    gallery: [
      "/images/H-FAM-01/H-FAM-01_Face Muscle Model 1.jpg",
      "/images/H-FAM-01/H-FAM-01_Face Muscle Model 2.jpg",
      "/images/H-FAM-01/H-FAM-01_Face Muscle Model 3.jpg",
      "/images/H-FAM-01/H-FAM-01_Face Muscle Model 4.jpg",
      "/images/H-FAM-01/H-FAM-01_Face Muscle Model Spec.jpg"
    ]
  },
  {
    id: "ss-62",
    slug: "tracheotomy-trainer",
    name: "Tracheotomy Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Multi-layer anatomical neck model with replaceable skin and trachea components for realistic tracheotomy incision, tube insertion, and airway management training.",
    description: `This tracheotomy trainer offers a realistic environment for practicing incision, tracheal access, and airway management. Its layered design includes skin, neck muscles, trachea, and thyroid cartilage, enabling learners to understand anatomical depth and orientation during procedures.

The silicone layers provide lifelike tactile resistance, allowing users to simulate incision depth, palpation, and tube insertion with precision. Replaceable skin and trachea components support repeated and hygienic training sessions while maintaining consistent performance.

The integrated lung balloon expands during correct tube placement, giving visual confirmation of airway patency. Its durability and structural accuracy make the model ideal for emergency medicine programs, simulation labs, and surgical training environments requiring reliable hands-on tracheotomy practice.`,
    features: [
      "Anatomically accurate neck structure replicating clinical conditions",
      "Replaceable skin pads and trachea parts for repeated practice",
      "Multi-layer silicone provides realistic resistance during incision",
      "Durable design supports stable and safe long-term training"
    ],
    image: "/images/H-TCT-01/H-TCT-01_Tracheotomy Trainer 1.jpg",
    gallery: [
      "/images/H-TCT-01/H-TCT-01_Tracheotomy Trainer 1.jpg",
      "/images/H-TCT-01/H-TCT-01_Tracheotomy Trainer 2.jpg",
      "/images/H-TCT-01/H-TCT-01_Tracheotomy Trainer 3.jpg",
      "/images/H-TCT-01/H-TCT-01_Tracheotomy Trainer 4.jpg",
      "/images/H-TCT-01/H-TCT-01_Tracheotomy Trainer 5.jpg",
      "/images/H-TCT-01/H-TCT-01_Tracheotomy Trainer 6.jpg",
      "/images/H-TCT-01/H-TCT-01_Tracheotomy Trainer 7.jpg",
      "/images/H-TCT-01/H-TCT-01_Tracheotomy Trainer Spec.jpg"
    ]
  },
  {
    id: "ss-57",
    slug: "ear-model",
    name: "Ear Model",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "True-size realistic silicone external ear model with accurate anatomical landmarks for hearing assessment training, impression-taking, fitting procedures, and anatomy education.",
    description: `This ear model replicates the anatomical form and proportions of the human external ear. The silicone surface is soft and elastic, allowing learners to experience tactile sensations similar to real skin during handling or examination. The model maintains its shape even after repeated use, making it suitable for continuous training.

Detailed anatomical landmarks such as the helix, antihelix, tragus, lobule, and the entrance of the external auditory canal are accurately represented. These details support both visual and tactile learning for anatomy education and clinical preparation. The model provides a realistic platform for practicing hearing assessments, fitting techniques, and impression-taking procedures.

Its durability and anatomical accuracy make the model ideal for medical programs, audiology training, hearing-aid fitting courses, and demonstration settings.`,
    features: [
      "Realistic reproduction of the human external ear in true size",
      "Soft, elastic silicone replicates the feel of real skin",
      "Suitable for incision practice, impression-taking, examination, and fitting",
      "Durable construction maintains shape during repeated training"
    ],
    image: "/images/H-EAR-01/_H-EAR-01_1.jpg",
    gallery: [
      "/images/H-EAR-01/_H-EAR-01_1.jpg",
      "/images/H-EAR-01/H-EAR-01_2.jpg"
    ]
  },
  {
    id: "ss-21",
    slug: "gastric-vascular-dissection-trainer",
    name: "Gastric Vascular Dissection Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Anatomically accurate upper abdominal trainer for gastric and vascular dissection, vessel ligation, and multi-layer tissue handling with replaceable modules.",
    description: `This trainer provides a realistic simulation environment for practicing upper abdominal vascular and gastric dissection. Its layered anatomical construction replicates the stomach, surrounding tissues, and arterial/venous pathways, enabling trainees to develop precise mobilization, vessel exposure, and suturing skills.

The integrated vascular network offers realistic tactile resistance and visual feedback, simulating real surgical challenges. Medical‑grade silicone ensures durability during repeated use, and replaceable modules support long‑term training needs in surgical residency programs, endoscopy workshops, and simulation centers.`,
    features: [
      "Anatomically accurate stomach and vascular structures",
      "3D vascular pathways minimizing unintended injury",
      "High-durability silicone for repeated dissection and suturing",
      "Multi-layer tissue planes simulating fascia, fat, and vessels",
      "Optimized for upper GI surgery, hepatobiliary procedures, and endoscopic training"
    ],
    image: "/images/A-GVD-01/A-GVD-01_1.jpg",
    gallery: [
      "/images/A-GVD-01/A-GVD-01_1.jpg",
      "/images/A-GVD-01/A-GVD-01_2.jpg",
      "/images/A-GVD-01/A-GVD-01_3.jpg",
      "/images/A-GVD-01/A-GVD-01_4.jpg",
      "/images/A-GVD-01/A-GVD-01_spec.jpeg"
    ]
  },
  {
    id: "ss-1",
    slug: "laparoscopic-skills-trainer",
    name: "Laparoscopic Skills Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Compact laparoscopic simulation box for developing minimally invasive surgical techniques and hand-eye coordination.",
    description: `The Laparoscopic Skills Trainer provides an effective and versatile platform for developing minimally invasive surgical skills. This compact simulation box features a built-in HD camera system and multiple interchangeable training modules, including silicone organs, suture pads, or real tissue specimens.

Learners can practice essential laparoscopic tasks such as:

• Peg transfer – moving small objects between locations to develop hand-eye coordination and instrument control
• Pattern cutting
• Intracorporeal and extracorporeal knot tying
• Suturing

The silicone skin layer enhances tactile realism, allowing students to palpate and manipulate instruments as they would in a clinical setting. Designed for both veterinary and medical training, the trainer helps students build the precision, dexterity, and confidence required for successful laparoscopic procedures. Its compact and portable design makes it ideal for classrooms, labs, or individual practice sessions.`,
    features: [
      "HD camera for real-time visualization",
      "Interchangeable training modules, including silicone organs",
      "Peg transfer, pattern cutting, and suturing exercises",
      "Silicone skin layer for realistic tactile feedback",
      "Compatible with suture pads or real tissue",
      "Ergonomic instrument ports for natural hand positioning"
    ],
    image: "/images/Laparoscopic  Skills Trainer/Gemini_Generated_Image_b6oojyb6oojyb6oo.jpg",
    gallery: [
      "/images/Laparoscopic  Skills Trainer/Gemini_Generated_Image_b6oojyb6oojyb6oo.jpg",
      "/images/Laparoscopic  Skills Trainer/Gemini_Generated_Image_drubxldrubxldrub.jpg",
      "/images/Laparoscopic  Skills Trainer/Gemini_Generated_Image_jie7xsjie7xsjie7.jpg"
    ]
  },
  {
    id: "ss-139",
    slug: "wound-treatment-training-kit",
    name: "Wound Treatment Training Kit",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Comprehensive wound simulator by SINI featuring eight realistic wound types including abrasion, laceration, burn, abscess, and puncture for progressive wound assessment, dressing, and treatment training.",
    description: `The Wound Treatment Training Kit is a comprehensive simulator designed to replicate eight clinically common wound types with high realism.

Each wound—such as abrasion, laceration, burn, abscess, infected ulcer, and puncture—features unique depth and tissue-damage characteristics, enabling progressive training in wound assessment and treatment.

Constructed from medical-grade silicone, the pads provide realistic tactile feedback suitable for cleansing, disinfection, dressing, ointment application, and basic suturing practice.

The modular design allows individual or combined use of the pads, supporting scenario-based training including emergency care, infection control, and patient-communication exercises.

This kit is widely used in medical schools, nursing programs, EMS education, and clinical training centers as a high-quality wound-management training solution.`,
    features: [
      "Eight realistic wound types including abrasion, incision, burn, infection, abscess, and puncture",
      "Varying depths and tissue-damage levels allow stepwise difficulty adjustment",
      "Medical-grade silicone provides lifelike skin texture and elasticity",
      "Supports cleansing, disinfection, dressing, ointment application, and suturing",
      "Modular wound pads allow flexible scenario building and easy storage",
    ],
    image: "/images/S-WTT-01/S-WTT-01_Wound Treatment Training Kit 1.jpg",
    gallery: [
      "/images/S-WTT-01/S-WTT-01_Wound Treatment Training Kit 1.jpg",
      "/images/S-WTT-01/S-WTT-01_Wound Treatment Training Kit (Abrasion).jpg",
      "/images/S-WTT-01/S-WTT-01_Wound Treatment Training Kit (Blister).jpg",
      "/images/S-WTT-01/S-WTT-01_Wound Treatment Training Kit (Burn).jpg",
      "/images/S-WTT-01/S-WTT-01_Wound Treatment Training Kit (Deep Open Wound with Bone Exposed).jpg",
      "/images/S-WTT-01/S-WTT-01_Wound Treatment Training Kit (Incision).jpg",
      "/images/S-WTT-01/S-WTT-01_Wound Treatment Training Kit (Inflammation In Wound).jpg",
      "/images/S-WTT-01/S-WTT-01_Wound Treatment Training Kit (Laceration).jpg",
      "/images/S-WTT-01/S-WTT-01_Wound Treatment Training Kit (Puncture Wound).jpg",
      "/images/S-WTT-01/S-WTT-01_Wound Treatment Training Kit spec.jpg",
    ],
  },
  {
    id: "ss-138",
    slug: "vascular-graft-trainer",
    name: "Vascular Graft Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "High-elasticity synthetic vessel trainer by SINI in Ø10mm and Ø18mm diameters for realistic vascular graft anastomosis, incision, suturing, and microsurgical reconstruction practice.",
    description: `The Vascular Graft Trainer is designed to support realistic practice in vascular anastomosis, grafting, incision, and suturing.

The synthetic vessel tubes closely mimic the elasticity, thickness, and tactile response of real human vessels, providing a highly accurate training experience.

With two diameter options—Ø10mm and Ø18mm—the trainer enables step-by-step skill development from basic to advanced levels.

Its durable structure allows repeated practice of microsurgical techniques, including graft connection, fine suturing, and anastomosis accuracy.

This model is widely used in medical schools, surgical training programs, and simulation centers.

Not intended for human implantation; practice use only.`,
    features: [
      "High-elasticity synthetic vessels that replicate real vascular feel and pressure response",
      "Two diameter options (Ø10mm and Ø18mm) for progressive training",
      "Wall thickness (approx. 0.3mm) ideal for precise suturing practice",
      "Highly durable structure designed for repeated anastomosis training",
      "Suitable for microsurgery training and vascular reconstruction scenarios",
    ],
    image: "/images/S-VGT-01/S-VGT-01_Vascular Graft Trainer 1.jpg",
    gallery: [
      "/images/S-VGT-01/S-VGT-01_Vascular Graft Trainer 1.jpg",
      "/images/S-VGT-01/S-VGT-01_Vascular Graft Trainer spec.jpg",
    ],
  },
  {
    id: "ss-137",
    slug: "vascular-anastomosis-trainer",
    name: "Vascular Anastomosis Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "High-precision vascular anastomosis trainer by SINI featuring synthetic vessels in diameters from Ø1mm to Ø15mm for stepwise practice in vascular suturing, grafting, and microsurgery techniques.",
    description: `The Vascular Anastomosis Trainer is engineered for high-precision practice in vascular suturing, anastomosis, grafting, and microsurgical techniques. The included synthetic vessels provide lifelike elasticity and allow realistic simulation of clinical vascular reconstruction scenarios.

Made from high-elasticity silicone, each vessel replicates the tactile feel of real vessel walls during incision, suturing, and manipulation. When paired with a surgical microscope or micro-instrument set, the model enables accurate and repeatable microsuture training.

Available in diameters ranging from Ø1mm to Ø15mm, the trainer supports progressive training from beginner to advanced levels, making it highly suitable for medical schools, microsurgery labs, and vascular-surgery workshops.`,
    features: [
      "Realistic synthetic vessels mimicking elasticity and tactile response of real vessels",
      "Various diameters (Ø1mm–Ø15mm) allow stepwise practice from basic to advanced levels",
      "Fully compatible with surgical microscopes and micro-instrumentation",
      "Cleanly cut vessel ends ideal for repeated suturing and anastomosis practice",
      "Anatomically accurate vessel wall thickness for lifelike simulation",
    ],
    image: "/images/S-VAT-01/S-VAT-01_Vascular Anastomosis Trainer 1.jpg",
    gallery: [
      "/images/S-VAT-01/S-VAT-01_Vascular Anastomosis Trainer 1.jpg",
      "/images/S-VAT-01/S-VAT-01_Vascular Anastomosis Trainer 2.jpg",
      "/images/S-VAT-01/S-VAT-01_Vascular Anastomosis Trainer Spec.jpg",
    ],
  },
  {
    id: "ss-136",
    slug: "tissue-stand",
    name: "Tissue Stand",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Versatile transparent acrylic tissue stand set by SINI including large and small stands for stably holding silicone pads, simulated vessels, and synthetic skin during suturing and incision training.",
    description: `The Tissue Stand is a versatile stand designed to stably hold various training materials—such as silicone pads, simulated vessels, and synthetic skin—for incision, suturing, and tissue-handling practice. Its transparent acrylic structure ensures clear visibility during training, making it ideal for both learners and instructors.

The kit includes both large and small stands to accommodate a wide range of training scenarios. Each stand provides firm fixation of tissues, allowing realistic tension control and minimizing unwanted movement during suturing.

With its lightweight and durable design, the Tissue Stand is suitable for medical schools, simulation centers, surgical workshops, and any setting requiring secure and adjustable positioning of training materials.`,
    features: [
      "Compatible with silicone pads, artificial vessels, synthetic skin, and various training models",
      "Transparent acrylic structure provides excellent visibility for observation and instruction",
      "Includes both large and small stands for flexible training setups",
      "Lightweight yet durable construction supports stable positioning",
      "Effectively controls tension and movement during suturing",
    ],
    image: "/images/S-TST-01/S-TIS-01_Tissue Stand Large 1.jpg",
    gallery: [
      "/images/S-TST-01/S-TIS-01_Tissue Stand Large 1.jpg",
      "/images/S-TST-01/S-TIS-01_Tissue Stand Large 2.jpg",
      "/images/S-TST-01/S-TIS-01_Tissue Stand Large 3 (Example of Use).jpg",
      "/images/S-TST-01/S-TIS-01_Tissue Stand Large Spec.jpg",
      "/images/S-TST-01/S-TIS-02_Tissue Stand Small 1.jpg",
      "/images/S-TST-01/S-TIS-02_Tissue Stand Small 2 (Example of Use).jpg",
      "/images/S-TST-01/S-TIS-02_Tissue Stand Small Spec.jpg",
    ],
  },
  {
    id: "ss-135",
    slug: "suture-knot-tying-practice-kit",
    name: "Suture & Knot Tying Practice Kit",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Comprehensive starter kit by SINI with a three-layer skin pad, essential instruments, and two-colored training string for fundamental and intermediate suturing, knot tying, and closure technique training.",
    description: `The Suture & Knot Tying Practice Kit is a comprehensive starter kit designed to support training in essential suturing and knot-tying techniques. Its three-layer anatomical skin pad provides realistic resistance and lifelike tactile feedback, enabling accurate practice of needle angle, tension control, and closure techniques.

The kit includes all essential instruments—scissors, needle holder, tissue forceps, sutures, and two-color training string—allowing learners to progress from basic suturing to more complex closure patterns.

Packaged in a convenient carry case, the kit is ideal for classrooms, workshops, simulation centers, and portable training environments.

S-SKP-01 is widely used across medical, nursing, and veterinary education as a reliable and realistic suturing practice solution.`,
    features: [
      "Three anatomical layers: epidermis, dermis, and subcutaneous fat",
      "Realistic tissue resistance during incision and suturing",
      "Multiple incision patterns and suture lines allow broad practical training",
      "Includes essential tools: scissors, needle holder, tissue forceps, sutures, and training string",
      "Highly durable silicone skin pad suitable for repeated use",
      "Portable carry case for workshops and on-the-go training",
    ],
    image: "/images/S-SKP-01/S-SKP-01_1.jpg",
    gallery: [
      "/images/S-SKP-01/S-SKP-01_1.jpg",
      "/images/S-SKP-01/S-SKP-01_2.jpg",
      "/images/S-SKP-01/S-SKP-01_3.jpg",
      "/images/S-SKP-01/S-SKP-01_4.jpg",
      "/images/S-SKP-01/S-SKP-01_5.jpg",
      "/images/S-SKP-01/S-SKP-01_6.jpg",
      "/images/S-SKP-01/S-SKP-01_Spec.png",
    ],
  },
  {
    id: "ss-134",
    slug: "needle-test-pad",
    name: "Needle Test Pad",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Compact high-elasticity silicone test pad by SINI for evaluating needle sharpness, penetration force, angle control, and tension stability across incision, puncture, and suturing tests.",
    description: `The Needle Test Pad (S-NDT-01) is designed for evaluating needle sharpness, penetration force, and handling characteristics. Its uniform structure allows accurate assessment of needle entry, angle control, and tension stability.

The compact pad supports multiple testing methods—including incision, puncture, and suturing—making it suitable for medical-device manufacturers, laboratories, and clinical training facilities. Durable silicone material ensures consistent performance through repeated testing.

This pad provides a practical and reliable solution for needle testing and equipment verification.`,
    features: [
      "High-elasticity silicone provides realistic resistance during needle insertion",
      "Uniform thickness optimized for comparing needle sharpness and penetration",
      "Compact, lightweight design ideal for testing, training, and equipment inspection",
      "Supports incision, puncture, and suturing tests",
      "Maintains shape and performance even after repeated use",
    ],
    image: "/images/S-NDT-01/S-NDT-01_1.jpg",
    gallery: [
      "/images/S-NDT-01/S-NDT-01_1.jpg",
      "/images/S-NDT-01/S-NDT-01_2.jpg",
      "/images/S-NDT-01/S-NDT-01_3.jpg",
      "/images/S-NDT-01/S-NDT-01_4.jpg",
      "/images/S-NDT-01/S-NDT-01_5.jpg",
      "/images/S-NDT-01/S-NDT-01_6.jpg",
      "/images/S-NDT-01/S-NDT-01_spec.jpg",
    ],
  },
  {
    id: "ss-133",
    slug: "magnets-board-skills-training-kit",
    name: "Magnets Board Skills Training Kit",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Versatile laparoscopic and MIS training platform by SINI with a strong magnetic baseboard for stable fixation of replaceable organ, vessel, and tissue modules for multi-scenario skill development.",
    description: `The Magnets Board Skills Training Kit is a versatile laparoscopic and minimally invasive surgery (MIS) training platform designed for multi-purpose skill development. The magnetic whiteboard structure enables quick fixation of organ, vessel, and soft-tissue modules, allowing instructors and trainees to configure custom training scenarios.

The magnetic system stabilizes precise laparoscopic movements, enabling repeated practice of suturing, clipping, dissection, knot-tying, and tissue manipulation.

The kit requires no complex setup and is ideal for workshops, medical schools, simulation centers, and hands-on MIS courses.

Replaceable organ and tissue modules support long-term use and cost-efficient operation, making this model an effective tool for comprehensive laparoscopic skill training.`,
    features: [
      "Strong magnetic baseboard allows stable fixation of multiple training modules",
      "Realistic handling that mimics laparoscopic instrument movement",
      "Compatible with replaceable organ, vessel, and tissue models",
      "Lightweight and easy to install for workshops and institutional training",
      "Provides a realistic environment for hands-on MIS skill development",
    ],
    image: "/images/S-MBK-01/S-MBK-01_Magnets Board Skills Training Kit 1.jpg",
    gallery: [
      "/images/S-MBK-01/S-MBK-01_Magnets Board Skills Training Kit 1.jpg",
      "/images/S-MBK-01/S-MBK-01_Magnets Board Skills Training Kit Spec.jpg",
    ],
  },
  {
    id: "ss-132",
    slug: "7layer-skin-pad",
    name: "7 Layer Skin Pad",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Seven-layer high-fidelity surgical simulator by Medtronic featuring epidermis, dermis, subcutaneous tissue, fascia, muscle, peritoneum, and myometrium for Caesarean section suturing and deep-layer closure training.",
    description: `The 7 Layer Skin Pad (S-LSV-01) is a high-fidelity surgical simulator designed for practising Caesarean section suturing and deep-layer tissue closure. Its seven-layer anatomical structure—including fascia, muscle, peritoneum, and myometrium—accurately replicates the resistance and tactile feedback encountered during real surgical procedures.

Each layer imitates lifelike incision feel and suturing tension, allowing learners to develop precise handling, layer identification, and secure closure skills. The durable medical-grade silicone construction supports repeated training without deformation, while the replaceable pad system ensures hygienic and cost-efficient long-term use.

This model is widely utilized in medical schools, nursing programs, simulation centers, and obstetrics-gynecology skill workshops for advanced suturing and layered closure training.`,
    features: [
      "Seven anatomical layers: epidermis, dermis, subcutaneous tissue, fascia, muscle, peritoneum, and myometrium",
      "Distinct tactile resistance for each layer during incision",
      "Realistic tension response during suturing",
      "Durable silicone structure suitable for repeated usage",
      "Replaceable skin pad for hygienic, long-term operation",
    ],
    image: "/images/S-LSV-01/S-LSV-01_1.jpg",
    gallery: [
      "/images/S-LSV-01/S-LSV-01_1.jpg",
      "/images/S-LSV-01/S-LSV-01_2.jpg",
      "/images/S-LSV-01/S-LSV-01_3.jpg",
      "/images/S-LSV-01/S-LSV-01_Spec.png",
    ],
  },
  {
    id: "ss-131",
    slug: "3layer-skin-pad-small",
    name: "3 Layer Skin Pad Small",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Compact three-layer suture pad by SINI with epidermis, dermis, and fat layers in a lightweight portable design for linear, ellipse, flap, and shaped suturing practice.",
    description: `The 3Layer Skin Pad Small (S-LSS-01) is a compact, highly portable suturing trainer designed for efficient practice in limited spaces. Its anatomical three-layer structure—epidermis, dermis, and fat—provides realistic resistance during incision and lifelike tissue feedback during suturing, allowing learners to practice various techniques such as linear, ellipse, flap, and shaped closures.

Constructed from durable medical-grade silicone, the pad maintains its shape even after repetitive use and provides a realistic texture comparable to real skin.

Its small footprint makes it ideal for individual study, small-group training, workshops, and institutional skill labs.

The replaceable pad design supports hygienic long-term use and allows educators to operate training programs efficiently. This model is a practical and versatile choice for suturing education across medical schools, nursing programs, and simulation centers.`,
    features: [
      "Three anatomical layers: epidermis, dermis, and fat",
      "Realistic tactile resistance during incision and suturing",
      "Compact and lightweight design for portable use",
      "Durable medical-grade silicone for repeated suturing practice",
      "Replaceable skin pad ensures hygienic long-term operation",
    ],
    image: "/images/S-LSS-01/S-LSS-01_3Layer Skin Pad Small 1.jpg",
    gallery: [
      "/images/S-LSS-01/S-LSS-01_3Layer Skin Pad Small 1.jpg",
      "/images/S-LSS-01/S-LSS-01_3Layer Skin Pad Small Spec.jpg",
    ],
  },
  {
    id: "ss-130",
    slug: "4layer-skin-pad",
    name: "4 Layer Skin Pad",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Four-layer anatomical suture pad by SINI with epidermis, dermis, fat, and fascia layers offering realistic cutting resistance and lifelike tension for basic and intermediate suturing practice.",
    description: `The 4Layer Skin Pad (S-LSR-01) is engineered for basic and intermediate suturing practice, featuring four realistic anatomical layers—epidermis, dermis, fat, and fascia.

Each layer provides distinct tactile resistance, enabling highly accurate simulation of incision, tissue handling, and suturing.

Constructed from durable medical-grade silicone, the trainer maintains its shape through repeated use and supports a wide range of surgical tools including sutures and staplers.

With its replaceable skin pad structure, the model ensures hygienic, long-term use in medical schools, nursing programs, and simulation centers.`,
    features: [
      "Four anatomical layers: epidermis, dermis, fat, and fascia",
      "Realistic cutting resistance and lifelike tension during suturing",
      "Standard size compatible with multiple jigs and stands",
      "Durable medical-grade silicone designed for repeated practice",
      "Replaceable skin pad for hygienic long-term training",
    ],
    image: "/images/S-LSR-01/S-LSR-01_1.jpg",
    gallery: [
      "/images/S-LSR-01/S-LSR-01_1.jpg",
      "/images/S-LSR-01/S-LSR-01_2.jpg",
      "/images/S-LSR-01/S-LSR-01_3.jpg",
      "/images/S-LSR-01/S-LSR-01_Spec.png",
    ],
  },
  {
    id: "ss-129",
    slug: "3layer-skin-pad-with-strap",
    name: "3 Layer Skin Pad with Strap",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Wearable three-layer suture trainer by SINI with epidermis, dermis, and fat layers and an adjustable strap for on-body practice on arm, hand, or leg, supporting both technical and patient-communication training.",
    description: `The 3 Layer Skin Pad with Strap (S-LSP-02) is a wearable suturing trainer designed for realistic practice on curved body surfaces. Its three-layer anatomical structure—epidermis, dermis, and fat—provides lifelike resistance and tactile feedback during incision and closure.

The included strap allows the pad to be worn on the arm, hand, or leg, enabling realistic body-positioning scenarios and patient-communication practice.

Made of durable medical-grade silicone, the trainer withstands repeated use without deforming, and the replaceable skin pad ensures hygienic, long-term operation.`,
    features: [
      "Three anatomical layers: epidermis, dermis, and fat",
      "Realistic resistance during incision and suturing",
      "Wearable strap allows on-body practice on arm, hand, or leg",
      "Highly durable silicone for repeated practice",
      "Replaceable skin pad for hygienic long-term use",
    ],
    image: "/images/S-LSP-02/S-LSP-02_3Layer Skin Pad 1.jpg",
    gallery: [
      "/images/S-LSP-02/S-LSP-02_3Layer Skin Pad 1.jpg",
      "/images/S-LSP-02/S-LSP-02_3Layer Skin Pad 2.jpg",
      "/images/S-LSP-02/S-LSP-02_3Layer Skin Pad spec.jpg",
    ],
  },
  {
    id: "ss-128",
    slug: "3layer-skin-pad-classic",
    name: "3 Layer Skin Pad (Classic)",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Classic three-layer suture trainer by SINI with epidermis, dermis, and fat layers on an ergonomic curved surface for realistic angle control, tension management, and various suturing techniques.",
    description: `The 3 Layer Skin Pad (S-LSP-01) is a standard suturing trainer designed with three realistic tissue layers—epidermis, dermis, and fat—to simulate true anatomical resistance during incision and closure.

Its contoured surface replicates natural body curvature, helping learners practice angle control, tension management, and various suturing techniques such as linear, ellipse, flap, and shaped closures.

Constructed from high-durability silicone, the pad withstands repeated suturing without significant deformation. Its compact size and easy setup make it suitable for personal study, workshops, and institutional training.

The replaceable pad system maintains hygiene and extends product lifespan, making it highly effective for long-term educational use.`,
    features: [
      "Three anatomical layers: epidermis, dermis, and fat",
      "Realistic tactile feedback with distinct layer resistance",
      "Ergonomic curved design supports natural suturing angles",
      "Durable silicone pad for repeated practice",
      "Replaceable skin pad ensures hygienic long-term use",
    ],
    image: "/images/S-LSP-01(discontinued)/S-LSP-01_3Layer Skin Pad 1.jpg",
    gallery: [
      "/images/S-LSP-01(discontinued)/S-LSP-01_3Layer Skin Pad 1.jpg",
      "/images/S-LSP-01(discontinued)/S-LSP-01_3Layer Skin Pad 2.jpg",
      "/images/S-LSP-01(discontinued)/S-LSP-01_3Layer Skin Pad 3.jpg",
      "/images/S-LSP-01(discontinued)/S-LSP-01_3Layer Skin Pad Spec.jpg",
    ],
  },
  {
    id: "ss-127",
    slug: "3layer-skin-pad",
    name: "3 Layer Skin Pad",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Standard three-layer suture trainer by SINI with epidermis, dermis, and fat layers on an ergonomic curved surface for realistic angle control, tension management, and various suturing techniques.",
    description: `The 3Layer Skin Pad is a standard suturing trainer designed with three realistic tissue layers—epidermis, dermis, and fat—to simulate true anatomical resistance during incision and closure.

Its contoured surface replicates natural body curvature, helping learners practice angle control, tension management, and various suturing techniques such as linear, ellipse, flap, and shaped closures.

Constructed from high-durability silicone, the pad withstands repeated suturing without significant deformation. Its compact size and easy setup make it suitable for personal study, workshops, and institutional training.

The replaceable pad system maintains hygiene and extends product lifespan, making it highly effective for long-term educational use.`,
    features: [
      "Three anatomical layers: epidermis, dermis, and fat",
      "Realistic tactile feedback with distinct layer resistance",
      "Ergonomic curved design supports natural suturing angles",
      "Durable silicone pad for repeated practice",
      "Replaceable skin pad ensures hygienic long-term use",
    ],
    image: "/images/S-LSP-01/S-LSP-01_1.jpeg",
    gallery: [
      "/images/S-LSP-01/S-LSP-01_1.jpeg",
      "/images/S-LSP-01/S-LSP-01_2.jpeg",
      "/images/S-LSP-01/S-LSP-01_3.jpeg",
      "/images/S-LSP-01/S-LSP-01_4.jpeg",
      "/images/S-LSP-01/S-LSP-01_Spec.jpeg",
    ],
  },
  {
    id: "ss-126",
    slug: "3layer-skin-pad-large",
    name: "3 Layer Skin Pad Large",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Large three-layer suture pad by SINI with epidermis, dermis, and subcutaneous fat layers offering realistic tactile feedback across a wide work surface, ideal for group training and multiple suture sites.",
    description: `The 3 Layer Skin Pad Large (S-LSL-01) is designed for realistic suturing practice in educational and group-training environments. The three-layer construction—epidermis, dermis, and fat—provides distinct tactile resistance, allowing trainees to understand tissue behavior during incision and suturing.

Its wide surface enables multiple suture sites to be practiced simultaneously, making it ideal for medical schools, nursing programs, surgical workshops, and simulation centers.

The durable silicone structure offers a lifelike suturing experience and maintains its form despite repeated practice.

The replaceable skin pad system ensures hygienic operation and long-term usability, supporting efficient training across a variety of clinical education settings.`,
    features: [
      "Three anatomical layers: epidermis, dermis, and subcutaneous fat",
      "Realistic tactile response that differs by layer",
      "Large working surface suitable for group training and multiple suture sites",
      "High-durability silicone withstands repeated suturing",
      "Replaceable skin pad supports hygienic long-term use",
    ],
    image: "/images/S-LSL-01/S-LSL-01 3Layer Skin Pad Large 1.jpg",
    gallery: [
      "/images/S-LSL-01/S-LSL-01 3Layer Skin Pad Large 1.jpg",
      "/images/S-LSL-01/S-LSL-01 3Layer Skin Pad Large Spec.jpg",
    ],
  },
  {
    id: "ss-125",
    slug: "5layer-skin-pad",
    name: "5 Layer Skin Pad",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Five-layer anatomical suture pad by SINI featuring epidermis, dermis, subcutaneous tissue, fascia, and a separated muscle module, with an adjustable tension jig for comprehensive suturing and tissue-management training.",
    description: `The 5 Layer Skin Pad is designed for realistic suturing practice, featuring a five-layer structure that mimics human skin anatomy: epidermis, dermis, subcutaneous tissue, fascia, and muscle.

Each layer provides different tactile resistance, enabling highly realistic training for various suturing techniques, including linear, ellipse, flap, and shaped closures.

The separated muscle layer enhances deep-layer suturing realism, and the jig with three individual tensioners allows adjustable tension control.

Made with high-durability silicone, the pad withstands repeated suturing and can be replaced as needed for long-term use.

This model is widely used in medical schools, nursing programs, surgical workshops, and simulation centers for comprehensive suture-skill training.`,
    features: [
      "Five anatomical layers: Epidermis, dermis, subcutaneous tissue, fascia, and muscle",
      "Muscle layer designed as a separated module for better simulation",
      "Jig with three tensioners allows adjustable tension settings",
      "Highly durable and suturable silicone for repeated practice",
      "Replaceable skin pad for hygienic long-term training",
    ],
    image: "/images/S-LSF-01/S-LSF-01_5Layer Skin Pad 1.jpg",
    gallery: [
      "/images/S-LSF-01/S-LSF-01_5Layer Skin Pad 1.jpg",
      "/images/S-LSF-01/S-LSF-01_5Layer Skin Pad 2.jpg",
      "/images/S-LSF-01/S-LSF-01_5Layer Skin Pad 3.jpg",
      "/images/S-LSF-01/S-LSF-01_5Layer Skin Pad 4.jpg",
      "/images/S-LSF-01/S-LSF-01_5Layer Skin Pad 5.jpg",
      "/images/S-LSF-01/S-LSF-01_5Layer Skin Pad 6.jpg",
      "/images/S-LSF-01/S-LSF-01_5Layer Skin Pad Spec.jpg",
    ],
  },
  {
    id: "ss-124",
    slug: "local-anesthesia-trainer",
    name: "Local Anesthesia Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Triple-layer local anesthesia trainer by SINI simulating epidermis, dermis, and subcutaneous fat with realistic tissue expansion, built-in drainage, and wearable strap for on-body role-playing practice.",
    description: `The Local Anesthesia Trainer features a realistic multi-layer structure that mimics the epidermis, dermis, and subcutaneous fat, allowing learners to practice accurate injection depth, angle control, and tissue feedback. During injection, the skin expands naturally and provides lifelike resistance, reflecting real clinical conditions.

A drainage hole at the bottom allows residual fluid to escape, enabling natural drying. Users may also apply gentle, even pressure with the palm to accelerate the drying process.

A built-in strap allows the model to be worn on the arm or leg, making it suitable for role-playing sessions that enhance both technical and communication skills.

The included storage case supports convenient transport and hygienic storage.

This trainer is widely used in clinical workshops, medical and dental training, veterinary programs, and simulation centers as an effective tool for local anesthesia training.`,
    features: [
      "Triple-layer structure simulating epidermis, dermis, and subcutaneous fat",
      "Realistic tissue expansion and resistance during injection",
      "Built-in drainage hole allows natural liquid outflow and self-drying",
      "Gentle palm pressure can accelerate drying after use",
      "Strap included for role-playing and on-body practice",
      "Washable, reusable, and highly durable",
    ],
    image: "/images/S-LOA-01/S-LOA-01_Local Anesthesia Trainer 1.jpg",
    gallery: [
      "/images/S-LOA-01/S-LOA-01_Local Anesthesia Trainer 1.jpg",
      "/images/S-LOA-01/S-LOA-01_Local Anesthesia Trainer 2.jpg",
      "/images/S-LOA-01/S-LOA-01_Local Anesthesia Trainer 3.jpg",
      "/images/S-LOA-01/S-LOA-01_Local Anesthesia Trainer spec.jpg",
    ],
  },
  {
    id: "ss-123",
    slug: "laparoscopic-training-kit-d",
    name: "Laparoscopic Training Kit D",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Three-dimensional multi-shaped silicone laparoscopic trainer by SINI for cutting, manipulation, and suturing with realistic tissue resistance, compatible with box trainers and magnetic boards.",
    description: `The Laparoscopic Training Kit D is designed to enhance fundamental laparoscopic skills such as cutting, manipulation, and suturing. Its three-dimensional multi-shaped silicone surface provides realistic tactile resistance, enabling trainees to practice angle control and two-hand coordination in a setting similar to clinical laparoscopic procedures.

The durable silicone construction maintains its form through repeated use, making the model suitable for medical schools, simulation centers, and hands-on laparoscopic workshops. When paired with a laparoscopic box trainer or magnetic skill board, it supports highly effective scenario-based training.`,
    features: [
      "Three-dimensional silicone structure simulating intra-abdominal surfaces",
      "Multiple grooves and shapes for diverse task simulation",
      "High-elasticity material replicates realistic tissue resistance",
      "Durable construction suitable for long-term repeated practice",
      "Compatible with laparoscopic box trainers and magnetic boards",
    ],
    image: "/images/S-LKD-01/S-LKD-01_Laparoscopic Training Kit D 1.jpg",
    gallery: [
      "/images/S-LKD-01/S-LKD-01_Laparoscopic Training Kit D 1.jpg",
      "/images/S-LKD-01/S-LKD-01_Laparoscopic Training Kit D 2.jpg",
      "/images/S-LKD-01/S-LKD-01_Laparoscopic Training Kit D spec.jpg",
    ],
  },
  {
    id: "ss-122",
    slug: "laparoscopic-training-kit-c",
    name: "Laparoscopic Training Kit C",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Intermediate laparoscopic trainer by SINI with multi-layer curved anatomical design and high-elasticity silicone for cutting, clipping, suturing, and coordinated instrument handling exercises.",
    description: `The Laparoscopic Training Kit C is engineered to enhance basic and intermediate laparoscopic skills, including cutting, clipping, suturing, and coordinated instrument handling. Its curved anatomical design closely simulates real intra-abdominal environments, enabling trainees to practice angle control, traction, and visual-motor coordination.

The high-elasticity silicone layers provide realistic tactile feedback and maintain durability through repeated sessions. When combined with a laparoscopic box trainer or magnetic board, the kit supports highly practical simulation workflows.

This model is widely used in medical schools, surgical training centers, and laparoscopic workshops as a versatile and effective training tool.`,
    features: [
      "Multi-layer curved design simulating intra-abdominal anatomy",
      "Realistic organ-shaped contours for depth and angle training",
      "Compatible with laparoscopic graspers, scissors, needle holders, and more",
      "High-elasticity silicone provides lifelike resistance during cutting and suturing",
      "Durable structure suitable for repeated practice",
      "Compatible with box trainers and magnetic training boards",
    ],
    image: "/images/S-LKC-01/S-LKC-01_Laparoscopic Training Kit C 1.jpg",
    gallery: [
      "/images/S-LKC-01/S-LKC-01_Laparoscopic Training Kit C 1.jpg",
      "/images/S-LKC-01/S-LKC-01_Laparoscopic Training Kit C 2.jpg",
      "/images/S-LKC-01/S-LKC-01_Laparoscopic Training Kit C spec.jpg",
    ],
  },
  {
    id: "ss-121",
    slug: "laparoscopic-training-kit-b",
    name: "Laparoscopic Training Kit B",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Foundational laparoscopic skill trainer by SINI featuring a high-elasticity silicone pad with rings, poles, and wires for grasping, transferring, and positioning exercises compatible with box and camera-based trainers.",
    description: `The Laparoscopic Training Kit B is designed for foundational laparoscopic skill development.

Using various objects—such as rings, poles, and wires—learners can practice grasping, transferring, and positioning tasks to improve precision, depth perception, and hand-eye coordination.

The high-elasticity silicone pad simulates realistic tissue resistance, providing a lifelike training experience. Compatible with SINI box trainers and multiple laparoscopic systems, this model is ideal for beginners seeking reliable and repeatable skill acquisition.`,
    features: [
      "High-elasticity silicone pad that mimics intra-abdominal tissue resistance",
      "Includes rings, poles, and wires for diverse manipulation tasks",
      "Fully compatible with laparoscopic graspers, forceps, and basic MIS instruments",
      "Works with box trainers, camera-based trainers, and magnetic boards",
      "Durable structure suitable for repeated practice",
    ],
    image: "/images/S-LKB-01/S-LKB-01_Laparoscopic Training Kit B 1.jpg",
    gallery: [
      "/images/S-LKB-01/S-LKB-01_Laparoscopic Training Kit B 1.jpg",
      "/images/S-LKB-01/S-LKB-01_Laparoscopic Training Kit B 2.jpg",
      "/images/S-LKB-01/S-LKB-01_Laparoscopic Training Kit B 3.jpg",
      "/images/S-LKB-01/S-LKB-01_Laparoscopic Training Kit B Spec.jpg",
    ],
  },
  {
    id: "ss-120",
    slug: "laparoscopic-training-kit-a",
    name: "Laparoscopic Training Kit A",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Entry-level laparoscopic practice board by SINI with multiple holes for bead-moving, ring-grasping, and pin-placement exercises, fully compatible with box trainers and camera-based laparoscopic trainers.",
    description: `The Laparoscopic Training Kit A (S-LKA-01) is an entry-level practice board designed to develop essential laparoscopic handling skills.

Its acrylic surface features multiple holes arranged to support exercises such as bead transfer, ring grasping, and pin placement. These movements help improve precision, instrument stability, and hand-eye coordination.

It can be combined with laparoscopic box trainers or camera-based trainers to simulate realistic surgical environments.

With strong durability and simple setup, the S-LKA-01 provides an effective platform for building foundational laparoscopic skills.`,
    features: [
      "Acrylic board with multiple holes for graded difficulty",
      "Supports bead-moving, ring-grasping, and pin-placement exercises",
      "Highly compatible with box trainers and camera-based laparoscopic trainers",
      "Durable acrylic board suitable for repeated practice",
      "Ideal for beginners to advanced learners",
    ],
    image: "/images/S-LKA-01/S-LKA-01_Laparoscopic Training Kit A 1.jpg",
    gallery: [
      "/images/S-LKA-01/S-LKA-01_Laparoscopic Training Kit A 1.jpg",
      "/images/S-LKA-01/S-LKA-01_Laparoscopic Training Kit A 2.jpg",
      "/images/S-LKA-01/S-LKA-01_Laparoscopic Training Kit A Spec.jpg",
    ],
  },
  {
    id: "ss-119",
    slug: "knot-tying-suture-trainer",
    name: "Knot Tying & Suture Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Comprehensive knot tying and suturing trainer by Johnson & Johnson featuring a three-layer silicone skin pad, transparent wire frame, and two-colored suture string for realistic surgical skill development.",
    description: `The Knot Tying & Suture Trainer is a comprehensive training system designed for practising essential suturing and knot-tying techniques.

Its multilayer skin pad—composed of skin, fat, and muscle—accurately mimics real-tissue resistance during incision and closure, providing realistic tactile feedback for training.

The transparent wire frame keeps the training components stable, enabling repeated suturing, knot-tying, and tissue-handling exercises. The included large skin pad, needle test pad, hook, transparent strings, and two-colored enlarged suture allow learners to practice a wide variety of clinical techniques.

The half-dyed two-colored string improves visibility of knot formation, crossing patterns, and tension control, making it especially effective for beginners.

With its portable carry case, the trainer is ideal for classrooms, workshops, simulation centers, and on-site skill-training environments.

S-KTS-01 is widely used as a practical and durable training solution for foundational suturing and knot-tying skills.`,
    features: [
      "Three-layer structure (skin, fat, muscle) provides lifelike tissue resistance",
      "Transparent wire frame stabilizes pads for repeated incision and suturing",
      "Includes a large skin pad, needle test pad, hook, transparent strings, and two-colored suture",
      "Half-dyed two-colored string enhances visualization of knot crossing and tension patterns",
      "Highly durable silicone ensures long-term, repeated practice",
      "Replaceable skin pad for hygienic and cost-efficient operation",
    ],
    image: "/images/S-KTS-01/S-KTS-01_Knot Tying _ Suture Trainer 1.jpg",
    gallery: [
      "/images/S-KTS-01/S-KTS-01_Knot Tying _ Suture Trainer 1.jpg",
      "/images/S-KTS-01/S-KTS-01_Knot Tying _ Suture Trainer 2.jpg",
      "/images/S-KTS-01/S-KTS-01_Knot Tying _ Suture Trainer 3.jpg",
      "/images/S-KTS-01/S-KTS-01_Knot Tying _ Suture Trainer 4.jpg",
      "/images/S-KTS-01/S-KTS-01_Knot Tying _ Suture Trainer spec.jpg",
    ],
  },
  {
    id: "ss-118",
    slug: "knot-tying-trainer-e",
    name: "Knot Tying Trainer E",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Simplest and most economical knot tying trainer by SINI, featuring transparent fixed strings and a two-colored enlarged suture string for beginners and basic surgical-skill training.",
    description: `The Knot Tying Trainer E is the simplest and economical model among SINI's knot-tying training tools, designed for beginners and basic surgical-skill training.

The trainer includes transparent strings that allow users to observe knot formation clearly, helping learners build confidence and accuracy.

A two-colored practice string is provided to simulate an enlarged suture for easier visualization, making it suitable for practicing both hand-tie and instrument-tie techniques.

Its compact, lightweight design makes it ideal for skills labs, workshops, and portable practice.

S-KTE-01 is widely used as an introductory and essential model for mastering fundamental knot-tying skills.`,
    features: [
      "Simple and cost-effective structure ideal for beginners",
      "Two-colored enlarged suture for clear visual confirmation of knot formation",
      "Transparent fixed strings on the board allow various knot-tying exercises",
      "Highly durable material suitable for repeated practice",
      "Lightweight design ideal for portable and classroom use",
    ],
    image: "/images/S-KTE-01/S-KTE-01_Knot Tying Trainer E 1.jpg",
    gallery: [
      "/images/S-KTE-01/S-KTE-01_Knot Tying Trainer E 1.jpg",
      "/images/S-KTE-01/S-KTE-01_Knot Tying Trainer E spec.jpg",
    ],
  },
  {
    id: "ss-117",
    slug: "knot-tying-trainer-d",
    name: "Knot Tying Trainer D",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Deep-hole knot tying trainer with two transparent acrylic cylinders of different sizes for progressive difficulty, non-slip base, and two-colored string for hand-tie and instrument-tie technique training.",
    description: `The Knot Tying Trainer D is designed for practising knot tying in deep and spatially restricted environments. Its transparent acrylic cylinder system allows learners to clearly observe knot formation, tension distribution, and crossing direction, which is crucial for mastering secure surgical knots.

With two sizes of cylinders, the trainer supports progressive difficulty levels and helps develop precise hand motions and spatial awareness. The included two-colored string enhances visual clarity during training and supports both hand-tie and instrument-tie techniques.

Lightweight yet sturdy, this model is widely used in medical schools, nursing programs, surgical skills courses, and simulation centers as an essential tool for improving knot security and fine motor control.`,
    features: [
      "Transparent acrylic cylinders allow clear visualization of knot formation",
      "Two different cylinder sizes enable adjustable training difficulty",
      "Non-slip base plate ensures stability during practice",
      "Two-colored string improves knot-pattern recognition",
      "Highly durable structure suitable for repeated practice",
      "Suitable for learners from beginner to advanced levels"
    ],
    image: "/images/S-KTD-01/S-KTD-01_Knot Tying Trainer D 1.jpg",
    gallery: [
      "/images/S-KTD-01/S-KTD-01_Knot Tying Trainer D 1.jpg",
      "/images/S-KTD-01/S-KTD-01_Knot Tying Trainer D spec.jpg"
    ]
  },
  {
    id: "ss-116",
    slug: "knot-tying-trainer-c",
    name: "Knot Tying Trainer C",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Compact knot tying trainer with two hooks, two transparent strings, and a deep-hole practice port — half-dyed enlarged suture string for clear visualization of knot formation.",
    description: `The Knot Tying Trainer C provides multiple knot-tying practice configurations on a single compact board. Two hooks and two transparent strings allow varied knot approaches, while the hole on the bent side of the board enables deep-hole knot tying practice.

The half-purple two-color string represents an enlarged suture for enhanced visibility, allowing learners to clearly observe suture crossing, tension, and knot structure during practice. Its durable and lightweight construction supports repeated use in classrooms, skills labs, and portable training environments.`,
    features: [
      "Two hooks and two transparent strings for varied knot practice configurations",
      "Deep-hole port on bent side for deep-space knot tying practice",
      "Half-dyed enlarged suture string for clear visualization of knot formation",
      "Lightweight and durable design for repeated use",
      "Compact form suitable for portable and classroom training"
    ],
    image: "/images/S-KTC-01(Discontinued)/S-KTC-01_Knot Tying Trainer C 1.jpg",
    gallery: [
      "/images/S-KTC-01(Discontinued)/S-KTC-01_Knot Tying Trainer C 1.jpg",
      "/images/S-KTC-01(Discontinued)/S-KTC-01_Knot Tying Trainer C spec.jpg"
    ]
  },
  {
    id: "ss-115",
    slug: "knot-tying-trainer-b",
    name: "Knot Tying Trainer B",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Lightweight knot tying trainer with three fixed hooks, semi-transparent wire, and transparent cylinder for deep-space knot visualization — compatible with laparoscopic box trainers and magnetic boards.",
    description: `The Knot Tying Trainer B is designed for essential knot-tying practice used in open, laparoscopic, and microsurgical procedures. The trainer features hooks and a transparent cylinder that provide realistic tension and allow users to visualize the interaction of the suture as knots are formed.

The semi-transparent wire system enhances visibility of suture crossing and tension control, making it highly effective for both beginners and advanced learners. Its lightweight and durable construction makes it suitable for use in skill labs, classrooms, and hands-on workshops. It can also be combined with laparoscopic box trainers or magnetic boards to expand practice scenarios.

S-KTB-01 is an essential model for medical training centers seeking accurate, repeatable knot-tying education.`,
    features: [
      "Three fixed hooks and semi-transparent wire for stable, repeatable knot practice",
      "Transparent cylinder enables deep-hole knot tying with clear visualization",
      "Lightweight design suitable for classrooms, workshops, and portable use",
      "Compatible with laparoscopic box trainers and magnetic practice boards"
    ],
    image: "/images/S-KTB-01/S-KTB-01_Knot Tying Trainer B 1.jpg",
    gallery: [
      "/images/S-KTB-01/S-KTB-01_Knot Tying Trainer B 1.jpg",
      "/images/S-KTB-01/S-KTB-01_Knot Tying Trainer B Spec.jpg"
    ]
  },
  {
    id: "ss-114",
    slug: "knot-tying-trainer-a",
    name: "Knot Tying Trainer A",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Versatile knot tying trainer with transparent cylinder for deep-space knot formation, silicone pad with tissue-like resistance, hooks for multi-angle practice, and two-color string for open and laparoscopic knot training.",
    description: `The Knot Tying Trainer A is designed for effective knot-tying practice in both open and laparoscopic surgical procedures. The silicone pad provides realistic resistance that mimics human tissue, helping trainees practice proper tension control, secure knot formation, and hand-eye coordination.

The trainer includes hooks for practicing various knot angles and a transparent cylinder that enables deep-space knot tying with direct visual feedback. The two-color string offers enhanced visibility, making it ideal for beginner instruction and detailed demonstration.

A replaceable skin pad is included for step-forward training with actual needles and sutures. This allows users to progress from basic knot techniques to realistic surgical knot practice.

This model is widely used in medical schools, nursing programs, laparoscopic workshops, and simulation centers as a versatile and essential knot-tying training tool.`,
    features: [
      "Transparent cylinder allows visual confirmation of deep-space knot formation",
      "Silicone pad provides realistic tension and tissue-like resistance",
      "Hooks and fixed wire support multiple knot angles and positions",
      "Replaceable skin pad for repeated, hygienic training",
      "Compatible with laparoscopic box trainers and magnetic boards"
    ],
    image: "/images/S-KTA-01/S-KTA-01_Knot Tying Trainer A 1.jpg",
    gallery: [
      "/images/S-KTA-01/S-KTA-01_Knot Tying Trainer A 1.jpg",
      "/images/S-KTA-01/S-KTA-01_Knot Tying Trainer A 2.jpg",
      "/images/S-KTA-01/S-KTA-01_Knot Tying Trainer A spec.jpg"
    ]
  },
  {
    id: "ss-113",
    slug: "intravenous-injection-trainer",
    name: "Intravenous Injection Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Realistic IV injection trainer with silicone skin pad, natural backflow response, transparent acrylic stand, simulated blood circulation, and replaceable vein pad for IV cannulation and blood sampling practice.",
    description: `The Intravenous Injection Trainer is designed to provide realistic training in IV injection, cannulation, and blood sampling. Vessels are embedded beneath the silicone skin, offering tactile resistance and natural vein entry feedback during needle insertion.

The transparent acrylic stand ensures stable positioning for accurate practice, while the simulated blood system allows learners to observe authentic backflow and flow-through responses.

The replaceable pad structure supports long-term, hygienic repeated training, making the model suitable for medical schools, nursing programs, and clinical simulation centers.`,
    features: [
      "Realistic silicone skin pad with lifelike vein behavior",
      "Natural backflow response for realistic injection practice",
      "Replaceable vein pad for hygienic repeated use",
      "Transparent acrylic stand provides stable positioning",
      "Supports simulated blood circulation"
    ],
    image: "/images/S-IVT-01/S-IVT-01_Intravenous Injection Trainer 1.jpg",
    gallery: [
      "/images/S-IVT-01/S-IVT-01_Intravenous Injection Trainer 1.jpg",
      "/images/S-IVT-01/S-IVT-01_Intravenous Injection Trainer 2.jpg",
      "/images/S-IVT-01/S-IVT-01_Intravenous Injection Trainer 3.jpg",
      "/images/S-IVT-01/S-IVT-01_Intravenous Injection Trainer spec.jpg"
    ]
  },
  {
    id: "ss-112",
    slug: "intravenous-injection-four-vein-trainer",
    name: "Intravenous Injection Four Vein Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Four-vein IV injection trainer with veins embedded at two different depths for multi-level venipuncture, blood collection, and vessel localization training with realistic tactile puncture feedback.",
    description: `The Intravenous Injection Four Vein Trainer is engineered for realistic venipuncture, IV insertion, and blood collection training. Four veins are embedded beneath the silicone skin at varied depths, enabling multiple difficulty levels and realistic vessel-finding practice.

The trainer provides lifelike tactile feedback when the needle enters the vessel, accurately replicating the sensation of venous access. Its durable construction supports long-term use across medical schools, nursing programs, simulation labs, and clinical skills workshops.`,
    features: [
      "Four embedded veins positioned at two different depths",
      "Realistic tactile resistance simulating vein puncture",
      "Durable silicone skin and tubing for repeated use",
      "Supports accurate needle angle, depth, and insertion technique"
    ],
    image: "/images/S-IVF-01/S-IVF-01_1.jpg",
    gallery: [
      "/images/S-IVF-01/S-IVF-01_1.jpg",
      "/images/S-IVF-01/S-IVF-01_2.jpg",
      "/images/S-IVF-01/S-IVF-01_3.jpg",
      "/images/S-IVF-01/S-IVF-01_Spec.png"
    ]
  },
  {
    id: "ss-111",
    slug: "intradermal-injection-trainer",
    name: "Intradermal Injection Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Multi-layer intradermal injection trainer with realistic blister formation upon correct injection, tactile depth and angle feedback, and fixing ring for arm-attachment role-playing practice.",
    description: `The Intradermal Injection Trainer is designed for high-accuracy training in intradermal injection techniques. The layered structure replicates the epidermis and dermis, providing realistic resistance as the needle enters the skin. A blister forms when the injection is performed correctly, offering clear visual feedback for skill assessment.

The soft silicone surface simulates human skin texture, enabling repeated practice without deforming the pad. With the included fixing ring, the trainer can be attached to the arm or other surfaces for role-playing sessions, enhancing both technical training and patient-communication skills.

This model is ideal for medical schools, nursing programs, workshops, and clinical simulation centers focusing on accurate intradermal injection techniques.`,
    features: [
      "Multi-layer construction mimicking epidermis and dermis thickness",
      "Realistic blister formation upon correct intradermal injection",
      "Provides tactile feedback for needle angle and depth",
      "Arm-shaped design suitable for realistic positioning",
      "Fixing rings allow secure attachment for role-playing practice"
    ],
    image: "/images/S-IDT-01/S-IDT-01_Intradermal injection Trainer 1.jpg",
    gallery: [
      "/images/S-IDT-01/S-IDT-01_Intradermal injection Trainer 1.jpg",
      "/images/S-IDT-01/S-IDT-01_Intradermal injection Trainer 2.jpg",
      "/images/S-IDT-01/S-IDT-01_Intradermal injection Trainer 3.jpg",
      "/images/S-IDT-01/S-IDT-01_Intradermal injection Trainer 4.jpg",
      "/images/S-IDT-01/S-IDT-01_Intradermal injection Trainer Spec.jpg"
    ]
  },
  {
    id: "ss-110",
    slug: "heart-fixation-box-trainer",
    name: "Heart Fixation Box Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Lightweight paper-based heart fixation box for stable multi-angle cardiac procedure training with wooden stabilizing sticks and disposal bag for hygienic wet-lab and surgical skill practice.",
    description: `The Heart Fixation Box Trainer is designed to provide stable fixation of heart models or animal organs during cardiac and vascular procedure training.

Made from a durable yet lightweight paper-based structure, the box can be assembled easily following the included manual.

The open-top configuration allows multi-directional access for incision, suturing, and simulated surgical workflows. Wooden stabilizing sticks support the heart in place, reducing unwanted movement and enabling accurate hands-on practice.

To maintain hygiene and simplify cleanup, a disposal plastic bag is included for handling used biological materials. This trainer is suitable for medical schools, surgical skill labs, wet-lab training, and team-based procedure simulations.`,
    features: [
      "Structural design optimized for holding the heart securely during procedures",
      "Lightweight paper-based material for easy assembly, transport, and storage",
      "Open structure enables multi-angle access for individual or team practice",
      "Includes disposal bag for hygienic cleanup after training",
      "Wooden sticks provide additional stabilization for various training setups"
    ],
    image: "/images/S-HFB-01/S-HFB-01_Heart Fixation Box Trainer 1.jpg",
    gallery: [
      "/images/S-HFB-01/S-HFB-01_Heart Fixation Box Trainer 1.jpg",
      "/images/S-HFB-01/S-HFB-01_Heart Fixation Box Trainer 2.jpg",
      "/images/S-HFB-01/S-HFB-01_Heart Fixation Box Trainer 3.jpg",
      "/images/S-HFB-01/S-HFB-01_Heart Fixation Box Trainer Spec.jpg"
    ]
  },
  {
    id: "ss-109",
    slug: "drainage-catheter-dressing-practice-pad",
    name: "Drainage Catheter Dressing Practice Pad",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Drainage catheter dressing practice pad compatible with 8–32Fr catheters, with 500cc internal water holder for simulated fluid drainage, secure dressing adhesion, and infection control training.",
    description: `The Drainage Catheter Dressing Practice Pad is designed to provide realistic training for dressing and stabilizing drainage catheters across multiple sizes (8–32Fr). The model enables repeated hands-on practice under conditions that closely simulate clinical environments.

The internal holder can contain up to 500cc of water, allowing trainees to simulate fluid drainage scenarios during dressing changes and catheter stabilization procedures. The unit does not include a separate outlet; fluid can be conveniently discarded by simply inverting the pad.

The skin surface is engineered to allow secure adhesion of adhesive tapes and dressing materials, enabling realistic practice of gauze application, catheter fixation, dressing replacement, and infection control procedures.

Constructed from durable medical-grade silicone, the pad maintains structural integrity through repeated attachment and removal of dressings. Washable and reusable, it is ideal for nursing schools, simulation centers, and clinical skills workshops requiring practical training in drainage catheter care and management.`,
    features: [
      "Compatible with drainage catheters sized 8, 12, 16, 20, 24, 28, and 32Fr",
      "Internal holder capable of containing up to 500cc of water for realistic simulation",
      "No separate outlet; fluid can be easily emptied by inverting the pad",
      "Skin surface designed for secure adhesion of tape and dressing materials",
      "Supports gauze application, catheter stabilization, and transparent film dressing practice",
      "High-durability medical-grade silicone suitable for repeated training use"
    ],
    image: "/images/S-DNC-01/S-DNC-01_1.jpeg",
    gallery: [
      "/images/S-DNC-01/S-DNC-01_1.jpeg",
      "/images/S-DNC-01/S-DNC-01_2.jpeg",
      "/images/S-DNC-01/S-DNC-01_3.jpeg",
      "/images/S-DNC-01/S-DNC-01_4.jpeg",
      "/images/S-DNC-01/S-DNC-01_5.jpeg",
      "/images/S-DNC-01/S-DNC-01_spec.jpeg"
    ]
  },
  {
    id: "ss-108",
    slug: "central-venous-catheter-dressing-practice-pad",
    name: "Central Venous Catheter Dressing Practice Pad",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Humanlike soft-tissue CVC dressing practice pad for realistic catheter site cleaning, disinfection, dressing application, and infection control training — compatible with gauze, tape, and film dressings.",
    description: `The Central Venous Catheter Dressing Practice Pad is designed for realistic training in the care and maintenance of central venous catheter insertion sites. Its lifelike skin texture allows learners to practice cleaning, disinfecting, dressing application, and dressing replacement following actual clinical workflows.

The surrounding tissue mimics natural skin elasticity and thickness, enabling effective training in infection detection, skin assessment, and catheter stabilization. The pad supports the use of various dressing materials including gauze, tape, and transparent film dressings.

Lightweight and washable, the model is suitable for repeated training sessions while maintaining hygiene. It is ideal for medical and nursing schools, simulation centers, and clinical skill workshops requiring hands-on practice in CVC dressing and maintenance procedures.`,
    features: [
      "Humanlike soft-tissue texture optimized for dressing and cleaning practice",
      "Realistic skin elasticity around the catheter insertion site",
      "Compatible with gauze, adhesive tape, film dressings, and various clinical materials",
      "Supports catheter stabilization and infection control training",
      "Durable medical-grade silicone maintained through repeated use"
    ],
    image: "/images/S-CVC-01/S-CVC-01_1.jpg",
    gallery: [
      "/images/S-CVC-01/S-CVC-01_1.jpg",
      "/images/S-CVC-01/S-CVC-01_2.jpg",
      "/images/S-CVC-01/S-CVC-01_3.jpg",
      "/images/S-CVC-01/S-CVC-01_spec.jpg"
    ]
  },
  {
    id: "ss-107",
    slug: "clear-suture-pad",
    name: "Clear Suture Pad",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Transparent silicone suture pad with tensioner and maintainer for real-time visualization of needle depth and suture path — fabric-reinforced for repeated use with replaceable skin pad.",
    description: `The Clear Suture Pad Set is designed to provide a realistic and visually guided suturing experience. The transparent silicone structure allows trainees to observe needle penetration depth, suture trajectory, and tissue response in real time, supporting precise and repeatable practice.

A reinforced internal fabric layer minimizes tearing during repeated suturing, while the tensioner allows the pad to be held at adjustable angles and tension levels. The maintainer stabilizes the entire setup, preventing movement during practice and ensuring consistent suturing conditions.

After completing the suturing, the incision can be opened to check knot tightness, suture spacing, and layer integrity, making the model ideal for skill assessment. The replaceable skin pad enables long-term hygienic use, and the design and branding can be customized upon request.

This model is widely used in medical schools, nursing programs, simulation centers, and suturing workshops as a high-quality hands-on training tool.`,
    features: [
      "Transparent silicone skin pad allows direct visualization of needle depth and suture path",
      "Internal fabric reinforcement prevents tearing and increases durability during repeated use",
      "Tensioner enables positioning at various angles and maintaining stable tension",
      "Maintainer provides firm stabilization during practice",
      "Replaceable skin pad ensures hygienic long-term operation"
    ],
    image: "/images/S-CSP-01/S-CSP-01_Clear Suture Pad 1.jpg",
    gallery: [
      "/images/S-CSP-01/S-CSP-01_Clear Suture Pad 1.jpg",
      "/images/S-CSP-01/S-CSP-01_Clear Suture Pad 2.jpg",
      "/images/S-CSP-01/S-CSP-01_Clear Suture Pad 3.jpg",
      "/images/S-CSP-01/S-CSP-01_Clear Suture Pad 4.jpg",
      "/images/S-CSP-01/S-CSP-01_Clear Suture Pad 5.jpg",
      "/images/S-CSP-01/S-CSP-01_Clear Suture Pad Spec.jpg"
    ]
  },
  {
    id: "ss-106",
    slug: "abscess-drainage-wearable-trainer",
    name: "Abscess Drainage Wearable Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Wearable arm-mounted abscess drainage trainer with fillable pus cavity and humanlike soft-tissue texture for full I&D workflow and communication skill training — 30 replaceable abscess pads included.",
    description: `The Abscess Drainage Wearable Trainer is designed to replicate the complete incision and drainage workflow in a realistic training environment.

The wearable jig fits securely on the arm, enabling instructors or standardized patients to participate in training sessions while learners practice both technical and communication skills.

The internal abscess cavity can be filled with simulated pus, providing natural pressure buildup and lifelike drainage when incised. This allows trainees to practice incision placement, pus evacuation, irrigation, and wound management in a controlled but realistic manner.

The replaceable abscess pads allow repeated high-fidelity practice, supporting large-group training sessions while maintaining hygiene. The model is suitable for medical and nursing schools, emergency medicine training, minor-procedure workshops, and simulation centers requiring realistic and wearable I&D training tools.`,
    features: [
      "Humanlike soft-tissue texture with realistic multilayer response",
      "Wearable design enabling instructor demonstration and learner interaction",
      "Simulated pus inside the abscess provides realistic drainage upon incision",
      "Supports full workflow: incision, drainage, irrigation, dressing, and drain insertion",
      "Replaceable abscess pads ensure hygienic, long-term repeated use"
    ],
    image: "/images/S-ABW-01/S-ABW-01_Abscess Drainage Wearable Trainer 1.jpg",
    gallery: [
      "/images/S-ABW-01/S-ABW-01_Abscess Drainage Wearable Trainer 1.jpg",
      "/images/S-ABW-01/S-ABW-01_Abscess Drainage Wearable Trainer 2.jpg",
      "/images/S-ABW-01/S-ABW-01_Abscess Drainage Wearable Trainer 3.jpg",
      "/images/S-ABW-01/S-ABW-01_Abscess Drainage Wearable Trainer 4.jpg"
    ]
  },
  {
    id: "ss-105",
    slug: "abscess-drainage-trainer",
    name: "Abscess Drainage Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Multi-layer soft-tissue abscess drainage trainer with fillable pus cavity pads for step-by-step incision, drainage, irrigation, and dressing practice — 30 replaceable abscess pads included.",
    description: `The Abscess Drainage Trainer is designed to provide a realistic platform for practicing incision and drainage procedures in a controlled training environment. The base unit and abscess pads are made from soft materials that closely mimic human soft-tissue response during palpation and incision.

Each abscess pad contains a simulated pus cavity that can be filled with fluid, enabling trainees to observe and manage realistic pus outflow when an incision is made. This allows learners to practice locating the optimal incision site, controlling incision depth, evacuating the abscess, and performing thorough irrigation.

Following drainage, users can continue training through wound cleansing, dressing application, and drain placement to simulate complete abscess management workflows. With 30 replaceable pads included, the trainer supports repeated sessions across multiple learners while maintaining hygiene and consistency.

Durable construction and humanlike tactile feedback make this model ideal for medical and nursing schools, emergency medicine and surgery skills workshops, and clinical simulation centers focusing on abscess management and minor procedure training.`,
    features: [
      "Multi-layer soft-tissue structure with humanlike skin texture",
      "Realistic abscess cavity that releases simulated pus upon incision",
      "Abscess pads can be filled with fluid for repeated drainage practice",
      "Includes replaceable abscess pads for hygienic and cost-effective training",
      "Enables step-by-step practice of incision, drainage, irrigation, and dressing"
    ],
    image: "/images/S-ABS-01/S-ABS-01_Abscess Drainage Trainer 1.jpg",
    gallery: [
      "/images/S-ABS-01/S-ABS-01_Abscess Drainage Trainer 1.jpg",
      "/images/S-ABS-01/S-ABS-01_Abscess Drainage Trainer 2.jpg",
      "/images/S-ABS-01/S-ABS-01_Abscess Drainage Trainer 3.jpg",
      "/images/S-ABS-01/S-ABS-01_Abscess Drainage Trainer 4.jpg",
      "/images/S-ABS-01/S-ABS-01_Abscess Drainage Trainer 5.jpg",
      "/images/S-ABS-01/S-ABS-01_Abscess Drainage Trainer spec.jpg"
    ]
  },
  {
    id: "ss-104",
    slug: "vaginitis-model",
    name: "Vaginitis Model",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Anatomically accurate vaginitis examination model with visual inflammation and discharge representation, compatible with speculums, swabs, and ultrasound probes for gynecological examination training.",
    description: `The Vaginitis Model is designed to provide a realistic environment for teaching and practicing the examination of vaginitis. The external genitalia, vaginal canal, and cervical structures are anatomically accurate, allowing learners to understand infection patterns, visual characteristics, and clinical symptoms.

The model replicates typical signs of vaginitis, including inflammation and discharge, enabling scenario-based training for clinical explanation and diagnostic practice. It supports the use of real instruments such as speculums, cotton swabs, and ultrasound probes, offering lifelike tactile feedback during insertion and exploration.

Constructed with high-durability medical-grade silicone, the model maintains its shape and texture through repeated training sessions. It is ideal for obstetrics and gynecology education, nursing programs, simulation centers, and patient-education environments requiring realistic visualization and hands-on examination practice.`,
    features: [
      "Realistic external and internal female genital anatomy",
      "Visual representation of inflammation, redness, and discharge",
      "Compatible with speculums, swabs, ultrasound probes, and other instruments",
      "Durable medical-grade silicone for repeated use",
      "Anatomically accurate internal structures for effective training"
    ],
    image: "/images/P-VAG-01/P-VAG-01_Vaginitis Model 1.jpg",
    gallery: [
      "/images/P-VAG-01/P-VAG-01_Vaginitis Model 1.jpg",
      "/images/P-VAG-01/P-VAG-01_Vaginitis Model 2.jpg",
      "/images/P-VAG-01/P-VAG-01_Vaginitis Model 3.jpg",
      "/images/P-VAG-01/P-VAG-01_Vaginitis Model 4.jpg",
      "/images/P-VAG-01/P-VAG-01_Vaginitis Model 5.jpg",
      "/images/P-VAG-01/P-VAG-01_Vaginitis Model spec.jpg"
    ]
  },
  {
    id: "ss-103",
    slug: "sacrocolpopexy-trainer",
    name: "Sacrocolpopexy Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Anatomically accurate sacrocolpopexy trainer by Intuitive Surgical with adjustable mesh fixation position and angle for pelvic-floor repair, mesh anchoring, and suturing practice in laparoscopic and robotic environments.",
    description: `The Sacrocolpopexy Trainer is an advanced simulation model engineered to replicate the full workflow of sacrocolpopexy with high anatomical accuracy.

It reproduces the sacrum, upper vaginal cavity, pelvic ligaments, and surrounding structures, allowing trainees to practice safe mesh anchoring, suturing, and tissue handling with realistic depth and angulation.

The internal vaginal structure provides natural resistance during exposure and dissection, helping learners develop precision in pelvic-floor repair techniques. Mesh fixation points can be adjusted to simulate various clinical scenarios, enhancing procedural adaptability.

Constructed from durable medical-grade silicone, the model maintains its form even after extensive use. Replaceable ligament and pelvic-pad modules support hygienic, long-term training. This trainer is ideal for OBGYN surgeons, robotic and laparoscopic training programs, and clinical simulation centers requiring high-fidelity sacrocolpopexy education.`,
    features: [
      "Anatomically accurate reproduction of sacral and pelvic-floor ligament structures",
      "Adjustable mesh fixation position, angle, and depth for scenario-based training",
      "Realistic pelvic access pathways simulating actual surgical environments",
      "Provides lifelike tactile resistance during dissection, suturing, and mesh anchoring",
      "High-durability medical-grade silicone suitable for repeated simulation"
    ],
    image: "/images/P-SCP-01/P-SCP-01_1.jpg",
    gallery: [
      "/images/P-SCP-01/P-SCP-01_1.jpg",
      "/images/P-SCP-01/P-SCP-01_2.jpg",
      "/images/P-SCP-01/P-SCP-01_3.jpg",
      "/images/P-SCP-01/P-SCP-01_4.jpg",
      "/images/P-SCP-01/P-SCP-01_5.jpg",
      "/images/P-SCP-01/P-SCP-01_6.jpg",
      "/images/P-SCP-01/P-SCP-01_7.jpg",
      "/images/P-SCP-01/P-SCP-01_spec.png"
    ]
  },
  {
    id: "ss-102",
    slug: "rectal-dissection-trainer",
    name: "Rectal Dissection Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Advanced pelvic rectal dissection trainer co-developed with LIVSMED for LAR and TME training with adjustable dissection depth, angle, and direction and replaceable rectum block.",
    description: `The Rectal Dissection Trainer is an advanced simulation model engineered for high-fidelity practice of rectal and distal colon dissection procedures such as low anterior resection (LAR) and total mesorectal excision (TME).

The model accurately reflects pelvic depth, tissue planes, and anatomical spatial relationships encountered in actual laparoscopic and robotic surgery.

Its realistic replication of the rectum, mesorectum, pelvic fat, and surrounding soft tissues allows trainees to identify correct dissection planes, maintain safe margins, and navigate narrow pelvic spaces. Adjustable dissection depth, angle, and direction enable scenario-based, repeated training under clinically relevant constraints.

With natural tactile resistance and visual cues, the trainer provides a lifelike environment for mastering deep pelvic dissection. The replaceable rectum block supports hygienic, long-term use, making the model ideal for medical schools, surgical residency programs, robotic surgery centers, and advanced colorectal training workshops.`,
    features: [
      "Anatomically accurate representation of the pelvic cavity and rectal positioning",
      "Allows free adjustment of dissection depth, angle, and direction for realistic simulation",
      "Optimized for laparoscopic and robotic rectal surgeries such as LAR and TME",
      "Represents pelvic organ relationships and surgical access pathways with high precision",
      "Provides lifelike tactile resistance and visual feedback during dissection",
      "Replaceable rectum block supports repeated and hygienic long-term training"
    ],
    image: "/images/P-RDT-01/P-RDT-01_1.jpg",
    gallery: [
      "/images/P-RDT-01/P-RDT-01_1.jpg",
      "/images/P-RDT-01/P-RDT-01_2.jpg",
      "/images/P-RDT-01/P-RDT-01_3.jpg",
      "/images/P-RDT-01/P-RDT-01_4.jpg",
      "/images/P-RDT-01/P-RDT-01_5.jpg",
      "/images/P-RDT-01/P-RDT-01_6.jpg",
      "/images/P-RDT-01/P-RDT-01_spec.jpg"
    ]
  },
  {
    id: "ss-101",
    slug: "prostatectomy-trainer",
    name: "Prostatectomy Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "High-fidelity prostatectomy trainer by Intuitive Surgical with anatomically detailed pelvic structures for open, laparoscopic, and robotic e-RARP prostatectomy training including dissection, resection, and multilayer closure.",
    description: `The Prostatectomy Trainer is a high-fidelity surgical simulator designed for mastering open, laparoscopic, and robotic prostatectomy techniques.

The model replicates essential pelvic anatomy—including the bladder, prostate gland, rectum, urethra, and adjacent soft tissues—allowing trainees to practice within realistic spatial and anatomical constraints.

Each tissue layer provides natural resistance and elasticity, enabling accurate practice in dissection, prostate mobilization, resection, and suturing. The structure supports varied incision angles and depths, mirroring real operative environments encountered in e-RARP and minimally invasive prostatectomy.

With durable silicone construction and replaceable components, the trainer maintains its performance even after extensive use. It is ideal for surgical residency programs, urology training centers, robotic-surgery workshops, and simulation labs seeking a realistic and reliable platform for prostatectomy education.`,
    features: [
      "Anatomically detailed reproduction of pelvic structures including the bladder, prostate, urethra, rectum, and surrounding tissues",
      "Supports full procedure: incision, dissection, prostate removal, and multilayer closure",
      "Optimized for laparoscopic techniques and Intuitive e-RARP robotic-surgery training",
      "Medical-grade high-elasticity silicone provides lifelike resistance during cutting and suturing",
      "Durable multi-layer construction suitable for repeated intensive practice"
    ],
    image: "/images/P-PRT-01/P-PRT-01_Prostatectomy Trainer 1.jpg",
    gallery: [
      "/images/P-PRT-01/P-PRT-01_Prostatectomy Trainer 1.jpg",
      "/images/P-PRT-01/P-PRT-01_Prostatectomy Trainer 2.jpg",
      "/images/P-PRT-01/P-PRT-01_Prostatectomy Trainer 3.jpg",
      "/images/P-PRT-01/P-PRT-01_Prostatectomy Trainer 4.jpg",
      "/images/P-PRT-01/P-PRT-01_Prostatectomy Trainer 5.jpg",
      "/images/P-PRT-01/P-PRT-01_Prostatectomy Trainer 6.jpg",
      "/images/P-PRT-01/P-PRT-01_Prostatectomy Trainer 7.jpg",
      "/images/P-PRT-01/P-PRT-01_Prostatectomy Trainer spec.jpg"
    ]
  },
  {
    id: "ss-100",
    slug: "pph-rectal-hemorrhoidectomy-trainer",
    name: "PPH Rectal Hemorrhoidectomy Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "High-fidelity PPH hemorrhoidopexy trainer with anatomically accurate anal canal, replaceable rectum module, and real stapler compatibility for mucosal resection, stapler positioning, and closure practice.",
    description: `The PPH Rectal Hemorrhoidectomy Trainer is a high-fidelity simulator designed to replicate the essential steps of circular stapled hemorrhoidopexy. Anatomically accurate layers of the anal canal and surrounding tissues allow trainees to practice mucosal resection, tissue handling, and stapler operation with realistic tactile feedback.

The replaceable rectum module provides appropriate resistance during cutting and stapling, supporting extensive repeated training without compromising structural integrity. Its design accommodates accurate instrument insertion and angulation, closely mirroring real procedural environments.

Constructed from durable medical-grade silicone, the trainer maintains its realistic texture and responsiveness over prolonged use. It is ideal for medical schools, surgical residency programs, colorectal surgery workshops, and minimally invasive surgery labs requiring advanced PPH procedure training.`,
    features: [
      "Replaceable rectum module enables repeated practice",
      "Anatomically accurate anal canal and perirectal soft-tissue structures",
      "Supports full PPH workflow including mucosal resection, stapler positioning, and closure",
      "Compatible with real surgical staplers for realistic hands-on simulation",
      "High-elasticity medical-grade silicone provides lifelike resistance during cutting, suturing, and stapling"
    ],
    image: "/images/P-PPH-01/P-PPH-01_PPH Rectal Hemorrhoidectomy Trainer 1.jpg",
    gallery: [
      "/images/P-PPH-01/P-PPH-01_PPH Rectal Hemorrhoidectomy Trainer 1.jpg",
      "/images/P-PPH-01/P-PPH-01_PPH Rectal Hemorrhoidectomy Trainer 2.jpg",
      "/images/P-PPH-01/P-PPH-01_PPH Rectal Hemorrhoidectomy Trainer 3.jpg",
      "/images/P-PPH-01/P-PPH-01_PPH Rectal Hemorrhoidectomy Trainer 4.jpg",
      "/images/P-PPH-01/P-PPH-01_PPH Rectal Hemorrhoidectomy Trainer 5.jpg",
      "/images/P-PPH-01/P-PPH-01_PPH Rectal Hemorrhoidectomy Trainer spec.jpg"
    ]
  },
  {
    id: "ss-98",
    slug: "ovarian-cystectomy-trainer",
    name: "Ovarian Cystectomy Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Anatomical ovarian cystectomy trainer co-developed with Intuitive Surgical featuring realistic cyst texture and membrane resistance for full cystectomy workflow training in laparoscopic and robotic environments.",
    description: `The Ovarian Cystectomy Trainer provides a realistic platform for practicing the full workflow of ovarian cyst removal, from initial incision and cyst exposure to dissection, excision, and ovarian repair. The model replicates key pelvic anatomy—including the uterus, ovary, and surrounding tissues—allowing trainees to work within clinically relevant spatial constraints.

The embedded ovarian cyst mimics the natural texture, membrane tension, and dissection resistance of real cystic tissue, helping learners refine delicate instrument handling and tissue-preserving techniques. During closure, the trainer offers lifelike feedback for suturing the ovarian surface while maintaining structural integrity.

Constructed from high-elasticity medical-grade silicone, the trainer withstands extensive use without significant deformation, making it suitable for intensive training programs. Replaceable cyst modules support hygienic, long-term use and repeated high-fidelity practice.

This model is ideal for medical schools, OBGYN residency programs, laparoscopic and robotic surgery centers, and simulation labs seeking a comprehensive tool for teaching ovarian cystectomy with high anatomical and tactile realism.`,
    features: [
      "Anatomically accurate reproduction of the uterus, ovary, and surrounding pelvic structures",
      "Realistic cyst size, texture, membrane separation resistance, and tissue behavior",
      "Supports step-by-step training from incision and cyst exposure to excision and repair",
      "High-durability medical-grade silicone designed for repeated incision, dissection, and suturing",
      "Suitable for both laparoscopic and robotic surgery training environments"
    ],
    image: "/images/P-OVC-01/P-OVC-01_1.jpg",
    gallery: [
      "/images/P-OVC-01/P-OVC-01_1.jpg",
      "/images/P-OVC-01/P-OVC-01_2.jpg",
      "/images/P-OVC-01/P-OVC-01_3.jpg",
      "/images/P-OVC-01/P-OVC-01_4.jpg",
      "/images/P-OVC-01/P-OVC-01_5.jpg",
      "/images/P-OVC-01/P-OVC-01_6.jpg",
      "/images/P-OVC-01/P-OVS-01_spec.png"
    ]
  },
  {
    id: "ss-97",
    slug: "uterine-myomectomy-trainer-v2",
    name: "Uterine Myomectomy Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Compact myomectomy trainer co-developed with Intuitive Surgical featuring embedded fibroids of varied sizes and depths for incision, dissection, enucleation, and multilayer suturing practice.",
    description: `The Uterine Myomectomy Trainer provides a realistic, hands-on environment for practicing key steps of myomectomy, including incision planning, fibroid dissection, removal, and multilayer uterine wall repair. Its high-quality silicone construction replicates the elasticity and resistance of real uterine tissue, delivering authentic haptic feedback throughout the procedure.

Embedded fibroids of varying sizes and depths simulate diverse clinical scenarios, helping trainees refine surgical judgment and technical precision. The compact form factor allows easy setup in small classrooms and skill-training labs, while the replaceable uterus module supports hygienic and cost-efficient long-term training.

This trainer is ideal for OBGYN residents, medical and nursing students, and surgical simulation programs seeking a reliable and realistic platform for foundational myomectomy practice.`,
    features: [
      "Simulates the full myomectomy workflow from incision to multilayer closure",
      "Realistic silicone provides lifelike elasticity and cutting resistance",
      "Embedded fibroids of various sizes and depths allow graded training",
      "Compact design fits well in classrooms, workshops, and limited training spaces",
      "Replaceable uterus module ensures hygienic, long-term use"
    ],
    image: "/images/P-MYT-02/_P-MYT-02_1.jpg",
    gallery: [
      "/images/P-MYT-02/_P-MYT-02_1.jpg",
      "/images/P-MYT-02/_P-MYT-02_2.jpg",
      "/images/P-MYT-02/_P-MYT-02_3.jpg",
      "/images/P-MYT-02/_P-MYT-02_4.jpg",
      "/images/P-MYT-02/_P-MYT-02_5.jpg",
      "/images/P-MYT-02/_P-MYT-02_6.jpg",
      "/images/P-MYT-02/_P-MYT-02_Spec.png"
    ]
  },
  {
    id: "ss-96",
    slug: "uterine-myomectomy-trainer",
    name: "Uterine Myomectomy Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Intuitive Surgical-exclusive myomectomy trainer with adjustable wire-and-suction-cup holder, embedded myomas, and multi-layer uterine construction for myomectomy, tissue management, and suturing practice.",
    description: `The Uterine Myomectomy Trainer is designed for myomectomy skills learning. It consists of a holder with suction cups and wire, and a uterus with myomas buried inside. The holder and wire system allows positioning the uterus at any angle or degree required, accurately simulating real surgical conditions.

Different uterine layers are simulated with differentiated materials to deliver realistic tissue response during incision and suturing. The materials are both suturable and highly durable, enabling multiple uses without loss of fidelity.

The replaceable uterus module allows trainees to purchase extra uteruses and continue reusing the holder, making it a cost-effective long-term training solution. This trainer is most commonly used alongside laparoscopic training systems for integrated minimally invasive surgical education.

Suitable for medical schools, OBGYN residency programs, laparoscopic surgery workshops, and simulation centers focused on myomectomy and tissue management skills.`,
    features: [
      "Adjustable holder with suction cups and wire for flexible uterus positioning",
      "Uterus with buried myomas for realistic myomectomy workflow",
      "Multi-layer uterine construction with differentiated materials for realistic tissue response",
      "Suturable and high-durability silicone for repeated use",
      "Replaceable uterus module for long-term, cost-effective training",
      "Anatomically accurate structure compatible with laparoscopic trainer setups"
    ],
    image: "/images/P-MYT-01(Discontinue)/P-MYT-01_Uterine Myomectomy Trainer 1.jpg",
    gallery: [
      "/images/P-MYT-01(Discontinue)/P-MYT-01_Uterine Myomectomy Trainer 1.jpg",
      "/images/P-MYT-01(Discontinue)/P-MYT-01_Uterine Myomectomy Trainer 2.jpg",
      "/images/P-MYT-01(Discontinue)/P-MYT-01_Uterine Myomectomy Trainer 3.jpg",
      "/images/P-MYT-01(Discontinue)/P-MYT-01_Uterine Myomectomy Trainer spec.jpg"
    ]
  },
  {
    id: "ss-95",
    slug: "uterine-myomectomy-suture-trainer",
    name: "Uterine Myomectomy Suture Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "TMC-exclusive myomectomy suture trainer with adjustable wire-and-suction-cup holder featuring a half-cut post-myomectomy uterus and multi-layer silicone construction for realistic suturing practice.",
    description: `The Uterine Myomectomy Suture Trainer is designed for myomectomy skills learning. It consists of a holder with suction cups and wire, and a uterus that is cut in half after myomectomy. The holder and wire system allows positioning the uterus at any angle or degree required, simulating real surgical conditions accurately.

Different uterine layers are simulated with differentiated materials to deliver realistic tissue response during suturing. The materials are both suturable and highly durable, enabling multiple uses without loss of fidelity.

The replaceable uterus module allows trainees to purchase extra uteruses and continue reusing the holder, making it a cost-effective long-term training solution. This trainer is most commonly used alongside laparoscopic training systems for integrated minimally invasive surgical education.

Suitable for medical schools, OBGYN residency programs, laparoscopic surgery workshops, and simulation centers focused on myomectomy and tissue management skills.`,
    features: [
      "Adjustable holder with suction cups and wire for flexible uterus positioning",
      "Half-cut post-myomectomy uterus for focused suturing practice",
      "Multi-layer uterine construction with differentiated materials for realistic tissue response",
      "Suturable and high-durability silicone for repeated use",
      "Replaceable uterus module for long-term, cost-effective training",
      "Anatomically accurate structure compatible with laparoscopic trainer setups"
    ],
    image: "/images/P-MYS-01/P-MYS-01_Uterine Myomectomy Suture Trainer 1.jpg",
    gallery: [
      "/images/P-MYS-01/P-MYS-01_Uterine Myomectomy Suture Trainer 1.jpg",
      "/images/P-MYS-01/P-MYS-01_Uterine Myomectomy Suture Trainer 2.jpg",
      "/images/P-MYS-01/P-MYS-01_Uterine Myomectomy Suture Trainer 3.jpg",
      "/images/P-MYS-01/P-MYS-01_Uterine Myomectomy Suture Trainer 4.jpg",
      "/images/P-MYS-01/P-MYS-01_Uterine Myomectomy Suture Trainer spec.jpg"
    ]
  },
  {
    id: "ss-94",
    slug: "uterine-myomectomy-pad-type-trainer",
    name: "Uterine Myomectomy Pad Type Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Lightweight pad-type myomectomy trainer with multiple fibroid sizes and depths for incision planning, dissection, removal, and basic uterine wall suturing practice.",
    description: `The Uterine Myomectomy Pad Type Trainer is an efficient hands-on tool designed to teach essential myomectomy skills.

It supports practical training in incision planning, fibroid dissection, removal, and basic uterine wall suturing.

The pad contains myomas of various depths and sizes, enabling trainees to simulate real-world scenarios such as adjusting incision angle, dissection depth, and surgical approach depending on the fibroid type. The high-elasticity silicone material replicates the tactile response of real uterine tissue, offering lifelike resistance during cutting and suturing.

Its compact and lightweight construction makes it easy to transport, store, and clean—ideal for classrooms, workshops, and clinical skills labs. The replaceable myoma pad extends long-term usability, allowing continuous and hygienic repeated training.

This trainer is suitable for medical schools, nursing programs, OBGYN residency training, and simulation centers focusing on foundational myomectomy education.`,
    features: [
      "Allows practice with multiple fibroid sizes and depths",
      "Realistic elasticity and cutting resistance similar to uterine tissue",
      "Enables trainees to adjust incision direction according to fibroid position",
      "Durable medical-grade silicone suitable for repeated use",
      "Lightweight and simple structure ideal for transport and classroom environments"
    ],
    image: "/images/P-MYP-01/P-MYP-01_1.jpg",
    gallery: [
      "/images/P-MYP-01/P-MYP-01_1.jpg",
      "/images/P-MYP-01/P-MYP-01_2.jpg",
      "/images/P-MYP-01/P-MYP-01_3.jpg",
      "/images/P-MYP-01/P-MYP-01_4.jpg"
    ]
  },
  {
    id: "ss-93",
    slug: "uterine-myomectomy-trainer-pelvic-plate",
    name: "Uterine Myomectomy Trainer – Pelvic Plate",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Full pelvic anatomy myomectomy trainer co-developed with Intuitive Surgical, featuring uterus, bladder, rectum, ureter, vessels, and nerves for open and laparoscopic myomectomy workflow training.",
    description: `The Uterine Myomectomy Trainer – Pelvic Plate is a high-fidelity surgical simulator designed for comprehensive myomectomy training. The model incorporates major pelvic organs—including the uterus, bladder, rectum, ureter, and surrounding vascular and neural structures—providing realistic anatomical orientation and access during surgical procedures.

The embedded myoma allows repeated practice in incision, dissection, enucleation, and multilayer suturing. Each anatomical layer is crafted from specialized medical-grade silicone, offering realistic tactile feedback and natural tissue resistance during surgical manipulation.

The pelvic-plate configuration enhances visibility and supports training for both open and laparoscopic approaches, accommodating a wide range of clinical workflows. The replaceable uterus module ensures hygienic operation and long-term use, even through repeated high-intensity training sessions.

This advanced simulator is ideal for OBGYN specialists, residents, medical students, nursing learners, and surgical simulation centers seeking a durable, realistic platform for learning myomectomy techniques and pelvic anatomy.`,
    features: [
      "Includes uterus, bladder, rectum, ureter, major vessels, and neural pathways for realistic pelvic anatomy",
      "Supports complete myomectomy workflow: incision, myoma removal, and multilayer uterine repair",
      "Provides lifelike tactile resistance across tissue layers for realistic surgical feedback",
      "Suitable for both open and laparoscopic surgical approaches",
      "Replaceable uterus module enables hygienic, long-term, cost-effective training",
      "Constructed from high-durability medical-grade silicone for repeated use"
    ],
    image: "/images/P-MYN-01/P-MYN-01_Uterine Myomectomy Trainer-Pelvic Plate(Intuitive GYN) 1.jpg",
    gallery: [
      "/images/P-MYN-01/P-MYN-01_Uterine Myomectomy Trainer-Pelvic Plate(Intuitive GYN) 1.jpg",
      "/images/P-MYN-01/P-MYN-01_Uterine Myomectomy Trainer-Pelvic Plate(Intuitive GYN) 2.jpg",
      "/images/P-MYN-01/P-MYN-01_Uterine Myomectomy Trainer-Pelvic Plate(Intuitive GYN) 3.jpg",
      "/images/P-MYN-01/P-MYN-01_Uterine Myomectomy Trainer-Pelvic Plate(Intuitive GYN) 4.jpg"
    ]
  },
  {
    id: "ss-92",
    slug: "uterine-myomectomy-half-trainer-large",
    name: "Uterine Myomectomy Half Trainer – Large",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Large half-cut uterine myomectomy trainer by Ethicon with multiple myoma sizes and depths for comprehensive incision, dissection, myoma removal, and multilayer suturing practice.",
    description: `The Uterine Myomectomy Half Trainer – Large is a high-fidelity simulation model designed for in-depth training in fibroid removal and uterine wall repair. The model replicates the elasticity, layered density, and anatomical proportions of real uterine tissue, allowing realistic practice of incision, myoma extraction, and multilayer suturing.

The half-cut configuration enhances visualization of internal structures and enables learners to follow each surgical step with precision. Multiple embedded myomas of different sizes and depths help trainees practice varied levels of surgical difficulty and refine their dissection techniques.

Medical-grade silicone materials reproduce authentic tactile transitions during cutting and suturing, supporting robust hands-on training. The replaceable uterus module ensures hygienic, long-term use across repeated sessions.

This trainer is widely utilized in medical schools, OBGYN residency programs, laparoscopic and open-surgery workshops, and simulation centers requiring accurate and reliable myomectomy training tools.`,
    features: [
      "Large half-cut uterine model replicating realistic anatomy and myoma placement",
      "Multiple myoma sizes and depths provide varied surgical difficulty",
      "High-durability silicone offers lifelike resistance during incision and suturing",
      "Half-cut design improves visibility and supports efficient repetitive practice",
      "Replaceable uterus module enables hygienic, long-term, cost-effective training",
      "External appearance and tactile sensation closely mimic real uterine tissue"
    ],
    image: "/images/P-MYL-01/P-MYL-01_1.jpg",
    gallery: [
      "/images/P-MYL-01/P-MYL-01_1.jpg",
      "/images/P-MYL-01/P-MYL-01_2.jpg",
      "/images/P-MYL-01/P-MYL-01_3.jpg"
    ]
  },
  {
    id: "ss-91",
    slug: "uterine-myomectomy-half-trainer",
    name: "Uterine Myomectomy Half Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Half-cut uterine myomectomy trainer with multi-layer silicone construction for incision, myoma excision, and uterine wall suturing practice with replaceable uterus module.",
    description: `The Uterine Myomectomy Half Trainer provides a realistic and accessible platform for hands-on practice in uterine fibroid removal and suturing. The half-cut structure enhances visibility and allows learners to follow the complete workflow—from incision and myoma excision to uterine wall closure—under anatomically accurate conditions.

Specialized silicone materials simulate the elasticity, density, and layered resistance of real uterine tissue. Trainees can experience authentic tactile transitions as the incision deepens through the uterine wall and into the myoma. The suturable surface retains durability even after repeated incision and repair, supporting extended practice sessions.

Its compact size and replaceable modules make it ideal for educational environments requiring efficient setup and hygienic maintenance. The trainer is widely used in medical schools, nursing programs, OBGYN residency training, and simulation centers for mastering myomectomy skills and understanding uterine tissue behavior.`,
    features: [
      "Half-cut uterine model replicating realistic anatomy and myoma placement",
      "Provides lifelike tactile resistance during incision, dissection, and suturing",
      "Multi-layer structure including uterine wall, myoma, and supporting tissues",
      "Durable, suturable medical-grade silicone designed for repeated use",
      "Compact and portable for small classrooms, workshops, and simulation labs",
      "Replaceable uterus module enables hygienic and cost-effective long-term training"
    ],
    image: "/images/P-MYH-01/P-MYH-01_Uterine Myomectomy Half Trainer 1.jpg",
    gallery: [
      "/images/P-MYH-01/P-MYH-01_Uterine Myomectomy Half Trainer 1.jpg",
      "/images/P-MYH-01/P-MYH-01_Uterine Myomectomy Half Trainer 2.jpg",
      "/images/P-MYH-01/P-MYH-01_Uterine Myomectomy Half Trainer 3.jpg",
      "/images/P-MYH-01/P-MYH-01_Uterine Myomectomy Half Trainer Spec.jpg"
    ]
  },
  {
    id: "ss-90",
    slug: "male-pelvic-simulator",
    name: "Male Pelvic Simulator",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Full anatomical male pelvic simulator by Boston Scientific featuring multi-layer skin, bone, muscle, ligaments, fascia, vessels, and organs for comprehensive pelvic surgery and suturing training.",
    description: `The Male Pelvic Simulator is a full anatomical reproduction of the male pelvis, incorporating skin, bone, muscle, related ligaments and cartilage, fascia, vessels, and organs. The three-layer skin construction—epidermis, dermis, and subcutaneous fat—uses differentiated materials to deliver realistic tissue response at every layer during incision and suturing.

The entire anatomical structure of the male pelvis has been accurately simulated, making the model compatible with a wide range of related surgical procedures. The materials are both suturable and highly durable, enabling extensive repeated practice without loss of fidelity.

The skin pad and internal components are fully replaceable, allowing training centers to purchase extra replacement parts and continue reusing the trainer stand. Developed in collaboration with Boston Scientific, this simulator is ideal for medical schools, urology and general-surgery residency programs, and clinical skills laboratories requiring high-realism pelvic surgical training.`,
    features: [
      "Complete male pelvic anatomy: skin, bone, muscle, ligaments, cartilage, fascia, vessels, and organs",
      "Three-layer skin construction (epidermis, dermis, subcutaneous fat) with differentiated material response",
      "Realistic tissue response during incision, dissection, and suturing",
      "Anatomically accurate structure co-developed with Boston Scientific",
      "Suturable and highly durable materials for multiple uses",
      "Replaceable skin pad and internal components for long-term use"
    ],
    image: "/images/P-MPS-01/P-MPS-01_Male Pelvic Simulator 1.jpg",
    gallery: [
      "/images/P-MPS-01/P-MPS-01_Male Pelvic Simulator 1.jpg",
      "/images/P-MPS-01/P-MPS-01_Male Pelvic Simulator 2.jpg",
      "/images/P-MPS-01/P-MPS-01_Male Pelvic Simulator 3.jpg",
      "/images/P-MPS-01/P-MPS-01_Male Pelvic Simulator 4.jpg",
      "/images/P-MPS-01/P-MPS-01_Male Pelvic Simulator 5.jpg",
      "/images/P-MPS-01/P-MPS-01_Male Pelvic Simulator 6.jpg",
      "/images/P-MPS-01/P-MPS-01_Male Pelvic Simulator Spec.jpg"
    ]
  },
  {
    id: "ss-87",
    slug: "hip-joint-surgery-trainer",
    name: "Hip Joint Surgery Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Anatomically accurate multi-layer hip joint trainer for THA, partial resection, and anterior/posterior/lateral approach practice with replaceable joint capsule module.",
    description: `The Hip Joint Surgery Trainer is engineered to provide a realistic and comprehensive platform for mastering hip-surgery procedures. The multi-layer anatomical structure—including skin, subcutaneous fat, muscle, ligaments, joint capsule, cartilage, and the femoral head—accurately reproduces the tactile feedback encountered during incision, dissection, and resection.

Its high-fidelity construction supports procedural training for total hip arthroplasty (THA), partial resection, and multiple surgical approaches such as anterior, lateral, and posterior access. Reinforced polymers combined with medical-grade silicone ensure long-term shape retention and lifelike mechanical response, even under repetitive use.

With a replaceable joint capsule and modular design, the trainer is ideal for medical schools, orthopedic residency programs, arthroplasty training centers, and robotic-surgery simulation environments that require reliable, high-performance hip surgery training tools.`,
    features: [
      "Anatomically accurate multi-layer reconstruction of the hip joint",
      "Realistic textures of muscle, ligament, joint capsule, cartilage, and femoral head",
      "Supports anterior, posterior, and lateral surgical approaches",
      "High-elasticity medical-grade silicone ensures durability for repeated procedures",
      "Suitable for practicing THA workflows and partial resection techniques",
      "Replaceable joint capsule module for long-term and cost-efficient use"
    ],
    image: "/images/P-HJS-01/P-HJS-01_Hip Joint Surgery Trainer 1.jpg",
    gallery: [
      "/images/P-HJS-01/P-HJS-01_Hip Joint Surgery Trainer 1.jpg",
      "/images/P-HJS-01/P-HJS-01_Hip Joint Surgery Trainer 2.jpg",
      "/images/P-HJS-01/P-HJS-01_Hip Joint Surgery Trainer 3.jpg",
      "/images/P-HJS-01/P-HJS-01_Hip Joint Surgery Trainer 4.jpg",
      "/images/P-HJS-01/P-HJS-01_Hip Joint Surgery Trainer 5.jpg"
    ]
  },
  {
    id: "ss-86",
    slug: "hip-surgery-trainer",
    name: "Hip Surgery Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Multi-layer hip anatomy trainer with femoral insertion system for anterior, lateral, and posterior approach practice including hemiarthroplasty, total hip arthroplasty, incision, and suturing.",
    description: `The Hip Surgery Trainer is engineered to simulate realistic hip anatomy and surgical workflows, enabling step-by-step practice of incision, dissection, resection, and suturing. Each anatomical layer—including skin, subcutaneous fat, muscle, joint capsule, cartilage, and bone—is crafted to replicate the tactile feedback encountered during real hip surgery.

Its integrated femoral insertion system supports comprehensive training for hemiarthroplasty, total hip arthroplasty, and other resection or replacement procedures. Multiple access approaches—anterior, lateral, and posterior—can be practiced, providing trainees with realistic operative scenarios for orthopedic and joint-replacement training.

Constructed from high-durability medical silicone, the trainer withstands repeated incision and suturing without deformation. Replaceable components such as fascia, skin pads, ligaments, and muscle modules extend its usability, making it ideal for orthopedic residency programs, surgical simulation centers, robotic-surgery labs, and joint-replacement education facilities.`,
    features: [
      "Realistic soft-tissue texture and anatomical response closely mimicking human tissue",
      "Multi-layer design including skin, fat, muscle, joint capsule, cartilage, and bone",
      "Supports anterior, lateral, and posterior hip surgical approaches",
      "Highly elastic medical-grade silicone maintains shape with repeated use",
      "Internal femoral insertion system suitable for hemiarthroplasty and total hip arthroplasty training",
      "Replaceable modules (skin, fascia, muscle, ligaments) ensure cost-efficient long-term training"
    ],
    image: "/images/P-HIP-01/P-HIP-01_Hip Surgery Trainer-LEFT 1.jpg",
    gallery: [
      "/images/P-HIP-01/P-HIP-01_Hip Surgery Trainer-LEFT 1.jpg",
      "/images/P-HIP-01/P-HIP-01_Hip Surgery Trainer-LEFT 2.jpg",
      "/images/P-HIP-01/P-HIP-01_Hip Surgery Trainer-LEFT 3.jpg",
      "/images/P-HIP-01/P-HIP-01_Hip Surgery Trainer-LEFT 4.jpg",
      "/images/P-HIP-01/P-HIP-01_Hip Surgery Trainer-LEFT 5.jpg",
      "/images/P-HIP-01/P-HIP-01_Hip Surgery Trainer-LEFT 6.jpg",
      "/images/P-HIP-01/P-HIP-01_Hip Surgery Trainer-LEFT 7.jpg"
    ]
  },
  {
    id: "ss-85",
    slug: "episiotomy-trainer",
    name: "Episiotomy Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Anatomically accurate multi-layer perineal trainer with cuttable skin pad for episiotomy incision angle, depth control, suturing, and perineal repair technique practice.",
    description: `The Episiotomy Trainer provides a realistic environment for learning and practicing both episiotomy incision and suturing techniques. Its anatomically accurate multi-layer design replicates the human perineum, enabling trainees to practice precise incision control, tissue handling, and perineal repair with lifelike tactile feedback.

The cuttable and suturable skin pad mimics real tissue behavior, allowing users to experience authentic resistance during cutting and natural tension during suturing. Made from high-durability medical-grade silicone, the trainer maintains structural integrity even after extensive practice sessions.

The replaceable episiotomy pad improves hygiene and cost-effectiveness, supporting long-term educational use. Ideal for medical schools, nursing programs, obstetric training centers, and childbirth education facilities, this trainer offers comprehensive hands-on practice in episiotomy procedures and perineal repair techniques.`,
    features: [
      "Anatomically accurate multi-layer perineal structure",
      "Cuttable artificial skin pad simulating real tissue response",
      "Layered design including skin, subcutaneous tissue, and muscle",
      "Compatible with real medical sutures and needles",
      "High-elasticity medical-grade silicone withstands repeated cutting and suturing",
      "Replaceable episiotomy pad for hygienic, long-term use"
    ],
    image: "/images/P-EPI-02/P-EPI-02_1.jpg",
    gallery: [
      "/images/P-EPI-02/P-EPI-02_1.jpg",
      "/images/P-EPI-02/P-EPI-02_2.jpg",
      "/images/P-EPI-02/P-EPI-02_3.jpg",
      "/images/P-EPI-02/P-EPI-02_4.jpg",
      "/images/P-EPI-02/P-EPI-02_5.jpg",
      "/images/P-EPI-02/P-EPI-02_6.jpg",
      "/images/P-EPI-02/P-EPI-02_7.jpg",
      "/images/P-EPI-02/P-EPI-02_8.jpg",
      "/images/P-EPI-02/P-EPI-02_Spec.jpg"
    ]
  },
  {
    id: "ss-84",
    slug: "decubitus-ulcer-trainer",
    name: "Decubitus Ulcer Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Multi-layer silicone model with Stage I–IV pressure ulcers for realistic wound assessment, cleansing, debridement, dressing, and wound-care procedure training.",
    description: `The Decubitus Ulcer Trainer provides a realistic, structured platform for training in pressure-ulcer assessment and treatment. Each ulcer stage—from Stage I through Stage IV—accurately reflects variations in depth, coloration, tissue damage, inflammation, and exudate, enabling realistic visual and tactile evaluation.

Its multi-layer composition delivers lifelike resistance during cleansing, debridement, and dressing applications. Detachable ulcer stages allow repeated practice and scenario-based simulations tailored to different clinical presentations.

Constructed from durable medical-grade silicone, the model supports consistent performance under repeated use. It is widely utilized in nursing programs, hospital education centers, and clinical simulation environments for comprehensive wound-care training.`,
    features: [
      "Realistic silicone design replicating Stage I–IV pressure ulcers",
      "Multi-layer structure including skin, subcutaneous tissue, and muscle",
      "Detachable and replaceable ulcer stages for repeated practice",
      "Supports cleansing, debridement, dressing, and disinfection procedures",
      "Optimized for nursing and wound-care education",
      "High-durability medical-grade silicone for long-term training use"
    ],
    image: "/images/P-DEU-01/P-DEU-01_Decubitus ulcer Trainer 1.jpg",
    gallery: [
      "/images/P-DEU-01/P-DEU-01_Decubitus ulcer Trainer 1.jpg",
      "/images/P-DEU-01/P-DEU-01_Decubitus ulcer Trainer 2.jpg",
      "/images/P-DEU-01/P-DEU-01_Decubitus ulcer Trainer 3.jpg",
      "/images/P-DEU-01/P-DEU-01_Decubitus ulcer Trainer 4.jpg",
      "/images/P-DEU-01/P-DEU-01_Decubitus ulcer Trainer spec.jpg"
    ]
  },
  {
    id: "ss-83",
    slug: "cystoscopy-ureteroscopy-trainer",
    name: "Cystoscopy & Ureteroscopy Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Anatomically accurate pelvic trainer with interchangeable male/female modules for cystoscope insertion, ureteroscope navigation, stone removal, and endoscopic visualization training.",
    description: `The Cystoscopy & Ureteroscopy Trainer provides a fully realistic training platform for urological endoscopy. Built on anatomically accurate pelvic structures, the simulator replicates natural insertion paths, angulation, and entry points for performing diagnostic cystoscopy and ureteroscopy.

Lifelike insertion resistance and directional feedback help trainees develop precision in scope advancement, visualization, and comprehensive instrument handling. Interchangeable male and female modules support anatomical variation training across a range of clinical scenarios.

The trainer is compatible with real medical cystoscopes and ureteroscopes, enabling practice of stone retrieval, tissue manipulation, irrigation, and complete procedural workflows. Its high-durability silicone structure maintains shape even with extensive use, making it ideal for urology residency programs, surgical training centers, medical schools, and hands-on device demonstration environments.`,
    features: [
      "Designed using accurate human pelvic anatomy for realistic endoscopic practice",
      "Interchangeable male and female genital modules for comprehensive procedural training",
      "Provides lifelike resistance and directional feedback during scope insertion",
      "Anatomically accurate bladder and ureter pathways enable realistic navigation",
      "Supports stone extraction, tissue manipulation, irrigation, and multi-step procedural simulation",
      "Constructed from durable medical-grade silicone to withstand repeated endoscopic use"
    ],
    image: "/images/P-CYS-01/P-CYS-01_1.jpg",
    gallery: [
      "/images/P-CYS-01/P-CYS-01_1.jpg",
      "/images/P-CYS-01/P-CYS-01_2.jpg",
      "/images/P-CYS-01/P-CYS-01_3.jpg",
      "/images/P-CYS-01/P-CYS-01_4.jpg",
      "/images/P-CYS-01/P-CYS-01_5.jpg"
    ]
  },
  {
    id: "ss-82",
    slug: "catheterization-trainer-b",
    name: "Catheterization Trainer B",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Male and female anatomical catheterization trainers with fillable bladder, multi-layer pelvic structure, and lifelike urethral resistance for sterile catheter insertion and drainage training.",
    description: `The Catheterization Trainer B provides a realistic and reliable platform for practicing urethral catheter insertion and bladder drainage procedures. Both male and female versions replicate accurate anatomical pathways, delivering lifelike resistance, curvature, and insertion depth similar to actual clinical catheterization.

The layered structure—composed of skin, fat, urethra, and bladder—offers natural tactile feedback during needle and catheter advancement. The bladder reservoir allows trainees to practice confirming proper placement through urine-flow simulation and complete the full procedural workflow, from insertion to fixation and removal.

Constructed from high-durability medical-grade silicone, the trainer withstands frequent use without deformation. Full compatibility with real medical catheters and sterile supplies enhances clinical realism, making it ideal for nursing schools, medical programs, clinical-skills laboratories, and hospital staff training environments.`,
    features: [
      "Anatomically accurate male and female pelvic structures",
      "Realistic resistance and directional changes during catheter insertion",
      "Bladder can be filled and drained for practical urine-flow simulation",
      "Multi-layer design including skin, subcutaneous tissue, urethra, and bladder",
      "Durable silicone construction maintains shape with repeated use",
      "Fully compatible with clinical catheters and sterile consumables"
    ],
    image: "/images/P-CTB-01/P-CTB-01_Catheterization Trainer B-Male 1.jpg",
    gallery: [
      "/images/P-CTB-01/P-CTB-01_Catheterization Trainer B-Male 1.jpg",
      "/images/P-CTB-01/P-CTB-01_Catheterization Trainer B-Male 2.jpg",
      "/images/P-CTB-01/P-CTB-01_Catheterization Trainer B-Male 3.jpg",
      "/images/P-CTB-01/P-CTB-01_Catheterization Trainer B-Male Spec.jpg",
      "/images/P-CTB-01/P-CTB-02_Catheterization Trainer B-Female 1.jpg",
      "/images/P-CTB-01/P-CTB-02_Catheterization Trainer B-Female 2.jpg",
      "/images/P-CTB-01/P-CTB-02_Catheterization Trainer B-Female 3.jpg",
      "/images/P-CTB-01/P-CTB-02_Catheterization Trainer B-Female Spec.jpg"
    ]
  },
  {
    id: "ss-81",
    slug: "catheterization-trainer-a",
    name: "Catheterization Trainer A",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Male and female anatomical catheterization trainers with fillable bladder, lifelike urethral resistance, and multi-layer structure for sterile urethral catheter insertion practice.",
    description: `The Catheterization Trainer A provides a realistic and comprehensive platform for practicing urethral catheterization and bladder drainage procedures. Both male and female units replicate true anatomical structures, including natural curvature, insertion depth, and tactile resistance experienced during actual catheterization.

Its layered composition—skin, muscle, urethra, and bladder—offers accurate tissue feedback, helping learners understand insertion angles, resistance points, and catheter advancement. The bladder can be filled with liquid, enabling visual confirmation of urine flow and supporting full procedural practice from insertion to fixation and removal.

Constructed from high-durability medical-grade silicone, the trainer withstands repeated use without deformation. The system is fully compatible with real clinical catheters and sterile supplies, making it ideal for nursing schools, medical universities, clinical skills labs, and patient-education simulations.`,
    features: [
      "Realistic anatomical design based on male and female genital structures",
      "Provides lifelike resistance and curvature during catheter insertion",
      "Bladder can be filled with liquid for urine-drainage simulation",
      "Multi-layer structure including skin, muscle, urethra, and bladder",
      "Durable medical-grade silicone suitable for repeated training",
      "Fully compatible with real clinical catheters and sterile consumables"
    ],
    image: "/images/P-CTA-01/P-CTA-01_Catheterization Trainer A-Male 1.jpg",
    gallery: [
      "/images/P-CTA-01/P-CTA-01_Catheterization Trainer A-Male 1.jpg",
      "/images/P-CTA-01/P-CTA-01_Catheterization Trainer A-Male 2.jpg",
      "/images/P-CTA-01/P-CTA-01_Catheterization Trainer A-Male 3.jpg",
      "/images/P-CTA-01/P-CTA-01_Catheterization Trainer A-Male 4.jpg",
      "/images/P-CTA-01/P-CTA-01_Catheterization Trainer A-Male 5.jpg",
      "/images/P-CTA-01/P-CTA-01_Catheterization Trainer A-Male spec.jpg",
      "/images/P-CTA-01/P-CTA-02_Catheterization Trainer A-Female 1.jpg",
      "/images/P-CTA-01/P-CTA-02_Catheterization Trainer A-Female 2.jpg",
      "/images/P-CTA-01/P-CTA-02_Catheterization Trainer A-Female 3.jpg",
      "/images/P-CTA-01/P-CTA-02_Catheterization Trainer A-Female 4.jpg",
      "/images/P-CTA-01/P-CTA-02_Catheterization Trainer A-Female spec.jpg"
    ]
  },
  {
    id: "ss-80",
    slug: "c-section-trainer",
    name: "C-Section Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "High-fidelity multi-layer abdominal simulator with skin, fascia, muscle, peritoneum, and uterus for realistic C-section incision, dissection, delivery, and suturing training.",
    description: `The C-Section Trainer is a high-fidelity simulator designed for comprehensive, step-by-step training in Cesarean section procedures. The model accurately reproduces all major abdominal wall layers—including the skin, subcutaneous tissue, fascia, muscle, peritoneum—and features a full uterus beneath the abdominal pad.

Each layer is engineered using specialized silicone to mimic the tactile response of real tissue during incision, dissection, and suturing. The abdominal pad is suturable and maintains durability even after extensive practice. Replaceable abdominal and uterine modules allow cost-effective long-term use.

This trainer supports the entire workflow of a Cesarean section, including incision, delivery simulation, uterine closure, and post-operative suturing. It is widely used in medical schools, nursing programs, operating-room training courses, and obstetric simulation workshops for mastering safe and accurate C-section techniques.`,
    features: [
      "Realistic multi-layer abdominal structure: skin, fat, fascia, muscle, peritoneum, and uterus",
      "Provides lifelike resistance during incision, dissection, and suturing",
      "High-durability medical-grade silicone maintains shape with repeated use",
      "Supports training with variable incision depths and positions",
      "Optimized for obstetrics, nursing, and operating-room skill education"
    ],
    image: "/images/P-CSE-01/P-CSE-01_C-Section Trainer 1.jpg",
    gallery: [
      "/images/P-CSE-01/P-CSE-01_C-Section Trainer 1.jpg",
      "/images/P-CSE-01/P-CSE-01_C-Section Trainer 2.jpg",
      "/images/P-CSE-01/P-CSE-01_C-Section Trainer 3.jpg",
      "/images/P-CSE-01/P-CSE-01_C-Section Trainer 4.jpg",
      "/images/P-CSE-01/P-CSE-01_C-Section Trainer 5.jpg",
      "/images/P-CSE-01/P-CSE-01_C-Section Trainer 6.jpg",
      "/images/P-CSE-01/P-CSE-01_C-Section Trainer Spec.jpg"
    ]
  },
  {
    id: "ss-78",
    slug: "shoulder-stand-model-a",
    name: "Shoulder Stand Model A",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "High-precision shoulder stand model with accurate muscle, ligament, and joint reproduction for anatomical education, surgical positioning, incision, and suturing practice.",
    description: `The Shoulder Stand Model A is a high-precision anatomical simulator designed to enhance understanding of the shoulder joint and support realistic surgical positioning and suturing practice. It accurately reproduces the relative positions and interactions of muscles, ligaments, and joint components, allowing trainees to study clinically relevant anatomy with clarity.

Mounted on a reinforced stand, the model provides stable support for multi-angle observation and hands-on procedures. Premium silicone and composite materials offer lifelike tactile quality and preserve structural integrity even after extensive use.

This model is widely used in orthopedic education, surgical skills training, anatomy courses, and rehabilitation research. It serves as an essential tool for practicing shoulder incisions, suturing methods, and comprehensive anatomical study of the shoulder joint.`,
    features: [
      "Fixed stand model that accurately replicates the anatomical structure of the human shoulder",
      "Clear representation of muscle, ligament, and joint interactions for realistic visualization",
      "Designed for both anatomical education and surgical skills development",
      "Constructed with durable silicone and composite materials for long-term use",
      "Stable stand design enables multi-angle observation and hands-on training"
    ],
    image: "/images/L-SHM-01/L-SHM-01_Shoulder Stand Model A 1.jpg",
    gallery: [
      "/images/L-SHM-01/L-SHM-01_Shoulder Stand Model A 1.jpg",
      "/images/L-SHM-01/L-SHM-01_Shoulder Stand Model A 2.jpg",
      "/images/L-SHM-01/L-SHM-01_Shoulder Stand Model A 3.jpg",
      "/images/L-SHM-01/L-SHM-01_Shoulder Stand Model A 4.jpg"
    ]
  },
  {
    id: "ss-77",
    slug: "shoulder-stand-model-b",
    name: "Shoulder Stand Model B",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Anatomically accurate shoulder stand model replicating humerus, scapula, and surrounding muscles with differentiated materials for realistic tissue response during demonstration and training.",
    description: `This model is designed for the demonstration and explanation of the shoulder joint. The humerus, scapula, and surrounding musculature are accurately simulated at the shoulder joint. Different materials are used for the distinct anatomical parts, enabling users to feel realistic tissue responses during examination and training.

The model supports a clear understanding of shoulder anatomy including joint structure, muscle arrangement, and soft tissue relationships. Its lifelike tactile characteristics make it suitable for anatomical education, clinical demonstrations, and procedural orientation in shoulder-related training programs.

High durability and accurate anatomical construction ensure reliable long-term use in medical schools, orthopedic programs, and simulation centers requiring a dependable shoulder anatomy reference model.`,
    features: [
      "High durability for long-term repeated use",
      "Anatomically accurate structure of humerus, scapula, and shoulder musculature",
      "Differentiated materials replicate realistic tissue response across anatomical layers",
      "Suitable for shoulder anatomy comprehension and tissue management training"
    ],
    image: "/images/L-SHJ-01/L-SHJ-01_Shoulder Stand Model B 1.jpg",
    gallery: [
      "/images/L-SHJ-01/L-SHJ-01_Shoulder Stand Model B 1.jpg"
    ]
  },
  {
    id: "ss-76",
    slug: "subcutaneous-thigh-injection-trainer",
    name: "Subcutaneous Thigh Injection Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Wearable medical-grade silicone thigh trainer with internal fluid chamber for subcutaneous injection angle, depth control, and medication delivery practice.",
    description: `The Subcutaneous Thigh Injection Trainer provides a realistic and structured environment for learning safe and accurate subcutaneous injection techniques. Made from premium medical-grade silicone, the trainer mimics the soft elasticity and tactile feel of real human tissue, offering natural resistance and pressure feedback during needle insertion.

The internal chamber allows hands-on practice with actual fluid injection, helping learners understand medication delivery and absorption within the subcutaneous layer. This feature enhances both technical proficiency and conceptual understanding of drug administration.

Its wearable thigh-band design ensures secure fixation on the body, enabling realistic posture training and simulation of patient-self-injection scenarios. Lightweight yet durable, the trainer is widely used in nursing and medical education programs, hospital staff training, emergency simulations, and patient education, making it an essential tool for mastering subcutaneous thigh injection skills.`,
    features: [
      "Realistic thigh shape and texture using medical-grade silicone",
      "Provides lifelike skin elasticity and subcutaneous resistance",
      "Internal chamber supports actual fluid injection practice",
      "Wearable thigh-band design ensures stable and clinically relevant positioning",
      "Durable construction suitable for repeated training"
    ],
    image: "/images/L-SCT-01/L-SCT-01_1.jpg",
    gallery: [
      "/images/L-SCT-01/L-SCT-01_1.jpg",
      "/images/L-SCT-01/L-SCT-01_2.jpg",
      "/images/L-SCT-01/L-SCT-01_Spec.png"
    ]
  },
  {
    id: "ss-75",
    slug: "meniscus-suture-trainer",
    name: "Meniscus Suture Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Anatomical knee model with meniscus, ligaments, and cartilage for arthroscopic meniscal repair, suturing technique, and incision training under realistic tissue resistance.",
    description: `The Meniscus Suture Trainer provides a highly realistic environment for learning and practicing complex meniscal repair techniques. Its anatomical structure includes the femur, tibia, patella, and surrounding ligamentous and cartilaginous components, enabling trainees to study tear patterns, approach strategies, and suturing methods.

The model delivers lifelike tactile feedback during incision and suturing, closely simulating the texture and resistance of actual meniscal tissue. The internal joint structure supports training under arthroscopic visualization, allowing learners to experience spatial constraints and instrument handling similar to real surgery.

Reinforced silicone and composite materials ensure long-term durability even with repeated suturing and cutting. This trainer is widely used in medical schools, orthopedic surgery programs, and sports medicine training centers, providing essential hands-on experience for mastering meniscus repair procedures.`,
    features: [
      "Anatomically accurate reproduction of the knee joint including meniscus, ligaments, and cartilage",
      "Realistic tissue tension and resistance during suturing and incision",
      "Supports training under arthroscopic visualization for realistic surgical positioning",
      "High-durability silicone and composite materials suitable for repeated practice",
      "Ideal for orthopedic and sports medicine education"
    ],
    image: "/images/L-MST-01/L-MST-01_Meniscus Suture Trainer 1.jpg",
    gallery: [
      "/images/L-MST-01/L-MST-01_Meniscus Suture Trainer 1.jpg",
      "/images/L-MST-01/L-MST-01_Meniscus Suture Trainer 2.jpg",
      "/images/L-MST-01/L-MST-01_Meniscus Suture Trainer 3.jpg",
      "/images/L-MST-01/L-MST-01_Meniscus Suture Trainer 4.jpg",
      "/images/L-MST-01/L-MST-01_Meniscus Suture Trainer 5.jpg",
      "/images/L-MST-01/L-MST-01_Meniscus Suture Trainer 6.jpg",
      "/images/L-MST-01/L-MST-01_Meniscus Suture Trainer spec.jpg"
    ]
  },
  {
    id: "ss-74",
    slug: "knee-surgery-trainer",
    name: "Knee Surgery Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "High-precision multi-layer anatomical knee model with skin, muscle, ligaments, and cartilage for incision, dissection, suturing, and intra-articular procedure training.",
    description: `The Knee Surgery Trainer simulates realistic orthopedic surgical conditions by replicating the anatomical structures and tissue responses of the human knee. The layered configuration—skin, fat, muscle, ligaments, and cartilage—provides precise tactile feedback during incision, layered dissection, and suturing.

Accurate reproduction of the femur, tibia, patella, and joint capsule enables a wide range of procedural practice, including arthroscopic approaches and open joint access. The modular system allows instructors to simulate various clinical scenarios and disease states, making the trainer highly versatile for advanced surgical education.

Constructed from high-elasticity medical-grade silicone, the model maintains shape integrity even after extensive repeated use. It serves as a core educational tool in medical schools, orthopedic programs, surgical simulation centers, and research facilities requiring detailed and realistic knee surgery training.`,
    features: [
      "High-precision anatomical reproduction of the human knee joint",
      "Multi-layer design including skin, fat, muscle, ligaments, and cartilage",
      "Provides realistic resistance during incision, dissection, and suturing",
      "Supports intra-articular access and arthroscopy-related procedural training",
      "Modular replaceable components allow simulation of various clinical cases",
      "Durable medical-grade silicone resists deformation during repeated use"
    ],
    image: "/images/L-KNS-01/L-KNS-01_Knee Surgery Trainer 1.jpg",
    gallery: [
      "/images/L-KNS-01/L-KNS-01_Knee Surgery Trainer 1.jpg",
      "/images/L-KNS-01/L-KNS-01_Knee Surgery Trainer 2.jpg",
      "/images/L-KNS-01/L-KNS-01_Knee Surgery Trainer 3.jpg",
      "/images/L-KNS-01/L-KNS-01_Knee Surgery Trainer 4.jpg",
      "/images/L-KNS-01/L-KNS-01_Knee Surgery Trainer 5.jpg",
      "/images/L-KNS-01/L-KNS-01_Knee Surgery Trainer 6.jpg",
      "/images/L-KNS-01/L-KNS-01_Knee Surgery Trainer 7.jpg",
      "/images/L-KNS-01/L-KNS-01_Knee Surgery Trainer 8.jpg",
      "/images/L-KNS-01/L-KNS-01_Knee Surgery Trainer 9.jpg",
      "/images/L-KNS-01/L-KNS-01_Knee Surgery Trainer 10.jpg",
      "/images/L-KNS-01/L-KNS-01_Knee Surgery Trainer spec.jpg"
    ]
  },
  {
    id: "ss-73",
    slug: "knee-aspiration-trainer",
    name: "Knee Aspiration Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Multi-layer anatomical knee model with simulated joint cavity for synovial fluid aspiration, intra-articular injection, and arthrocentesis needle insertion practice.",
    description: `This knee aspiration trainer provides a realistic environment for practicing intra-articular needle insertion, synovial fluid aspiration, and injection. The multi-layer anatomical structure—composed of skin, fat, muscle, cartilage, and joint cavity—helps trainees understand correct needle depth, angle, and tactile resistance.

The joint cavity delivers lifelike pressure response and fluid behavior, allowing users to experience clinically relevant feedback during aspiration. The model also recreates natural patella palpation, supporting accurate identification of the entry point for arthrocentesis.

Constructed from durable medical-grade silicone, the trainer maintains structural stability during repeated use and is suitable for orthopedic training programs, hospital skill centers, and clinical education courses. Adjustable positioning allows practice across various approach angles, offering a comprehensive training solution for mastering knee joint aspiration.`,
    features: [
      "Anatomically accurate knee joint structure modeled after real human anatomy",
      "Layered design distinguishing skin, fat, muscle, cartilage, and joint cavity",
      "Provides realistic needle resistance when passing through each anatomical layer",
      "Joint cavity simulates synovial fluid pressure and aspiration feedback",
      "High-durability medical-grade silicone supports repeated practice without deformation"
    ],
    image: "/images/L-KAT-01/L-KAT-01_Knee Aspiration Trainer 1.jpg",
    gallery: [
      "/images/L-KAT-01/L-KAT-01_Knee Aspiration Trainer 1.jpg",
      "/images/L-KAT-01/L-KAT-01_Knee Aspiration Trainer 2.jpg",
      "/images/L-KAT-01/L-KAT-01_Knee Aspiration Trainer 3.jpg",
      "/images/L-KAT-01/L-KAT-01_Knee Aspiration Trainer 4.jpg",
      "/images/L-KAT-01/L-KAT-01_Knee Aspiration Trainer 5.jpg",
      "/images/L-KAT-01/L-KAT-01_Knee Aspiration Trainer spec.jpg"
    ]
  },
  {
    id: "ss-71",
    slug: "intravenous-injection-hand-trainer-wearable",
    name: "Intravenous Injection Hand Trainer – Wearable",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Wearable silicone hand trainer with motorized blood circulation for realistic venipuncture, cannula insertion, vein palpation, and ergonomic IV injection practice.",
    description: `This wearable intravenous injection hand trainer provides a highly realistic environment for practicing venipuncture, blood sampling, and cannula insertion. The silicone hand pad incorporates vein-like tubing that accurately replicates vessel depth and position, supporting precise palpation and needle entry techniques.

The built-in motorized circulation system delivers natural blood backflow upon correct puncture, reinforcing proper needle positioning and depth control. The wearable design enables users to train in authentic clinical posture, improving ergonomics and injection accuracy.

The modular configuration includes a detachable manikin hand model for individual practice. Reinforced internal structures prevent tubing damage and ensure long-term durability. This trainer is widely used in medical schools, nursing programs, hospitals, and emergency care education centers to support immersive, safe, and realistic IV training.`,
    features: [
      "Realistic silicone skin replicating human hand texture and elasticity",
      "Embedded vein tubing simulating accurate vein depth, location, and diameter",
      "Motorized circulation system provides natural blood backflow when puncture is successful",
      "Wearable structure supports true clinical positioning and ergonomic needle control",
      "Reinforced internal tubing prevents vessel damage during repeated use",
      "Modular components allow easy transport, assembly, and storage"
    ],
    image: "/images/L-IVH-01/L-IVH-01_Intravenous Injection Hand Trainer Wearable 1.jpg",
    gallery: [
      "/images/L-IVH-01/L-IVH-01_Intravenous Injection Hand Trainer Wearable 1.jpg",
      "/images/L-IVH-01/L-IVH-01_Intravenous Injection Hand Trainer Wearable 2.jpg",
      "/images/L-IVH-01/L-IVH-01_Intravenous Injection Hand Trainer Wearable 3.jpg",
      "/images/L-IVH-01/L-IVH-01_Intravenous Injection Hand Trainer Wearable 4.jpg",
      "/images/L-IVH-01/L-IVH-01_Intravenous Injection Hand Trainer Wearable spec.jpg"
    ]
  },
  {
    id: "ss-70",
    slug: "intravenous-injection-arm-trainer",
    name: "Intravenous Injection Arm Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Full-length anatomical arm trainer with motorized blood circulation for IV injection, catheter insertion, vein palpation, and venipuncture technique practice.",
    description: `This Intravenous Injection Arm Trainer provides a realistic training environment for venipuncture, IV catheter insertion, and blood sampling. The model reproduces human arm anatomy, including vein depth, pathway, and tactile characteristics, allowing learners to practice with accurate feedback.

The integrated circulation system produces natural blood backflow when the vein is correctly punctured. This helps users verify needle placement and develop confidence in vascular access techniques. The layered design—skin, subcutaneous tissue, and vein tubing—delivers realistic resistance as the needle advances through each layer.

Durable medical-grade silicone maintains structural integrity during repeated practice. Replaceable components reduce maintenance costs and allow continuous, hygienic operation. This trainer is widely used in hospitals, nursing schools, emergency care programs, and clinical skills centers for mastering venipuncture and IV administration with precision.`,
    features: [
      "Anatomically accurate arm structure with realistic skin texture",
      "Internal blood circulation system provides natural backflow during venipuncture",
      "Full-length venous pathway supports multi-site puncture practice",
      "Multi-layer silicone design replicates lifelike resistance during needle insertion",
      "Durable materials ensure long-term use with repeated training",
      "Replaceable pads and components support hygienic and cost-effective operation"
    ],
    image: "/images/L-IVA-01/L-IVA-01_Intravenous Injection Arm Trainer 1.jpg",
    gallery: [
      "/images/L-IVA-01/L-IVA-01_Intravenous Injection Arm Trainer 1.jpg",
      "/images/L-IVA-01/L-IVA-01_Intravenous Injection Arm Trainer 1(1).jpg",
      "/images/L-IVA-01/L-IVA-01_Intravenous Injection Arm Trainer 2.jpg",
      "/images/L-IVA-01/L-IVA-01_Intravenous Injection Arm Trainer 2(1).jpg",
      "/images/L-IVA-01/L-IVA-01_Intravenous Injection Arm Trainer 3.jpg",
      "/images/L-IVA-01/L-IVA-01_Intravenous Injection Arm Trainer 3(1).jpg",
      "/images/L-IVA-01/L-IVA-01_Intravenous Injection Arm Trainer 4.jpg",
      "/images/L-IVA-01/L-IVA-01_Intravenous Injection Arm Trainer 4(1).jpg",
      "/images/L-IVA-01/L-IVA-01_Intravenous Injection Arm Trainer 5.jpg",
      "/images/L-IVA-01/L-IVA-01_Intravenous Injection Arm Trainer 6.jpg",
      "/images/L-IVA-01/L-IVA-01_Intravenous Injection Arm Trainer Spec.jpg"
    ]
  },
  {
    id: "ss-69",
    slug: "deltoid-intramuscular-injection-trainer",
    name: "Deltoid Intramuscular Injection Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Three-layer anatomical deltoid model with skin, fat, and muscle for realistic IM injection angle control, depth feedback, and needle insertion practice.",
    description: `This deltoid intramuscular injection trainer is designed to provide a realistic and reliable platform for mastering IM injection skills. Its anatomical design replicates the natural shape and contour of the upper arm, allowing trainees to practice positioning and injection angles with accuracy.

The multilayer configuration—skin, subcutaneous fat, and muscle—offers distinct tactile sensations as the needle progresses through each layer. This helps learners understand proper depth control and build confidence for clinical practice. The high-elasticity silicone ensures long-term durability and consistent performance even with repeated use.

The skin-like appearance enhances the realism of demonstration and training sessions, making this model suitable for nursing schools, hospitals, medical education programs, and simulation centers seeking high-fidelity IM injection training tools.`,
    features: [
      "Anatomically accurate curvature and placement of the human deltoid region",
      "Three-layer construction of skin, fat, and muscle for lifelike IM injection response",
      "Provides realistic needle resistance and depth feedback during insertion",
      "High-elasticity medical-grade silicone maintains shape during repeated practice",
      "Ideal for nursing education, clinical skills training, and simulation programs"
    ],
    image: "/images/L-IMD-03/L-IMD-03_Deltoid Intramuscular Injection Trainer 1.jpg",
    gallery: [
      "/images/L-IMD-03/L-IMD-03_Deltoid Intramuscular Injection Trainer 1.jpg",
      "/images/L-IMD-03/L-IMD-03_Deltoid Intramuscular Injection Trainer 2.jpg",
      "/images/L-IMD-03/L-IMD-03_Deltoid Intramuscular Injection Trainer - Spec.png"
    ]
  },
  {
    id: "ss-68",
    slug: "foot-model",
    name: "Foot Model",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "High-elasticity silicone foot model with accurate proportions, detailed toes, dorsum, arch, and plantar surface for anatomy education and pre-clinical demonstration.",
    description: `This foot model is designed to support anatomical education by replicating the natural shape and proportions of the human foot. It features detailed structures such as toe joints, skin folds, and surface contours, helping learners visualize anatomical landmarks with clarity.

The model is made of soft, high-elasticity medical silicone that closely simulates real skin texture. Subtle muscle outlines on the dorsum, vascular elevations, and fine plantar details contribute to a realistic tactile experience during demonstration or examination.

Its durable construction ensures stable performance over long-term use, while the lightweight design makes it easy to transport between classrooms, clinics, and research environments. This model provides both visual and tactile accuracy, enhancing effective and immersive foot anatomy education.`,
    features: [
      "Accurate replication of human foot proportions and external structure",
      "High-elasticity silicone mimics real skin texture and flexibility",
      "Detailed representation of toes, dorsum, arch, and plantar surface",
      "Durable medical-grade material suitable for repeated educational use",
      "Lightweight design for easy transport and display"
    ],
    image: "/images/L-FOO-01/L-FOO-01_Foot Model 1.jpg",
    gallery: [
      "/images/L-FOO-01/L-FOO-01_Foot Model 1.jpg",
      "/images/L-FOO-01/L-FOO-01_Foot Model 2.jpg",
      "/images/L-FOO-01/L-FOO-01_Foot Model spec.jpg"
    ]
  },
  {
    id: "ss-67",
    slug: "finger-surgery-trainer",
    name: "Finger Surgery Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Multi-layer anatomical finger model with skin, nerves, vessels, tendons, and bone for incision, suturing, amputation, and excision technique training.",
    description: `This Finger Surgery Trainer is designed for high-precision training in orthopedic, plastic, and microsurgical techniques. The anatomical structure of the human finger is faithfully replicated, allowing learners to practice incision, suturing, amputation, and excision with realistic feedback.

The multi-layer design—comprising skin, nerves, vessels, tendons, and bone—provides tactile responses similar to real surgical procedures. Each layer delivers distinct resistance, helping trainees understand depth control and accurate tissue handling.

Constructed from medical-grade silicone, the model maintains structural stability during repeated practice. The replaceable finger module supports hygienic and economical long-term use, making the trainer ideal for medical schools, surgical centers, and hospital-based skills programs.`,
    features: [
      "Realistic anatomical reproduction of finger size, shape, and joint structure",
      "Multi-layer composition including skin, nerves, vessels, tendons, and bone",
      "Provides lifelike resistance during cutting and suturing",
      "Durable medical-grade silicone suitable for repeated use",
      "Replaceable finger module enables cost-effective long-term training"
    ],
    image: "/images/L-FIS-01/L-FIS-01_Finger Surgery Trainer 1.jpg",
    gallery: [
      "/images/L-FIS-01/L-FIS-01_Finger Surgery Trainer 1.jpg",
      "/images/L-FIS-01/L-FIS-01_Finger Surgery Trainer 2.jpg",
      "/images/L-FIS-01/L-FIS-01_Finger Surgery Trainer 3.jpg"
    ]
  },
  {
    id: "ss-65",
    slug: "burn-treatment-model",
    name: "Burn Treatment Model",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Realistic 2nd–3rd degree burn silicone pad for wound cleansing, dressing, debridement, ointment application, and infection control training.",
    description: `This burn treatment model provides a realistic platform for training in emergency burn care, wound management, and infection prevention. The simulated burn surface represents multiple burn depths, allowing trainees to understand tissue damage and practice clinical procedures such as cleansing, disinfection, ointment application, and dressing techniques.

The silicone surface mimics the elasticity and tactile feel of human soft tissue, offering natural responses during dressing changes or ointment absorption. An adjustable strap on the back allows the pad to be attached to different body parts, enabling scenario-based practice and improving patient communication training.

Made from high-durability medical-grade silicone, the model is easy to clean and reusable, making it ideal for repeated training sessions. It is widely used in hospitals, medical schools, nursing programs, and emergency care education as a reliable simulator for mastering burn treatment skills.`,
    features: [
      "Realistic color and texture replicating true burn tissue",
      "Accurately represents 2nd–3rd degree burn layers",
      "Supports ointment application, cleansing, debridement, dressing, and simple suturing",
      "Durable medical-grade silicone suitable for repeated use",
      "Lightweight, flexible pad that can be strapped to various body areas",
      "Designed based on real patient case characteristics"
    ],
    image: "/images/L-BUR-01/L-BUR-01_Burn Treatment Model 1.jpg",
    gallery: [
      "/images/L-BUR-01/L-BUR-01_Burn Treatment Model 1.jpg",
      "/images/L-BUR-01/L-BUR-01_Burn Treatment Model 2.jpg",
      "/images/L-BUR-01/L-BUR-01_Burn Treatment Model 3.jpg",
      "/images/L-BUR-01/L-BUR-01_Burn Treatment Model Spec.jpg"
    ]
  },
  {
    id: "ss-60",
    slug: "eyeball-holder",
    name: "Eyeball Holder",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Head-shaped silicone eyeball holder with removable eye block for stable ophthalmic surgery simulation, device placement training, and repeated hygienic practice.",
    description: `This eyeball holder is designed to support realistic ophthalmic procedure training by securely stabilizing artificial eyeballs. The model features a head-shaped base with a soft silicone layer that replicates natural skin texture, allowing users to practice device placement and handling with greater accuracy.

The removable eye block structure enables easy insertion, removal, and replacement of artificial eyeballs. This design supports repeated training sessions while maintaining hygiene through simple cleaning. Its compatibility with various artificial eye types makes the model suitable for a wide range of ophthalmic training scenarios, from basic clinical skills to advanced microsurgical techniques.

Developed in collaboration with T.M.C Japan, the model provides long-term durability and structural stability, making it ideal for medical schools, simulation centers, and research institutions that require a dependable ophthalmology training tool.`,
    features: [
      "Realistic head-shaped base modeled after human facial contours",
      "Soft and elastic silicone pad offering lifelike tactile sensation",
      "Dedicated removable eye block for stable fixation and easy replacement",
      "Easy-to-clean structure suitable for repeated and hygienic practice",
      "Compatible with multiple types of artificial eyeballs for diverse training needs"
    ],
    image: "/images/H-EYH-01/H-EYH-01_Eyeball Holder 1.jpg",
    gallery: [
      "/images/H-EYH-01/H-EYH-01_Eyeball Holder 1.jpg",
      "/images/H-EYH-01/H-EYH-01_Eyeball Holder 2.jpg",
      "/images/H-EYH-01/H-EYH-01_Eyeball Holder 3.jpg"
    ]
  },
  {
    id: "ss-59",
    slug: "eye-model-for-lipiflow",
    name: "Eye Model for LipiFlow",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Anatomically shaped periocular silicone model for realistic LipiFlow dry-eye treatment simulation, device placement, angle control, and applicator fitting practice.",
    description: `This eye model is designed for realistic training and demonstration of the LipiFlow dry-eye treatment device. The model reproduces the anatomical curvature around the eye, allowing trainees to practice device placement, angle adjustments, and treatment positioning with precision.

The soft silicone material provides lifelike tactile feedback and detailed contour reproduction, enabling realistic practice of applicator fitting, pressure control, and alignment. Its durable design ensures that the model maintains shape and function even after extensive repeated use.

This trainer is well suited for ophthalmology programs, clinical education, and medical device demonstrations. It supports consistent and reliable hands-on training for enhancing proficiency in LipiFlow procedures.`,
    features: [
      "Designed specifically to replicate the clinical LipiFlow treatment environment",
      "Anatomically shaped periocular region enabling accurate positioning and angle control",
      "Soft, elastic silicone mimics real skin texture for realistic tactile feedback",
      "Durable construction suitable for repeated demonstrations and long-term training use"
    ],
    image: "/images/H-ELF-01/H-ELF-01_Eye model for Lipiflow 1.jpg",
    gallery: [
      "/images/H-ELF-01/H-ELF-01_Eye model for Lipiflow 1.jpg",
      "/images/H-ELF-01/H-ELF-01_Eye model for Lipiflow 2.jpg",
      "/images/H-ELF-01/H-ELF-01_Eye model for Lipiflow 3.jpg",
      "/images/H-ELF-01/H-ELF-01_Eye model for Lipiflow 4.jpg",
      "/images/H-ELF-01/H-ELF-01_Eye model for Lipiflow 5.jpg"
    ]
  },
  {
    id: "ss-58",
    slug: "ear-training-model",
    name: "Ear Training Model",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Anatomically accurate ear training model in soft elastic silicone for ENT examination, external ear inspection, impression-taking, and hearing aid fitting practice.",
    description: `This ear training model provides a realistic platform for practicing ear inspection, impression-taking, and hearing aid fitting. The detailed auricle and external auditory canal recreate the natural curves and depth of the human ear, enabling learners to understand anatomical features with clarity.

The silicone material offers soft and elastic tactile responses similar to human skin. It maintains its structural integrity even after repeated use with impression materials, making it suitable for continuous training.

The precisely shaped canal supports safe insertion techniques and allows learners to practice procedures up to the simulated tympanic membrane. This model is ideal for ENT education, audiology courses, hearing aid fitting workshops, and device manufacturing environments that require reliable and durable training tools.`,
    features: [
      "Anatomically accurate auricle and external auditory canal",
      "Soft, elastic silicone providing lifelike tactile feedback",
      "Supports repeated impression-taking without deformation",
      "Suitable for audiology, ENT, and hearing-aid training programs"
    ],
    image: "/images/H-EAR-02/H-EAR-02_1.jpg",
    gallery: [
      "/images/H-EAR-02/H-EAR-02_1.jpg",
      "/images/H-EAR-02/H-EAR-02_2.jpg",
      "/images/H-EAR-02/H-EAR-02_spec.jpeg"
    ]
  },
  {
    id: "ss-56",
    slug: "cervical-spine-model",
    name: "Cervical Spine Model",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Freestanding cervical spine model with anatomically accurate vertebrae and nerve structures for demonstrating cervical spine-related treatments and surgical procedures.",
    description: `This cervical spine model is designed to demonstrate cervical spine-related treatments. It features anatomically accurate cervical vertebrae and nerve structures mounted on a dedicated stand for stable, freestanding use during demonstrations and training.

The model provides a clear representation of spinal anatomy, making it suitable for surgical education, medical device demonstrations, and clinical procedure explanation in orthopedic and neurosurgical training environments.`,
    features: [
      "Anatomically accurate cervical vertebrae and nerve representation",
      "Integrated stand allows stable freestanding use during demonstrations",
      "High-durability construction suitable for repeated handling and display",
      "Ideal for cervical spine treatment demonstration and surgical education"
    ],
    image: "/images/H-CVC-01(Discontinue)/H-CVC-01_Cervical Spine Model 1.jpg",
    gallery: [
      "/images/H-CVC-01(Discontinue)/H-CVC-01_Cervical Spine Model 1.jpg",
      "/images/H-CVC-01(Discontinue)/H-CVC-01_Cervical Spine Model 2.jpg",
      "/images/H-CVC-01(Discontinue)/H-CVC-01_Cervical Spine Model spec.jpg"
    ]
  },
  {
    id: "ss-55",
    slug: "craniotomy-dura-mater-trainer",
    name: "Craniotomy Dura Mater Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Multi-layer craniotomy trainer with scalp, skull, dura mater, and brain for realistic craniotomy and dural incision practice, with replaceable components for long-term neurosurgical training.",
    description: `This trainer provides a realistic setting for practicing craniotomy procedures and dura mater handling. The layered structure — scalp, skull, dura, and brain — recreates the tactile differences felt during incision, bone removal, and dural opening.

The skull and dura layers are engineered to deliver lifelike resistance. This allows trainees to sense the changes in depth control and separation that occur during actual surgery. The dura layer also offers realistic tension during suturing, making it suitable for fine repair techniques.

All major components are replaceable. This enables repeated practice sessions without compromising training quality. Its durability and structural accuracy make the model well-suited for simulation centers, neurosurgery programs, and research institutions that require reliable craniotomy training tools.`,
    features: [
      "Anatomically accurate skull structure modeled from real morphology",
      "Multi-layer design including scalp, skull, dura mater, and brain",
      "Realistic tactile separation between bone and dura layers",
      "Replaceable components support repeated training with consistent performance",
      "Durable materials suitable for long-term neurosurgical education"
    ],
    image: "/images/H-CDM-01/H-CDM-01_Craniotomy Dura Mater Trainer(Johnson & Johnson) 1.jpg",
    gallery: [
      "/images/H-CDM-01/H-CDM-01_Craniotomy Dura Mater Trainer(Johnson & Johnson) 1.jpg",
      "/images/H-CDM-01/H-CDM-01_Craniotomy Dura Mater Trainer(Johnson & Johnson) 2.jpg",
      "/images/H-CDM-01/H-CDM-01_Craniotomy Dura Mater Trainer(Johnson & Johnson) 3.jpg",
      "/images/H-CDM-01/H-CDM-01_Craniotomy Dura Mater Trainer(Johnson & Johnson) 4.jpg"
    ]
  },
  {
    id: "ss-54",
    slug: "cerebrovascular-model",
    name: "Cerebrovascular Model",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Anatomically accurate transparent silicone cerebrovascular model for catheter navigation, embolization, angioplasty, and stent placement training with realistic blood-flow visualization.",
    description: `This cerebrovascular model replicates the complexity of human brain vessels for advanced neurosurgical and interventional training. The branching pattern and curvature follow true anatomical structures, enabling learners to practice catheter navigation and understand blood-flow dynamics with accuracy.

The transparent silicone body makes the movement of injected fluid and embolic materials easy to observe. It also reflects realistic vascular responses when pressure or flow changes, offering a highly immersive training experience for various neuro-interventional procedures.

Two manufacturing styles are available. One option embeds the vascular network within clear silicone gel, providing stable support and enhanced visualization. The other option uses a vessel-only structure mounted on a customized holder, which allows direct manipulation of the vascular path depending on the training scenario.

Durable materials ensure consistent performance during repeated use. The model is ideal for simulation centers, medical schools, research facilities, and training programs that require reliable and realistic cerebrovascular practice.`,
    features: [
      "Anatomically accurate design based on real human cerebrovascular structures",
      "Transparent silicone allows clear visualization of internal blood flow",
      "Vessels respond realistically to variations in injected pressure and flow rate",
      "Suitable for catheter navigation, embolization, angioplasty, and stent placement",
      "Two manufacturing options depending on training needs"
    ],
    image: "/images/H-CBV-01/H-CBV-01_1.jpg",
    gallery: [
      "/images/H-CBV-01/H-CBV-01_1.jpg",
      "/images/H-CBV-01/H-CBV-01_2.jpg",
      "/images/H-CBV-01/H-CBV-01_3.jpg",
      "/images/H-CBV-01/H-CBV-01_4.jpg",
      "/images/H-CBV-01/H-CBV-01_5.jpg",
      "/images/H-CBV-01/H-CBV-01_6.jpg",
      "/images/H-CBV-01/H-CBV-01_7.jpg",
      "/images/H-CBV-01/H-CBV-01_8.jpg",
      "/images/H-CBV-01/H-CBV-01_9.jpg",
      "/images/H-CBV-01/H-CBV-01_10.jpg",
      "/images/H-CBV-01/H-CBV-01_11.jpg",
      "/images/H-CBV-01/H-CBV-01_12.jpg",
      "/images/H-CBV-01/H-CBV-01_13.jpg",
      "/images/H-CBV-01/H-CBV-01_14.jpg",
      "/images/H-CBV-01/H-CBV-01_15.jpg",
      "/images/H-CBV-01/H-CBV-01_16.jpg",
      "/images/H-CBV-01/H-CBV-01_17.jpg",
      "/images/H-CBV-01/H-CBV-01_18.jpg",
      "/images/H-CBV-01/H-CBV-01_19.jpg"
    ]
  },
  {
    id: "ss-53",
    slug: "sternal-fixation-trainer",
    name: "Sternal Fixation Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Anatomically inspired ribcage and sternum trainer for ZIPFIX sternal fixation training, supporting sternotomy, cable routing, and tensioning with suction-cup tabletop stability.",
    description: `This sternal fixation trainer is designed for training with the DePuy Synthes ZIPFIX system. It reproduces the ribcage and sternum with realistic anatomical proportions.

Users can practice sternotomy, plate positioning, cable routing, and fixation in a step-by-step manner. Each step provides tactile feedback similar to real procedures. The bottom of the trainer includes suction cups that secure the model to a tabletop and reduce unwanted movement, allowing focused and uninterrupted training.

The trainer is made from durable synthetic materials that maintain their shape even after repeated use. It is suitable for thoracic surgery training, medical device demonstrations, and product education.`,
    features: [
      "Anatomically inspired ribcage and sternum structure",
      "Supports full ZIPFIX procedural steps including incision, cable routing, and fixation",
      "Realistic tactile feedback during cutting and fixation",
      "Suction cups provide stable fixation to the tabletop",
      "Durable synthetic material suitable for repeated training"
    ],
    image: "/images/C-STF-01/C-STF-01_Sternal Fixation Trainer 1.jpg",
    gallery: [
      "/images/C-STF-01/C-STF-01_Sternal Fixation Trainer 1.jpg",
      "/images/C-STF-01/C-STF-01_Sternal Fixation Trainer 2.jpg",
      "/images/C-STF-01/C-STF-01_Sternal Fixation Trainer 3.jpg",
      "/images/C-STF-01/C-STF-01_Sternal Fixation Trainer 4.jpg",
      "/images/C-STF-01/C-STF-01_Sternal Fixation Trainer 5.jpg",
      "/images/C-STF-01/C-STF-01_Sternal Fixation Trainer Spec.jpg"
    ]
  },
  {
    id: "ss-52",
    slug: "lung-segmentectomy-trainer",
    name: "Lung Segmentectomy Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Realistic lung segmentectomy trainer with accurate lobar and segmental anatomy for practicing segmental incision, bronchial separation, vascular identification, and suturing techniques.",
    description: `This lung segmentectomy trainer is designed for comprehensive training in segmental lung resection techniques. Modeled after realistic lung anatomy, it allows trainees to practice step-by-step procedures including segmental incision, bronchial separation, vascular identification, and suturing.

The silicone composite material provides lifelike tactile feedback and natural resistance during incision and dissection, closely simulating real pulmonary tissue behavior. Its segmented structure allows clear visualization of anatomical relationships among bronchi, vessels, and lung parenchyma.

Durable and easy to clean, this trainer supports repeated use and offers an optimized environment for thoracic surgery programs, medical schools, and simulation centers to train and master lung segmentectomy techniques.`,
    features: [
      "Realistic lung texture and coloration closely replicating human pulmonary tissue",
      "Clear visualization of lobar and segmental anatomy including bronchial and vascular structures",
      "Provides lifelike resistance during incision, separation, and suturing",
      "High durability suitable for repeated dissection and reconstruction training"
    ],
    image: "/images/C-LUS-01/C-LUS-01_Lung Segmentectomy Trainer 1.jpg",
    gallery: [
      "/images/C-LUS-01/C-LUS-01_Lung Segmentectomy Trainer 1.jpg",
      "/images/C-LUS-01/C-LUS-01_Lung Segmentectomy Trainer 2.jpg",
      "/images/C-LUS-01/C-LUS-01_Lung Segmentectomy Trainer 3.jpg"
    ]
  },
  {
    id: "ss-51",
    slug: "lung-trachea-suture-trainer",
    name: "Lung & Trachea Suture Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Anatomically accurate lung and trachea silicone models for practicing anastomosis, airway reconstruction, incision, and suture techniques in thoracic surgery simulation.",
    description: `This Lung & Trachea Suture Trainer is designed to provide a realistic environment for practicing surgical techniques involving the connection and reconstruction of the lung and trachea.

Both components replicate anatomical morphology and tissue behavior, enabling trainees to practice incision, suturing, anastomosis, and structural reattachment with lifelike tactile feedback. The trachea component is made from elastic silicone that accurately reproduces airway stiffness and tension, offering realistic response during needle penetration and suture tightening.

The lung model simulates the softness and movement of real pulmonary tissue, supporting advanced training scenarios involving resection and reconstruction. Durable and suitable for repeated use, this trainer is ideal for thoracic surgery programs, medical schools, simulation centers, and medical device testing environments.`,
    features: [
      "Anatomically accurate lung and trachea structures designed for realistic surgical training",
      "High-elasticity silicone provides true-to-life resistance during incision and suturing",
      "Allows practice of various suture patterns, reconnection techniques, and tissue handling",
      "Durable construction ensures minimal deformation even after repeated use",
      "Suitable for thoracic surgery education, airway reconstruction simulation, and device testing"
    ],
    image: "/images/C-LTS-01/C-LTS-01_Lung & Trachea Suture Trainer 1.jpg",
    gallery: [
      "/images/C-LTS-01/C-LTS-01_Lung & Trachea Suture Trainer 1.jpg",
      "/images/C-LTS-01/C-LTS-01_Lung & Trachea Suture Trainer 2.jpg"
    ]
  },
  {
    id: "ss-50",
    slug: "heart-surgery-costal-model",
    name: "Heart Surgery Costal Model",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Openable ribcage model for heart surgery training with adjustable sternum wire, acrylic angle board, and mesh-covered platform for real or simulated heart placement and sternum fixation practice.",
    description: `This model is designed to demonstrate and practice heart surgery within the ribcage. The ribcage can be opened and the wire between the sternum maintains a more expansive inner space during heart surgery simulation. An acrylic board on the back allows the trainer angle to be adjusted to suit different training setups.

The wire-framed, mesh-covered internal platform accommodates a real or simulated heart for practice. Following heart surgery training, the model also supports sternum fixation technique demonstration, making it a comprehensive tool for cardiac surgical education.`,
    features: [
      "Openable ribcage structure replicating real surgical access conditions",
      "Sternal wire maintains expanded inner cavity during heart surgery practice",
      "Adjustable acrylic back board allows angle customization",
      "Mesh-covered wire platform supports real or simulated heart placement",
      "Supports both heart surgery and post-operative sternum fixation training"
    ],
    image: "/images/C-HSC-01/C-HSC-01_Heart Surgery Costal Model (TMC Only) 1.jpg",
    gallery: [
      "/images/C-HSC-01/C-HSC-01_Heart Surgery Costal Model (TMC Only) 1.jpg",
      "/images/C-HSC-01/C-HSC-01_Heart Surgery Costal Model (TMC Only) 2.jpg",
      "/images/C-HSC-01/C-HSC-01_Heart Surgery Costal Model (TMC Only) 3.jpg",
      "/images/C-HSC-01/C-HSC-01_Heart Surgery Costal Model (TMC Only) 4.jpg"
    ]
  },
  {
    id: "ss-49",
    slug: "breast-model-for-nsm",
    name: "Breast Model for NSM",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Human-size multi-layer breast model for robotic NSM training with Intuitive Surgical systems, featuring replaceable skin and breast components for subcutaneous dissection, excision, and suturing practice.",
    description: `This breast model is specifically developed for robotic Nipple-Sparing Mastectomy (NSM) training. It reproduces the true size, texture, and multi-layer structure of the human breast, enabling realistic practice of incisions, dissection, tumor removal, and suturing.

The internal space closely simulates the subcutaneous pocket and dissection planes essential for NSM procedures. Constructed from high-durability medical silicone, the model withstands repeated use and is ideal for robotic surgery simulation, surgical training programs, and medical device testing. Replaceable skin and breast components support long-term training consistency.`,
    features: [
      "Realistic human-size breast model with accurate anatomical layer structure",
      "Multi-layer composition including skin and mammary gland tissue for detailed surgical training",
      "Provides lifelike resistance and tissue feedback during incision, dissection, and suturing",
      "High-durability medical silicone ensures minimal deformation during repeated training",
      "Compatible with Intuitive Surgical robotic systems"
    ],
    image: "/images/C-BNS-01/C-BNS-01_1.jpg",
    gallery: [
      "/images/C-BNS-01/C-BNS-01_1.jpg",
      "/images/C-BNS-01/C-BNS-01_2.jpg",
      "/images/C-BNS-01/C-BNS-01_3.jpg",
      "/images/C-BNS-01/C-BNS-01_4.jpg",
      "/images/C-BNS-01/C-BNS-01_5.jpg",
      "/images/C-BNS-01/C-BNS-01_6.jpg",
      "/images/C-BNS-01/C-BNS-01_7.jpg",
      "/images/C-BNS-01/C-BNS-01_Spec.png"
    ]
  },
  {
    id: "ss-48",
    slug: "breast-model-for-nsm-simple",
    name: "Breast Model for NSM – Simple",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Robotic NSM training breast model compatible with Intuitive Surgical platforms, featuring soft elastic silicone, internal cavity for biological tissue, and realistic subcutaneous dissection and incision simulation.",
    description: `This simple-type breast model is designed for robotic Nipple-Sparing Mastectomy (NSM) training and is compatible with Intuitive Surgical platforms.

The anatomically realistic breast structure enables effective practice of subcutaneous dissection, incision, tumor removal, and suturing. The internal cavity allows the insertion of animal tissue, providing deeper tactile realism similar to actual surgical procedures. Made from soft, high-elasticity silicone, the model offers lifelike tissue resistance and maintains durability for repeated use. Its stable stand minimizes movement during procedures, enhancing precision and control for surgical training environments.`,
    features: [
      "Realistic human breast size and texture for accurate simulation",
      "Soft, elastic silicone allows lifelike incision, dissection, and suturing",
      "Simple structure optimized for cleaning, portability, and repeated use",
      "Compatible with robotic surgical platforms such as Intuitive Surgical systems",
      "Internal cavity accommodates biological tissue for advanced simulation"
    ],
    image: "/images/C-BNM-01/C-BNM-01_Breast Model for NSM - Simple_1.jpg",
    gallery: [
      "/images/C-BNM-01/C-BNM-01_Breast Model for NSM - Simple_1.jpg",
      "/images/C-BNM-01/C-BNM-01_Breast Model for NSM - Simple_2.jpg",
      "/images/C-BNM-01/C-BNM-01_Breast Model for NSM - Simple_3.jpg",
      "/images/C-BNM-01/C-BNM-01_Breast Model for NSM - Simple_Spec.jpeg"
    ]
  },
  {
    id: "ss-47",
    slug: "breast-implant-torso-model",
    name: "Breast Implant Torso Model",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Anatomically accurate breast implant torso with separate fat and pectoral muscle layers for demonstrating submuscular and subglandular implant placement, incision comparison, and augmentation training.",
    description: `This breast implant torso model provides a realistic platform for demonstrating augmentation mammaplasty procedures. Its layered anatomy — including skin, mammary gland fat, and two pectoral muscle layers — accurately represents both subglandular and submuscular implant placement.

The silicone composition recreates lifelike tactile resistance during incision, pocket creation, and implant insertion. Both breasts are fully functional, enabling comparison of implant sizes, placement methods, and incision types side-by-side.

Durable and deformation-resistant, the model is ideal for surgical workshops, patient consultations, and medical training environments.`,
    features: [
      "Anatomically accurate torso reflecting realistic breast, muscle, and chest wall structure",
      "Separate breast fat and pectoral muscle layers allow demonstration of multiple implant placement planes",
      "Realistic silicone texture replicates tissue resistance during incision and implant insertion",
      "Both breasts usable for comparing implant sizes, placement planes, and incision methods",
      "Highly durable for repeated demonstrations and workshops"
    ],
    image: "/images/C-BIT-01/C-BIT-01_Breast Implant Torso Model 1.jpg",
    gallery: [
      "/images/C-BIT-01/C-BIT-01_Breast Implant Torso Model 1.jpg",
      "/images/C-BIT-01/C-BIT-01_Breast Implant Torso Model 2.jpg",
      "/images/C-BIT-01/C-BIT-01_Breast Implant Torso Model 3.jpg",
      "/images/C-BIT-01/C-BIT-01_Breast Implant Torso Model 4.jpg",
      "/images/C-BIT-01/C-BIT-01_Breast Implant Torso Model 5.jpg"
    ]
  },
  {
    id: "ss-46",
    slug: "breast-implant-half-model",
    name: "Breast Implant Half Model",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Half-section breast implant model with multi-layer silicone anatomy (skin, muscle, chest wall) for augmentation mammaplasty demonstration, implant pocket creation, and surgical simulation.",
    description: `This breast implant half model provides a realistic platform for demonstrating augmentation mammaplasty procedures. Its half-section structure exposes internal breast layers, allowing clear visualization of incision routes, implant insertion, and pocket formation. The silicone material replicates lifelike elasticity and resistance, enabling precise surgical simulation. Durable and anatomically accurate, it is ideal for workshops, anatomy courses, and patient consultations.`,
    features: [
      "Half-section anatomical design enabling clear visualization of implant pathways",
      "Multi-layer structure (skin, muscle, chest wall) for realistic surgical explanation",
      "Medical-grade silicone providing lifelike elasticity and tactile resistance",
      "High durability suitable for repeated incision, implant placement, and training use"
    ],
    image: "/images/C-BIH-01/C-BIH-01_cover.jpg",
    gallery: [
      "/images/C-BIH-01/C-BIH-01_cover.jpg",
      "/images/C-BIH-01/C-BIH-01_cover_mall.jpg",
      "/images/C-BIH-01/C-BIH-01_1.jpg",
      "/images/C-BIH-01/C-BIH-01_2.jpg",
      "/images/C-BIH-01/C-BIH-01_3.jpg",
      "/images/C-BIH-01/C-BIH-01_4.jpg",
      "/images/C-BIH-01/C-BIH-01_5.jpg",
      "/images/C-BIH-01/C-BIH-01_6.jpg",
      "/images/C-BIH-01/C-BIH-01_7.jpg",
      "/images/C-BIH-01/C-BIH-01_Spec.png"
    ]
  },
  {
    id: "ss-45",
    slug: "bronchus-endoscopic-trainer",
    name: "Bronchus Endoscopic Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Anatomically accurate bronchoscopy trainer with trachea, carina, main bronchi, and segmental branches in soft flexible silicone for endoscope navigation, pathway recognition, and instrument handling practice.",
    description: `This bronchus endoscopic trainer provides a highly realistic environment for bronchoscopy practice and procedural education. Constructed from soft, flexible medical-grade silicone, the model replicates the natural elasticity and tactile resistance of human bronchial tissue, allowing lifelike endoscope navigation and manipulation.

The anatomical design includes the trachea, carina, right and left main bronchi, and their subsequent lobar and segmental branches, enabling trainees to practice pathway recognition, directional control, scope stabilization, and safe instrument handling. Its durable structure supports extensive repeated use, making it ideal for medical schools, simulation centers, and endoscopic training workshops.`,
    features: [
      "Anatomically accurate trachea and bronchial branching structure",
      "Soft, flexible silicone replicates real bronchial resistance during scope insertion",
      "Detailed pathways extending from the trachea to right/left main bronchi and distal segmental branches",
      "Compatible with various bronchoscopic devices for versatile training",
      "High durability with minimal deformation even after repeated endoscopic practice"
    ],
    image: "/images/C-BEN-01/C-BEN-01_Bronchus Endoscopic Trainer 1.jpg",
    gallery: [
      "/images/C-BEN-01/C-BEN-01_Bronchus Endoscopic Trainer 1.jpg",
      "/images/C-BEN-01/C-BEN-01_Bronchus Endoscopic Trainer 2.jpg",
      "/images/C-BEN-01/C-BEN-01_Bronchus Endoscopic Trainer spec.jpg"
    ]
  },
  {
    id: "ss-44",
    slug: "breast-anatomy-model-torso",
    name: "Breast Anatomy Model Torso",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Human-size breast anatomy torso with embedded multi-size lumps and anatomical layers for palpation training, self-examination education, and breast cancer awareness programs.",
    description: `This breast anatomy torso model is designed for realistic palpation training and breast cancer self-examination education. Constructed with detailed anatomical layers — including skin, subcutaneous fat, mammary gland tissue, and chest wall — the model accurately replicates the tactile characteristics of human breast tissue. Lumps of varying sizes are embedded within the breast to provide a realistic experience for identifying abnormalities during palpation.

Made from soft, high-elasticity medical silicone, the model offers lifelike touch and clear differentiation between normal and abnormal tissue. It is highly durable and retains its shape and texture even after repeated use. Ideal for medical schools, nursing programs, hospital education centers, and patient counseling environments, this model provides an effective platform for teaching anatomy, early detection techniques, and clinical communication.`,
    features: [
      "Realistic human-size torso with lifelike breast texture and elasticity",
      "Embedded lumps of various sizes for palpation training",
      "Anatomically accurate representation of skin, fat, glandular tissue, and chest wall",
      "Suitable for clinical education, patient counseling, and breast cancer awareness programs",
      "Durable medical-grade silicone designed for repeated long-term use"
    ],
    image: "/images/C-BAT-01/C-BAT-01_Breast Anatomy Torso Model 2026-1.jpg",
    gallery: [
      "/images/C-BAT-01/C-BAT-01_Breast Anatomy Torso Model 2026-1.jpg",
      "/images/C-BAT-01/C-BAT-01_Breast Anatomy Torso Model 2026-2.jpg"
    ]
  },
  {
    id: "ss-43",
    slug: "breast-anatomy-lactation-model",
    name: "Breast Anatomy Lactation Model",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Wearable breast anatomy model with embedded multi-size lumps, anatomical skin, fat, and mammary gland layers, and adjustable belt for palpation, self-examination, and patient communication training.",
    description: `This wearable breast anatomy model is designed for realistic training in palpation, lactation anatomy, and patient self-examination education. Constructed with soft, elastic medical-grade silicone, the model replicates the natural texture and contour of breast tissue, providing highly realistic tactile feedback during training.

The internal structure includes skin, fat, and mammary gland layers, with embedded lumps of varying sizes that simulate real clinical findings. This enables effective practice in breast examination, early abnormality detection, and patient communication training.

The adjustable wearable belt supports hands-on practice in natural positions, offering a realistic simulation environment. Highly durable and resistant to deformation, this model is ideal for repeated use in nursing schools, clinical skills labs, maternity education, and medical simulation centers.`,
    features: [
      "Wearable structure closely replicating the movement and contour of the human breast",
      "Embedded lumps of multiple sizes for realistic palpation training",
      "Detailed anatomical layers including skin, fat tissue, and mammary gland",
      "High-durability silicone providing lifelike elasticity and tactile response",
      "Adjustable belt system suitable for users of various body types"
    ],
    image: "/images/C-BAL-01/C-BAL-01_Breast Anatomy Lactation Model 1.jpg",
    gallery: [
      "/images/C-BAL-01/C-BAL-01_Breast Anatomy Lactation Model 1.jpg",
      "/images/C-BAL-01/C-BAL-01_Breast Anatomy Lactation Model 2.jpg",
      "/images/C-BAL-01/C-BAL-01_Breast Anatomy Lactation Model 3.jpg",
      "/images/C-BAL-01/C-BAL-01_Breast Anatomy Lactation Model 4.jpg",
      "/images/C-BAL-01/C-BAL-01_Breast Anatomy Lactation Model Spec.jpg"
    ]
  },
  {
    id: "ss-42",
    slug: "breast-anatomy-model-half",
    name: "Breast Anatomy Model Half",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Half-section breast anatomy model with multi-layer silicone construction including skin, fat, glandular tissue, muscle, and chest wall for implant procedure explanation and incision practice.",
    description: `This breast anatomy half-model is designed for anatomical education and surgical explanation, particularly for breast implant procedures. The internal layers — skin, fat, mammary gland, muscle, and chest wall — are clearly defined, enabling precise visualization and understanding of subglandular and submuscular implant approaches.

The medical-grade silicone replicates realistic softness and resistance, making the model highly effective for incision and suturing practice. Its durable construction ensures that the model maintains shape and performance even with repeated use, supporting long-term training in medical schools, surgical programs, aesthetic surgery workshops, and patient consultation settings.`,
    features: [
      "Half-section model replicating true human cross-sectional anatomy",
      "Multi-layer structure including skin, fat, glandular tissue, muscle, and chest wall",
      "Medical-grade silicone providing lifelike elasticity and tactile response",
      "Suitable for incision and suturing exercises",
      "High durability ensuring long-term, repeated practice without deformation"
    ],
    image: "/images/C-BAH-01/C-BAH-01_Breast Anatomy Half Model 1.jpg",
    gallery: [
      "/images/C-BAH-01/C-BAH-01_Breast Anatomy Half Model 1.jpg",
      "/images/C-BAH-01/C-BAH-01_Breast Anatomy Half Model 2.jpg",
      "/images/C-BAH-01/C-BAH-01_Breast Anatomy Half Model 3.jpg",
      "/images/C-BAH-01/C-BAH-01_Breast Anatomy Half Model 4.jpg",
      "/images/C-BAH-01/C-BAH-01_Breast Anatomy Half Model 5.jpg",
      "/images/C-BAH-01/C-BAH-01_Breast Anatomy Half Model 6.jpg",
      "/images/C-BAH-01/C-BAH-01_Breast Anatomy Half Model 7.jpg",
      "/images/C-BAH-01/C-BAH-01_Breast Anatomy Half Model Spec.jpg"
    ]
  },
  {
    id: "ss-41",
    slug: "artificial-heart-valve",
    name: "Artificial Heart Valve",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Compact silicone heart valve model with anatomically accurate cardiac valve structures for realistic valve replacement surgery and atrial-ventricular suturing training.",
    description: `This artificial heart valve model is designed for realistic training in valve replacement surgery and atrial-ventricular suturing techniques. The silicone construction accurately mimics the feel, elasticity, and resistance of real valve tissue, enabling true-to-life surgical practice.

Its compact design allows easy integration into various training setups, such as medical school simulation labs, procedural workshops, and device demonstrations. Durable and hygienic, the model is optimized for repeated suturing without deformation and is intended solely for medical education and training, not for clinical use.`,
    features: [
      "Anatomically accurate representation of cardiac valve structures",
      "High-elasticity silicone provides realistic tactile feedback during suturing",
      "Lightweight design suitable for portable and tabletop surgical training",
      "Maintains durability and shape even with repeated use",
      "Useful for clinical education, surgical simulation, and device demonstration"
    ],
    image: "/images/C-AHV-01/C-AHV-01_Artificial Heart Valve Model 1.jpg",
    gallery: [
      "/images/C-AHV-01/C-AHV-01_Artificial Heart Valve Model 1.jpg",
      "/images/C-AHV-01/C-AHV-01_Artificial Heart Valve Model Spec.jpg"
    ]
  },
  {
    id: "ss-40",
    slug: "wearable-pregnancy-palpation-model",
    name: "Wearable Pregnancy Palpation Model",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Wearable 28-week pregnancy palpation simulator with anatomically structured internal fetal model for Leopold manoeuvres, fetal part identification, and empathy-based clinical training.",
    description: `The Wearable Pregnancy Palpation Model is a premier educational tool designed to replicate the abdominal conditions of a 7-month (28-week) pregnancy. It provides a sophisticated platform for mastering fetal palpation and orientation assessment in a controlled simulation environment.

The internal fetal model is anatomically structured to provide distinct tactile feedback, allowing trainees to identify the fetal head, shoulders, and torso through deep palpation. This feature bridges the gap between theory and practice, helping learners develop the precise tactile skills required for diagnosing fetal presentation in clinical settings.

Beyond technical skills, the wearable design facilitates immersive empathy-based learning. By wearing the simulator, students can experience the physical weight and movement restrictions of an expectant mother, fostering better patient interaction and holistic care. Constructed from high-elasticity medical silicone, the simulator offers a lifelike texture while ensuring exceptional durability for rigorous, repeated use in nursing schools and clinical simulation centers.`,
    features: [
      "Realistic palpation feedback for clear identification of fetal head, shoulders, and torso",
      "Specifically engineered for deep palpation at the 28-week pregnancy stage",
      "Can be worn directly on a trainee or mannequin",
      "High-durability medical-grade silicone suitable for repeated training use",
      "Supports empathy-based learning through wearable immersive design"
    ],
    image: "/images/A-WPP-01/A-WPP-01_1.jpeg",
    gallery: [
      "/images/A-WPP-01/A-WPP-01_1.jpeg",
      "/images/A-WPP-01/A-WPP-01_2.jpeg",
      "/images/A-WPP-01/A-WPP-01_3.jpeg",
      "/images/A-WPP-01/A-WPP-01_spec.png"
    ]
  },
  {
    id: "ss-39",
    slug: "ureteroscopic-trainer",
    name: "Ureteroscopic Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Anatomically accurate ureteroscopic trainer with kidney, ureter, and bladder structures, three stone sizes (3/5/8mm), and replaceable organ modules for endoscopic stone removal and navigation training.",
    description: `This ureteroscopic trainer provides a realistic platform for training in endoscopic stone removal and navigation. Its anatomically accurate kidney, ureter, and bladder structures enable step-by-step practice from scope insertion to stone extraction.

Three different stone sizes offer graded difficulty, allowing trainees to experience diverse clinical scenarios. The silicone-based internal channels simulate true tissue resistance, supporting skill development in manipulation, visualization, and retrieval.

Replaceable organ modules ensure long-term use, making this model ideal for urology training programs, medical schools, simulation centers, and hands-on surgical workshops.`,
    features: [
      "Anatomically accurate upper urinary tract: kidney, ureter, and bladder",
      "Realistic internal resistance during endoscopic advancement",
      "Three stone sizes (3mm, 5mm, 8mm) for multi-level simulation",
      "Supports stone retrieval, navigation, wire control, and visualization practice",
      "Replaceable components for long-term, hygienic training"
    ],
    image: "/images/A-URS-01/A-URS-01_Ureteroscopic Trainer 1.jpg",
    gallery: [
      "/images/A-URS-01/A-URS-01_Ureteroscopic Trainer 1.jpg",
      "/images/A-URS-01/A-URS-01_Ureteroscopic Trainer 2.jpg",
      "/images/A-URS-01/A-URS-01_Ureteroscopic Trainer 3.jpg",
      "/images/A-URS-01/A-URS-01_Ureteroscopic Trainer 4.jpg",
      "/images/A-URS-01/A-URS-01_Ureteroscopic Trainer 5.jpg",
      "/images/A-URS-01/A-URS-01_Ureteroscopic Trainer 6.jpg",
      "/images/A-URS-01/A-URS-01_Ureteroscopic Trainer Spec.jpg",
      "/images/A-URS-01/A-URS-01-R4_1.jpg",
      "/images/A-URS-01/A-URS-01-R4_2.jpg",
      "/images/A-URS-01/A-URS-01-R5_1.jpg",
      "/images/A-URS-01/A-URS-01-R5_2.jpg"
    ]
  },
  {
    id: "ss-38",
    slug: "stomach-suture-model",
    name: "Stomach Suture Model",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Human-size anatomically accurate stomach suture model with high-elasticity silicone for realistic incision, needle penetration, and single-layer or layered closure practice.",
    description: `This stomach suture model is designed for realistic and effective training in stomach incision and suturing techniques. The anatomical design accurately reflects the contours and proportions of the human stomach, enabling learners to practice suturing with lifelike tactile feedback. The high-elasticity silicone material replicates the natural resistance felt during needle insertion and tissue manipulation, making it ideal for practicing various suturing patterns, including single-layer and layered closures.

The model's durability supports repeated practice without losing structural integrity, and it is compatible with tissue stands or laparoscopic trainers for expanded training setups. Suitable for medical schools, surgical residency programs, and clinical skill development courses, this model helps learners build confidence and precision in essential stomach suturing skills.`,
    features: [
      "Anatomically accurate stomach structure designed for realistic suturing practice",
      "Actual human-size proportions for enhanced spatial understanding",
      "Realistic tissue texture and resistance for lifelike needle penetration",
      "High-durability silicone suitable for repeated cutting and suturing",
      "Compatible with various sutures and standard surgical instruments"
    ],
    image: "/images/A-STS-01/A-STS-01_Stomach Suture Trainer 1.jpg",
    gallery: [
      "/images/A-STS-01/A-STS-01_Stomach Suture Trainer 1.jpg",
      "/images/A-STS-01/A-STS-01_Stomach Suture Trainer 2.jpg",
      "/images/A-STS-01/A-STS-01_Stomach Suture Trainer 3(Example of use).jpg",
      "/images/A-STS-01/A-STS-01_Stomach Suture Trainer Spec.jpg"
    ]
  },
  {
    id: "ss-37",
    slug: "stomach-surgery-trainer",
    name: "Stomach Surgery Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Anatomically accurate multi-layer stomach trainer with membrane adhesion simulation for incision, dissection, and suturing practice in open and laparoscopic surgical training.",
    description: `This stomach surgery trainer provides a realistic platform for practicing stomach incision, membrane dissection, and suturing techniques. The multi-layer stomach wall structure replicates lifelike tissue resistance, enabling precise surgical training.

A membrane layer is attached to the left gastric wall, simulating realistic adhesion found during surgery and encouraging careful dissection to avoid tissue injury. Durable, suture-friendly silicone ensures long-term use in medical schools, surgical residency programs, and procedural training centers.`,
    features: [
      "Anatomically accurate stomach structure with realistic surface texture",
      "Multi-layer stomach wall replicating natural resistance during incision and dissection",
      "Suitable for various suturing techniques",
      "High-durability silicone maintaining shape during repeated training",
      "Compatible with open and laparoscopic training setups"
    ],
    image: "/images/A-STO-01/A-STO-01_1.jpg",
    gallery: [
      "/images/A-STO-01/A-STO-01_1.jpg",
      "/images/A-STO-01/A-STO-01_2.jpg",
      "/images/A-STO-01/A-STO-01_3.jpg",
      "/images/A-STO-01/A-STO-01_4.jpg",
      "/images/A-STO-01/A-STO-01_Spec.png"
    ]
  },
  {
    id: "ss-36",
    slug: "stomach-esophagus-anastomosis-trainer",
    name: "Stomach & Esophagus Anastomosis Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Anatomically inspired silicone stomach and esophagus models for upper GI anastomosis training, suture placement, and knot-tying with realistic needle penetration resistance.",
    description: `This trainer provides a realistic platform for practicing stomach–esophagus anastomosis as part of upper GI reconstruction. The silicone organs replicate true tissue density and elasticity, enabling lifelike tactile feedback during suturing and knot-tying.

Designed for repeated use without deformation, the model can be combined with broader abdominal simulators for full-procedure training. Ideal for surgical residents, medical schools, and simulation centers, it supports the development of precision, consistency, and confidence in upper GI anastomosis techniques.`,
    features: [
      "Anatomically inspired stomach and esophagus structures",
      "High-elasticity silicone providing realistic needle penetration resistance",
      "Durable organ modules suitable for repeated suturing",
      "Compatible with abdominal trainers for multi-step surgical simulation",
      "Appropriate for foundational and advanced GI surgery training"
    ],
    image: "/images/A-SEA-01/A-SEA-01_Stomach & Esophagus Anastomosis Trainer 1.jpg",
    gallery: [
      "/images/A-SEA-01/A-SEA-01_Stomach & Esophagus Anastomosis Trainer 1.jpg",
      "/images/A-SEA-01/A-SEA-01_Stomach & Esophagus Anastomosis Trainer 2.jpg"
    ]
  },
  {
    id: "ss-35",
    slug: "spinal-dura-mater-suture-trainer",
    name: "Spinal Dura Mater Suture Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Multi-layer spinal trainer with skin, fascia, muscle, and dura mater for realistic dural incision, closure, and deep-layer needle handling in neurosurgical and spinal procedure training.",
    description: `This spinal dura mater suture trainer is designed to simulate realistic conditions for dural incision and closure training. Its multi-layer silicone structure mimics the density and needle response of real human tissues, allowing trainees to develop precision suturing and soft-tissue handling skills necessary for spinal and neurosurgical procedures.

The replaceable pad system supports repeated, hygienic practice without compromising structural integrity, making this model ideal for surgical training programs, medical schools, neurosurgical workshops, and clinical device demonstrations.`,
    features: [
      "Multi-layer anatomical design including skin, fascia, muscle, and dura mater",
      "Realistic tactile resistance during incision and suturing",
      "Durable silicone suitable for repeated, high-fidelity training",
      "Replaceable pad system for hygienic, long-term use",
      "Ideal for neurosurgical, spinal, and microsuturing education"
    ],
    image: "/images/A-SDS-01/A-SDS-01_Spinal Dura Mater Suture Trainer 1.jpg",
    gallery: [
      "/images/A-SDS-01/A-SDS-01_Spinal Dura Mater Suture Trainer 1.jpg",
      "/images/A-SDS-01/A-SDS-01_Spinal Dura Mater Suture Trainer 2.jpg",
      "/images/A-SDS-01/A-SDS-01_Spinal Dura Mater Suture Trainer 3.jpg",
      "/images/A-SDS-01/A-SDS-01_Spinal Dura Mater Suture Trainer 4.jpg",
      "/images/A-SDS-01/A-SDS-01_Spinal Dura Mater Suture Trainer 5.jpg",
      "/images/A-SDS-01/A-SDS-01_Spinal Dura Mater Suture Trainer 6.jpg",
      "/images/A-SDS-01/A-SDS-01_Spinal Dura Mater Suture Trainer 7.jpg"
    ]
  },
  {
    id: "ss-34",
    slug: "partial-nephrectomy-trainer",
    name: "Partial Nephrectomy Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Multi-layer kidney trainer with two tumor sites, realistic capsule and parenchyma elasticity, and replaceable kidney module for open, laparoscopic, and robotic partial nephrectomy practice.",
    description: `This partial nephrectomy trainer provides a realistic platform for mastering renal tumor excision and reconstruction. The model replicates renal anatomy, including capsule, parenchyma, and calyx-like structures, offering true-to-life resistance during cutting, dissection, and needle insertion.

Two included tumor sites enable stepwise training from basic to advanced scenarios, allowing practice in excision, defect reconstruction, hemostasis, and parenchymal suturing. Its high-elasticity silicone construction ensures structural stability even after extensive suturing practice, making it suitable for surgical residents, urology trainees, medical schools, and simulation centers.

The replaceable kidney module supports repeated, hygienic long-term training for real-world partial nephrectomy procedures.`,
    features: [
      "Anatomically inspired multi-layer kidney structure",
      "Realistic elasticity providing lifelike tactile feedback during incision and suturing",
      "Includes two tumor sites for varied surgical scenarios and difficulty control",
      "Designed for repeated use without deformation",
      "Suitable for open, laparoscopic, and robotic surgical training"
    ],
    image: "/images/A-PNE-01/A-PNE-01_Partial Nephrectomy Trainer 1.jpg",
    gallery: [
      "/images/A-PNE-01/A-PNE-01_Partial Nephrectomy Trainer 1.jpg",
      "/images/A-PNE-01/A-PNE-01_Partial Nephrectomy Trainer 2.jpg",
      "/images/A-PNE-01/A-PNE-01_Partial Nephrectomy Trainer 3.jpg",
      "/images/A-PNE-01/A-PNE-01_Partial Nephrectomy Trainer 4.jpg",
      "/images/A-PNE-01/A-PNE-01_Partial Nephrectomy Trainer 5.jpg"
    ]
  },
  {
    id: "ss-33",
    slug: "pancreatojejunostomy-anastomosis-plate",
    name: "Pancreatojejunostomy Anastomosis Plate",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Anatomically accurate pancreatojejunostomy trainer with removable silicone pancreas and jejunum, designed for duct-to-mucosa anastomosis, reconstruction practice, and fine suturing in confined spaces.",
    description: `This pancreatojejunostomy anastomosis plate is engineered for practicing reconstruction techniques following pancreatic resection. The model mimics true anatomical positioning between the pancreas and jejunum, supporting duct cannulation, fine suturing, and realistic anastomosis workflow.

High-elasticity silicone reproduces tissue resistance during needle passage and tightening, offering an authentic surgical experience. Its removable organ components allow hygienic maintenance and repeated use without deforming, making it ideal for surgical residency programs, hepatobiliary specialists, and simulation centers.`,
    features: [
      "Anatomically accurate structure reflecting pancreas–jejunum orientation",
      "Realistic tissue elasticity for lifelike incision and suturing",
      "Ideal for duct-to-mucosa anastomosis and delicate reconstruction",
      "Removable silicone organs for hygiene, replacement, and long-term repetitive use",
      "Durable material designed for professional surgical training"
    ],
    image: "/images/A-PAP-01/A-PAP-01_1.jpg",
    gallery: [
      "/images/A-PAP-01/A-PAP-01_1.jpg",
      "/images/A-PAP-01/A-PAP-01_2.jpg",
      "/images/A-PAP-01/A-PAP-01_3.jpg",
      "/images/A-PAP-01/A-PAP-01_4.jpg",
      "/images/A-PAP-01/A-PAP-01_Spec.jpg"
    ]
  },
  {
    id: "ss-32",
    slug: "pancreas-anastomosis-trainer",
    name: "Pancreas Anastomosis Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "High-fidelity single-piece silicone pancreas trainer with anatomically accurate embedded duct for practicing pancreatic anastomosis, duct suturing, and soft-tissue reconstruction techniques.",
    description: `This trainer provides a high-fidelity platform for practicing pancreatic duct suturing and anastomosis techniques. The internal duct system mimics real pancreatic anatomy, offering realistic resistance and tissue behavior during incision and suturing. The soft silicone parenchyma supports natural needle penetration and tension control, enabling repeated training without deformation.

Its durable, single-piece structure is compatible with tissue stands, laparoscopic systems, and other surgical simulators, making it suitable for surgical residency programs, medical schools, and clinical demonstration environments focused on advanced pancreatic anastomosis skills.`,
    features: [
      "Soft-tissue response replicating pancreatic parenchyma",
      "Anatomically accurate embedded duct structure",
      "High-elasticity silicone ideal for repeated suturing practice",
      "Single-piece construction for easy cleaning and maintenance",
      "Suitable for open, laparoscopic, and robotic anastomosis training"
    ],
    image: "/images/A-PAA-01/A-PAA-01_Pancreas Anatomosis Trainer 1.jpg",
    gallery: [
      "/images/A-PAA-01/A-PAA-01_Pancreas Anatomosis Trainer 1.jpg",
      "/images/A-PAA-01/A-PAA-01_Pancreas Anatomosis Trainer 2.jpg",
      "/images/A-PAA-01/A-PAA-01_Pancreas Anatomosis Trainer 3(Example of use).jpg",
      "/images/A-PAA-01/A-PAA-01_Pancreas Anatomosis Trainer Spec.jpg"
    ]
  },
  {
    id: "ss-31",
    slug: "ostomy-care-model",
    name: "Ostomy Care Model",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Realistic abdominal stoma model with lifelike silicone texture, compatible with commercial ostomy bags, for training in pouch replacement, stoma cleansing, and peri-stomal skin care.",
    description: `This ostomy care model accurately replicates the anatomy and tactile feel of a human abdominal stoma, providing a realistic platform for training in cleaning, pouch application, skin care, and device replacement. Compatible with commonly used ostomy bags and adhesive systems, the model enables repetitive, hygienic practice of attachment and removal techniques.

Durable silicone construction ensures long-term use, making it ideal for nursing programs, caregiver instruction, patient education, and medical device demonstrations.`,
    features: [
      "Realistic stoma structure with lifelike texture and elasticity",
      "High-elasticity silicone suitable for repeated cleaning and device application",
      "Compatible with commercial ostomy bags and accessories",
      "Lightweight design for easy transport and classroom use",
      "Ideal for nursing education, clinical demonstration, and patient training"
    ],
    image: "/images/A-OSC-01/A-OSC-01_1.jpg",
    gallery: [
      "/images/A-OSC-01/A-OSC-01_1.jpg",
      "/images/A-OSC-01/A-OSC-01_2.jpg",
      "/images/A-OSC-01/A-OSC-01_3.jpg"
    ]
  },
  {
    id: "ss-30",
    slug: "laparoscopic-portable-trainer-v2",
    name: "Laparoscopic Portable Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Portable laparoscopic trainer with 11 trocar-compatible ports, integrated camera and lighting, tissue stand, and lightweight ABS frame compatible with Android, tablet, and PC visualization.",
    description: `This portable laparoscopic trainer is engineered for realistic, accessible practice in minimally invasive surgery. The silicone upper panel contains 11 trocar-compatible ports that replicate abdominal wall resistance, while the integrated camera system delivers high-quality visualization for simulated intra-abdominal procedures.

Its compact, lightweight design enables rapid setup in hospitals, classrooms, workshops, and conference environments. The included tissue stand supports key laparoscopic tasks including incision, suturing, knot-tying, clip application, and instrument coordination, making it suitable for beginner-to-intermediate training levels.

Durable and reusable, this trainer is ideal for medical schools, surgical residency programs, and device evaluation settings requiring portable simulation capability.`,
    features: [
      "Multiple trocar port configurations simulate real laparoscopic environments",
      "Integrated camera and lighting system provides a clear operative field",
      "Lightweight, portable structure suitable for hospitals, classrooms, and conferences",
      "Durable silicone and ABS frame for extended, repetitive use",
      "Includes tissue stand for incision, suturing, knot-tying, clip insertion, and more",
      "Compatible with Android devices, tablets, and PCs for real-time visualization"
    ],
    image: "/images/A-LPP-02/A-LPP-02_Laparoscopic Portable Trainer 1.jpg",
    gallery: [
      "/images/A-LPP-02/A-LPP-02_Laparoscopic Portable Trainer 1.jpg",
      "/images/A-LPP-02/A-LPP-02_Laparoscopic Portable Trainer 2.jpg",
      "/images/A-LPP-02/A-LPP-02_Laparoscopic Portable Trainer 3.jpg",
      "/images/A-LPP-02/A-LPP-02_Laparoscopic Portable Trainer 4.jpg",
      "/images/A-LPP-02/A-LPP-02_Laparoscopic Portable Trainer 5.jpg",
      "/images/A-LPP-02/A-LPP-02_Laparoscopic Portable Trainer 6.jpg",
      "/images/A-LPP-02/A-LPP-02_Laparoscopic Portable Trainer 7.jpg"
    ]
  },
  {
    id: "ss-29",
    slug: "laparoscopic-portable-trainer",
    name: "Laparoscopic Portable Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Light and compact portable laparoscopic trainer with adjustable height and angle, replaceable trocar insertion skinpad, and included SONY camera and 15\" digital monitor.",
    description: `This laparoscopic portable trainer is light and compact — all components fit in a single bag for easy transport anywhere. The height of the inner surface and the angle of the upper board are adjustable by screws, and transparency can be controlled by covering the upper board with the included black thin sponge.

The trocar insertion skinpad is easily replaceable, allowing repeated use of all other components. The set includes a SONY A-300 camera, a 15" digital monitor, and a large tissue stand for complete out-of-the-box training capability.`,
    features: [
      "Light and compact — fully portable in one bag",
      "Camera and 15\" digital monitor included",
      "Adjustable inner surface height and upper board angle",
      "Selectable transparency with included black sponge cover",
      "Replaceable trocar insertion skinpad for long-term reuse"
    ],
    image: "/images/A-LPP-01(Discontinue)/A-LPP-01_Laparoscopic Portable Trainer 1.jpg",
    gallery: [
      "/images/A-LPP-01(Discontinue)/A-LPP-01_Laparoscopic Portable Trainer 1.jpg",
      "/images/A-LPP-01(Discontinue)/A-LPP-01_Laparoscopic Portable Trainer 2.jpg",
      "/images/A-LPP-01(Discontinue)/A-LPP-01_Laparoscopic Portable Trainer 3.jpg",
      "/images/A-LPP-01(Discontinue)/A-LPP-01_Laparoscopic Portable Trainer 4.jpg",
      "/images/A-LPP-01(Discontinue)/A-LPP-01_Laparoscopic Portable Trainer 5.jpg",
      "/images/A-LPP-01(Discontinue)/A-LPP-01_Laparoscopic Portable Trainer 6.jpg",
      "/images/A-LPP-01(Discontinue)/A-LPP-01_Laparoscopic Portable Trainer 7.jpg",
      "/images/A-LPP-01(Discontinue)/A-LPP-01_Laparoscopic Portable Trainer Spec.jpg"
    ]
  },
  {
    id: "ss-28",
    slug: "laparoscopic-port-closure-trainer",
    name: "Laparoscopic Port Closure Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Multi-layer silicone laparoscopic port closure trainer with acrylic stand, simulating skin, fat, muscle, and fascia for realistic trocar-site repair and layered closure practice.",
    description: `This laparoscopic port closure trainer simulates realistic trocar-site repair using a multilayer construction that includes skin, subcutaneous fat, muscle, and fascia. Designed for accurate tactile feedback, the model allows trainees to practice layered closure, incision repair, and knot tying with both laparoscopic and standard suturing tools.

The included acrylic stand enhances visualization by allowing bottom and angled viewing during instrument handling. Durable and reusable, the trainer is ideal for surgical residents, medical schools, and demonstration environments requiring a reliable platform for mastering laparoscopic port-site closure skills.`,
    features: [
      "Multi-layer silicone construction reproducing realistic human tissue",
      "Provides lifelike resistance and tension during incision and suturing",
      "Supports closure after trocar insertion and removal",
      "Highly durable for repeated long-term practice",
      "Suitable for surgical education, residency programs, and device demonstration"
    ],
    image: "/images/A-LPO-01/A-LPO-01_Laparoscopic Port Closure Trainer 1.jpg",
    gallery: [
      "/images/A-LPO-01/A-LPO-01_Laparoscopic Port Closure Trainer 1.jpg",
      "/images/A-LPO-01/A-LPO-01_Laparoscopic Port Closure Trainer 2.jpg",
      "/images/A-LPO-01/A-LPO-01_Laparoscopic Port Closure Trainer 3.jpg",
      "/images/A-LPO-01/A-LPO-01_Laparoscopic Port Closure Trainer 4.jpg",
      "/images/A-LPO-01/A-LPO-01_Laparoscopic Port Closure Trainer Spec.jpg"
    ]
  },
  {
    id: "ss-27",
    slug: "laparoscopic-body-type-trainer-large",
    name: "Laparoscopic Body Type Trainer Large",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Large-format laparoscopic body trainer with realistic abdominal curvature, HDMI camera, adjustable wire-frame cavity, and high-durability silicone for advanced MIS surgical training.",
    description: `This large-format laparoscopic body trainer replicates the anatomical curvature and tactile feedback of the human abdomen, providing a highly realistic environment for surgical practice. Its spacious interior allows precise port insertion and trocar manipulation, enabling learners to practice triangulation, suturing, knot-tying, and camera navigation.

The included HDMI camera directly connects to external monitors, simulating real laparoscopic visualization. The adjustable wire-frame system ensures adaptable training conditions by allowing variation in abdominal cavity height.

Designed for repeated, long-term use, this trainer is ideal for medical schools, surgical residency programs, simulation centers, and device demonstration environments requiring high-fidelity MIS training.`,
    features: [
      "Realistic abdominal curvature reproduced in large-body format",
      "High-durability silicone with lifelike skin elasticity and thickness",
      "Supports multi-angle port placement and trocar manipulation",
      "Includes HDMI-compatible camera for real-time laparoscopic viewing",
      "Wire-frame structure allows adjustable cavity height",
      "Suitable for general surgery, gynecology, urology, and MIS education"
    ],
    image: "/images/A-LPL-01/A-LPL-01_Laparoscopic Body Type Trainer Large 2026-1.jpg",
    gallery: [
      "/images/A-LPL-01/A-LPL-01_Laparoscopic Body Type Trainer Large 2026-1.jpg",
      "/images/A-LPL-01/A-LPL-01_Laparoscopic Body Type Trainer Large 2026-2.jpg",
      "/images/A-LPL-01/A-LPL-01_Laparoscopic Body Type Trainer Large 2026-3.jpg",
      "/images/A-LPL-01/A-LPL-01_Laparoscopic Body Type Trainer Large 2026-4.jpg",
      "/images/A-LPL-01/A-LPL-01_Laparoscopic Body Type Trainer Large 2026-5.jpg"
    ]
  },
  {
    id: "ss-26",
    slug: "laparoscopic-hernia-surgery-trainer",
    name: "Laparoscopic Hernia Surgery Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Anatomically accurate laparoscopic hernia trainer with replaceable silicone abdominal wall pad, acrylic board, and trocar stands for mesh insertion, fixation, and inguinal hernia repair practice.",
    description: `This laparoscopic hernia surgery trainer provides a realistic platform for mastering mesh-based inguinal hernia repair. The silicone abdominal wall simulates real tissue elasticity, enabling trainees to practice incision, dissection, mesh placement, and fixation with lifelike tactile feedback.

The included acrylic board and trocar stands allow multi-angle port insertion and stable device setup. Designed for repeated use, the replaceable abdominal wall pad ensures hygienic and long-term training capability. This simulator is ideal for medical schools, surgical residency programs, and demonstration environments requiring high-fidelity laparoscopic training.`,
    features: [
      "Anatomically accurate reproduction of the inguinal hernia region",
      "High-durability silicone abdominal wall pad with realistic tissue resistance",
      "Includes acrylic board and three trocar stands for stable positioning",
      "Suitable for trocar handling, mesh placement, and fixation training",
      "Replaceable abdominal wall pad for repeated hygienic practice",
      "Designed for training surgical residents and medical students"
    ],
    image: "/images/A-LPH-01/A-LPH-01_Laparoscopic Hernia Surgery Trainer 1.jpg",
    gallery: [
      "/images/A-LPH-01/A-LPH-01_Laparoscopic Hernia Surgery Trainer 1.jpg",
      "/images/A-LPH-01/A-LPH-01_Laparoscopic Hernia Surgery Trainer 2.jpg",
      "/images/A-LPH-01/A-LPH-01_Laparoscopic Hernia Surgery Trainer 3.jpg",
      "/images/A-LPH-01/A-LPH-01_Laparoscopic Hernia Surgery Trainer 4(Example of use).jpg",
      "/images/A-LPH-01/A-LPH-01_Laparoscopic Hernia Surgery Trainer 5.jpg",
      "/images/A-LPH-01/A-LPH-01_Laparoscopic Hernia Surgery Trainer Spec.jpg"
    ]
  },
  {
    id: "ss-25",
    slug: "laparoscopic-dome-trainer",
    name: "Laparoscopic Dome Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Dome-shaped laparoscopic trainer with over 60 port locations, realistic silicone abdominal resistance, and spacious inner cavity for endoscopic visualization and multi-angle instrument practice.",
    description: `This laparoscopic dome trainer recreates realistic minimally invasive surgery conditions with its multi-port dome structure. Offering over 60 port locations, it enables trainees to practice trocar insertion, triangulation, suturing, depth perception, and endoscope navigation.

Its silicone-based surface provides realistic resistance during entry and instrument movement, enhancing tactile precision. The spacious inner cavity supports endoscopic visualization using standard camera systems, making it ideal for surgical residents, medical schools, and MIS training centers.`,
    features: [
      "Dome-shaped structure replicating realistic laparoscopic working angles",
      "Over 60 port locations for multi-directional access and difficulty adjustment",
      "Tactile feedback simulating real abdominal wall resistance",
      "High-durability silicone surface for repeated practice",
      "Compatible with standard laparoscopic cameras and visualization systems"
    ],
    image: "/images/A-LPD-01/A-LPD-01_1.jpg",
    gallery: [
      "/images/A-LPD-01/A-LPD-01_1.jpg",
      "/images/A-LPD-01/A-LPD-01_2.jpg",
      "/images/A-LPD-01/A-LPD-01_3.jpg",
      "/images/A-LPD-01/A-LPD-01_4.jpg",
      "/images/A-LPD-01/A-LPD-01_5.jpg",
      "/images/A-LPD-01/A-LPD-01_Spec.png"
    ]
  },
  {
    id: "ss-24",
    slug: "laparoscopic-circle-pad-trainer",
    name: "Laparoscopic Circle Pad Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Compact laparoscopic trainer with replaceable silicone circle pad, semi-cylindrical body for multi-angle port insertion, and realistic abdominal tissue feedback.",
    description: `This circular-pad laparoscopic trainer is engineered for foundational minimally invasive surgery training. The silicone pad replicates the resistance and texture of abdominal skin and subcutaneous layers, supporting realistic practice of incision, suturing, knot-tying, and trocar manipulation.

The semi-cylindrical body allows multi-angle port placement to enhance instrument coordination and laparoscopic spatial awareness. The internal cavity accommodates sufficient working space for realistic endoscopic visualization and tool motion.

Durable and adaptable, this model is ideal for surgical residents, medical schools, training centers, and device demonstrations requiring compact yet realistic laparoscopic simulation.`,
    features: [
      "Realistic tactile feedback simulating human abdominal tissue",
      "Replaceable silicone circle pad for repeated long-term training",
      "Semi-cylindrical trainer body allows port insertion from multiple angles",
      "High-durability silicone construction suitable for extensive practice",
      "Applicable across general surgery, gynecology, urology, and MIS training programs"
    ],
    image: "/images/A-LPC-01/A-LPC-01_Laparoscopic Circle Pad Trainer 1.jpg",
    gallery: [
      "/images/A-LPC-01/A-LPC-01_Laparoscopic Circle Pad Trainer 1.jpg",
      "/images/A-LPC-01/A-LPC-01_Laparoscopic Circle Pad Trainer 2.jpg",
      "/images/A-LPC-01/A-LPC-01_Laparoscopic Circle Pad Trainer 3.jpg",
      "/images/A-LPC-01/A-LPC-01_Laparoscopic Circle Pad Trainer Spec.jpg"
    ]
  },
  {
    id: "ss-23",
    slug: "laparoscopic-body-type-trainer",
    name: "Laparoscopic Body Type Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Anatomically realistic laparoscopic body trainer with adjustable wire-frame cavity, HDMI camera, and durable silicone for trocar insertion, suturing, and instrument handling.",
    description: `This laparoscopic body-type trainer provides a realistic environment for essential laparoscopic skills training. Its silicone surface closely mimics the human abdomen, supporting repeated practice of trocar insertion, suturing, knot-tying, and instrument handling.

The wire‑frame internal system allows height and angle adjustments, enabling simulation of various abdominal conditions and enhancing training flexibility. The included HDMI camera supports external monitor connection, creating an authentic laparoscopic view for practice.

Durable and versatile, this trainer is widely used in medical schools, surgical residency programs, and minimally invasive surgery training centers.`,
    features: [
      "Anatomically realistic abdominal curvature and texture",
      "Adjustable wire-frame system for customizable cavity height and port angles",
      "HDMI-compatible camera for real-time visualization",
      "High-durability silicone suitable for repeated suturing and knot-tying",
      "Applicable for general surgery, gynecology, and urology training"
    ],
    image: "/images/A-LPB-01/A-LPB-01 2026-1.jpg",
    gallery: [
      "/images/A-LPB-01/A-LPB-01 2026-1.jpg",
      "/images/A-LPB-01/A-LPB-01 2026-2.jpg",
      "/images/A-LPB-01/A-LPB-01 2026-3.jpg",
      "/images/A-LPB-01/A-LPB-01 2026-4.jpg"
    ]
  },
  {
    id: "ss-22",
    slug: "linx-model",
    name: "LINX Model",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Transparent gastroesophageal junction model for demonstrating the LINX Reflux Management System, magnetic ring mechanics, and reflux physiology.",
    description: `This model accurately depicts the gastroesophageal junction to demonstrate Ethicon's LINX Reflux Management System. The transparent esophagus and stomach allow clear visualization of content movement and the magnetic opening/closing functionality of the LINX ring, supporting intuitive explanation of reflux physiology and LINX therapy.

Constructed from durable silicone with a reinforced acrylic stand, the model maintains shape through repeated demonstrations, making it highly suitable for medical device representatives, educators, surgeons, and patient consultation scenarios.`,
    features: [
      "Anatomically accurate gastroesophageal junction reproduction",
      "Transparent silicone structure for clear visualization of reflux and LINX magnetic action",
      "Demonstrates magnetic ring opening/closing mechanics",
      "Durable construction for repeated demonstration",
      "Suitable for medical education, device demonstration, and patient explanation"
    ],
    image: "/images/A-LNX-01/A-LNX-01_LINX Model 1.jpg",
    gallery: [
      "/images/A-LNX-01/A-LNX-01_LINX Model 1.jpg",
      "/images/A-LNX-01/A-LNX-01_LINX Model 2.jpg",
      "/images/A-LNX-01/A-LNX-01_LINX Model 3.jpg",
      "/images/A-LNX-01/A-LNX-01_LINX Model 4.jpg",
      "/images/A-LNX-01/A-LNX-01_LINX Model 5.jpg",
      "/images/A-LNX-01/A-LNX-01_LINX Model 6.jpg",
      "/images/A-LNX-01/A-LNX-01_LINX Model 7.jpg",
      "/images/A-LNX-01/A-LNX-01_LINX Model Spec.jpg"
    ]
  },
  {
    id: "ss-20",
    slug: "ercp-trainer",
    name: "ERCP Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Realistic ERCP trainer with anatomically accurate esophagus, stomach, duodenum, bile ducts, and pancreas for cannulation, contrast injection, and stent placement practice.",
    description: `This ERCP trainer provides a realistic platform for practicing endoscopic retrograde cholangiopancreatography. It includes anatomically accurate structures that enable high-fidelity navigation, cannulation, contrast injection, and stent manipulation.

The semi-transparent bile duct system allows visual tracking of guidewire movement, enhancing anatomical understanding. Its silicone construction mimics real clinical resistance, supporting repeated training sessions in medical schools, hospitals, and endoscopy simulation centers.`,
    features: [
      "Realistic anatomy: esophagus, stomach, duodenum, bile ducts, pancreas",
      "Flexible ducts replicate insertion resistance",
      "Transparent acrylic housing for visual monitoring",
      "Supports cannulation, contrast injection, stent placement and removal",
      "Durable silicone construction for repeated training",
      "Adjustable positioning with acrylic stand"
    ],
    image: "/images/A-ERC-01/A-ERC-01_ERCP Trainer 1.jpg",
    gallery: [
      "/images/A-ERC-01/A-ERC-01_ERCP Trainer 1.jpg",
      "/images/A-ERC-01/A-ERC-01_ERCP Trainer 2.jpg",
      "/images/A-ERC-01/A-ERC-01_ERCP Trainer 3.jpg",
      "/images/A-ERC-01/A-ERC-01_ERCP Trainer Spec.jpg"
    ]
  },
  {
    id: "ss-19",
    slug: "double-layer-bowel",
    name: "Double Layer Bowel",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "High-elasticity double-layer bowel model in multiple diameters for realistic bowel incision, suturing, anastomosis, and multi-layer closure training.",
    description: `This double-layer bowel model replicates the muscular and mucosal layers to provide realistic training in bowel incision, suturing, and anastomosis. The model offers lifelike elasticity and tension, enabling precise multi‑layer handling and closure techniques.

Its multiple diameter and length configurations support a wide range of gastrointestinal training scenarios. Constructed with high‑elasticity silicone, the bowel maintains shape even with repeated use, making it ideal for medical schools, surgical training programs, and simulation centers focusing on gastrointestinal surgery skills.`,
    features: [
      "Double-layer construction replicating muscular outer layer and mucosal inner layer",
      "Realistic elasticity and tension during cutting and suturing",
      "Multiple diameters and lengths for varied training scenarios",
      "High-durability silicone suitable for repeated procedures",
      "Applicable for basic and advanced gastrointestinal surgery training"
    ],
    image: "/images/A-DLB-01/A-DLB-01_Double Layer Bowel 1.jpg",
    gallery: [
      "/images/A-DLB-01/A-DLB-01_Double Layer Bowel 1.jpg",
      "/images/A-DLB-01/A-DLB-01_Double Layer Bowel 2.jpg",
      "/images/A-DLB-01/A-DLB-01_Double Layer Bowel Spec.jpg"
    ]
  },
  {
    id: "ss-18",
    slug: "colon-polypectomy-trainer",
    name: "Colon Polypectomy Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "32 cm colon polypectomy trainer with 9 polyps of varying sizes for progressive endoscopic snaring, resection, and retrieval skill development.",
    description: `This Colon Polypectomy Trainer is designed to simulate realistic endoscopic polypectomy procedures. The 32 cm colon model features nine polyps of varying sizes (15 mm, 10 mm, 5 mm), strategically placed to offer progressive difficulty in detection, snaring, and removal.

The internal mucosal surface reproduces realistic anatomical structure and tactile texture, providing lifelike feedback during resection, snaring, and tissue retrieval. Its durable silicone construction ensures long-term hygienic use, making it ideal for medical schools, teaching hospitals, endoscopy training programs, and simulation centers.`,
    features: [
      "32 cm-long colon segment with realistic mucosal texture",
      "Includes 9 polyps (four 15 mm, three 10 mm, two 5 mm) at variable difficulty levels",
      "Realistic resistance during snaring, cutting, and retrieval",
      "Durable silicone suitable for repeated, hygienic practice",
      "Compatible with standard colonoscopy systems"
    ],
    image: "/images/A-CPT-01/A-CPT-01_1.jpg",
    gallery: [
      "/images/A-CPT-01/A-CPT-01_1.jpg",
      "/images/A-CPT-01/A-CPT-01_2.jpg",
      "/images/A-CPT-01/A-CPT-01_3.jpg",
      "/images/A-CPT-01/A-CPT-01_4.jpg",
      "/images/A-CPT-01/A-CPT-01_Spec.png"
    ]
  },
  {
    id: "ss-17",
    slug: "colonoscopy-trainer",
    name: "Colonoscopy Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Full-length two-unit colonoscopy trainer with 15 polyps, realistic bends and narrow passages, and lifelike tactile feedback for diagnostic and procedural colonoscopy mastery.",
    description: `This colonoscopy trainer provides a highly realistic full-length anatomical environment for mastering diagnostic and procedural colonoscopy. Its two-unit structure allows targeted training—from experimental tissue insertion to multi-level lesion detection using 15 included polyps.

The internal lumen design includes angulated segments, rotations, and narrow zones that simulate real clinical difficulty. High-strength acrylic and silicone ensure durability and lifelike tactile response, making the trainer suitable for medical schools, endoscopy programs, and simulation centers.`,
    features: [
      "Replicates human colon length (approximately 85 cm)",
      "Two-unit system: Unit 1 porthole-type for real tissue insertion; Unit 2 includes 15 polyps (7 pedunculated, 4 granular, 3 sessile)",
      "Realistic pathways including bends, rotations, and narrow passages",
      "Lifelike tactile feedback during insertion, withdrawal, and air insufflation",
      "Durable acrylic housing with silicone colon construction",
      "Compatible with standard colonoscopy systems; replaceable modules for hygienic practice"
    ],
    image: "/images/A-COL-01/A-COL-01_1.jpg",
    gallery: [
      "/images/A-COL-01/A-COL-01_1.jpg",
      "/images/A-COL-01/A-COL-01_2.jpg",
      "/images/A-COL-01/A-COL-01_3.jpg",
      "/images/A-COL-01/A-COL-01_4.jpg",
      "/images/A-COL-01/A-COL-01_5.jpg",
      "/images/A-COL-01/A-COL-01_6.jpg",
      "/images/A-COL-01/A-COL-01_7.jpg",
      "/images/A-COL-01/A-COL-01_8.jpg",
      "/images/A-COL-01/A-COL-01_9.jpg",
      "/images/A-COL-01/A-COL-01_10.jpg",
      "/images/A-COL-01/A-COL-01_11.jpg"
    ]
  },
  {
    id: "ss-16",
    slug: "cholecystectomy-trainer",
    name: "Cholecystectomy Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Advanced cholecystectomy trainer with anatomically accurate gallbladder, vessels, and ducts for realistic hands-on gallbladder removal training.",
    description: `Introducing our advanced Cholecystectomy Trainer, an innovative educational tool designed for realistic, hands-on training in gallbladder removal procedures. This comprehensive simulator features detailed anatomical structures, including the gallbladder, and associated vessels and ducts. Users can practice and perfect their skills in a safe, controlled environment. With interchangeable parts and anatomical accuracy, our Cholecystectomy Trainer ensures thorough and repeatable training sessions, enhancing proficiency and confidence in performing cholecystectomies.`,
    features: [
      "Realistic tissue response",
      "Actual human size",
      "High durability",
      "Interchangeable parts for repeated training sessions",
      "Anatomically accurate gallbladder, vessels, and ducts"
    ],
    image: "/images/A-CHO-01(discontinued)/A-CHO-01_1.jpg",
    gallery: [
      "/images/A-CHO-01(discontinued)/A-CHO-01_1.jpg",
      "/images/A-CHO-01(discontinued)/A-CHO-01_2.jpg",
      "/images/A-CHO-01(discontinued)/A-CHO-01_3.jpg",
      "/images/A-CHO-01(discontinued)/A-CHO-01_spec.png"
    ]
  },
  {
    id: "ss-15",
    slug: "appendectomy-trainer",
    name: "Appendectomy Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "High-fidelity appendectomy trainer with anatomically accurate appendix, cecum, and mesoappendix for open and laparoscopic appendectomy skill development.",
    description: `This appendectomy trainer provides a realistic simulation environment for mastering appendectomy procedures. The model accurately recreates abdominal anatomy, enabling step-by-step training in incision, dissection, mesoappendix control, ligation, appendix removal, and suturing.

The appendix and mesoappendix are distinctly separated, supporting precise dissection and ligation skills. The replaceable appendix block allows repeated practice without compromising structural integrity.

When paired with a laparoscopic training system, users can practice camera handling and laparoscopic dissection techniques. This trainer is ideal for medical schools, surgical residency programs, and simulation centers seeking a high-fidelity appendectomy training tool.`,
    features: [
      "Anatomically accurate appendix, cecum, and mesoappendix structures",
      "Clear separation for precise dissection and ligation",
      "Replaceable appendix module for repeated hygienic practice",
      "High-elasticity silicone provides lifelike tissue tension and cutting response",
      "Compatible with laparoscopic trainers for combined training"
    ],
    image: "/images/A-APP-01/A-APP-01_1.jpg",
    gallery: [
      "/images/A-APP-01/A-APP-01_1.jpg",
      "/images/A-APP-01/A-APP-01_2.jpg",
      "/images/A-APP-01/A-APP-01_3.jpg",
      "/images/A-APP-01/A-APP-01_4.jpg",
      "/images/A-APP-01/A-APP-01_Spec.png"
    ]
  },
  {
    id: "ss-14",
    slug: "abdominal-injection-trainer",
    name: "Abdominal Injection Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Strap-attachable abdominal injection trainer with realistic curvature, soft silicone feedback, and internal fluid system for subcutaneous injection skill development.",
    description: `This abdominal injection trainer accurately reproduces human abdominal curvature and tissue resistance, providing a realistic platform for practicing subcutaneous injection techniques. The layered structure reflects anatomical differences between skin and subcutaneous fat, supporting training in needle depth, angle, and pressure control.

The soft silicone material simulates authentic tactile feedback during skin pinching and needle insertion. Its strap-attachable design enables use on human bodies, mannequins, or flat surfaces, recreating realistic clinical environments for insulin injections, medication delivery, and vaccination scenarios.

With an internal fluid-injection and drainage system, this model supports repeated hygienic practice and is ideal for nursing programs, medical schools, and clinical simulation centers.`,
    features: [
      "Realistic skin texture and abdominal curvature",
      "Durable material suitable for repeated injection practice",
      "Internal structure supports simulated fluid injection and absorption",
      "Soft silicone provides lifelike pressure and puncture feedback",
      "Strap-attachable design for stable practice on mannequins, tables, or wearable setups"
    ],
    image: "/images/A-AIT-01/A-AIT-01_Abdominal Injection Trainer 1.jpg",
    gallery: [
      "/images/A-AIT-01/A-AIT-01_Abdominal Injection Trainer 1.jpg",
      "/images/A-AIT-01/A-AIT-01_Abdominal Injection Trainer 2.jpg",
      "/images/A-AIT-01/A-AIT-01_Abdominal Injection Trainer 3.jpg",
      "/images/A-AIT-01/A-AIT-01_Abdominal Injection Trainer 4.jpg",
      "/images/A-AIT-01/A-AIT-01_Abdominal Injection Trainer Spec.jpg"
    ]
  },
  {
    id: "ss-13",
    slug: "abdominal-injection-trainer-small",
    name: "Abdominal Injection Trainer – Small",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Compact strap-wearable abdominal injection trainer for portable subcutaneous injection practice with realistic tissue elasticity and internal fluid drainage.",
    description: `This compact abdominal injection trainer replicates realistic abdominal tissue elasticity and resistance, allowing effective practice of subcutaneous injection skills. Its portable size makes it ideal for personal learning, nursing students, or environments requiring compact training tools.

The strap-wearable design simulates real patient conditions, supporting practice for insulin injections, anticoagulant administration, and vaccine delivery. The internal layered system enables fluid injection, absorption, and drainage for repeated hygienic training.`,
    features: [
      "Realistic human-like skin texture and abdominal elasticity",
      "Compact and lightweight, ideal for portable or personal training",
      "Durable material suitable for repeated injection exercises",
      "Internal structure supports fluid injection and drainage",
      "Strap-wearable design for stable and realistic practice"
    ],
    image: "/images/A-AIS-01/A-AIS-01_Abdominal Injection Trainer-Small 1.jpg",
    gallery: [
      "/images/A-AIS-01/A-AIS-01_Abdominal Injection Trainer-Small 1.jpg",
      "/images/A-AIS-01/A-AIS-01_Abdominal Injection Trainer-Small 2.jpg",
      "/images/A-AIS-01/A-AIS-01_Abdominal Injection Trainer-Small Spec.jpg"
    ]
  },
  {
    id: "ss-12",
    slug: "abdominal-injection-trainer-degree-stand",
    name: "Abdominal Injection Trainer – Degree Stand",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Abdominal injection trainer with degree stand for practicing realistic tissue response, needle depth control, and patient position simulation.",
    description: `This trainer is designed for learning of abdominal injection skill. You can feel the realistic tissue response when you pinch the skin or puncture with needle, you can also inject simulated medication into the trainer. The accumulated liquid can be easily flow out by the drainage hole at the bottom. The included stand provides different degree options for various positions of the patient.`,
    features: [
      "Realistic soft tissue response",
      "Drainage hole for easy liquid removal",
      "High durability",
      "Degree stand for multiple patient position simulations"
    ],
    image: "/images/A-AID-01/A-AID-01_Abdominal injection Traininer-Degree Stand 1.jpg",
    gallery: [
      "/images/A-AID-01/A-AID-01_Abdominal injection Traininer-Degree Stand 1.jpg",
      "/images/A-AID-01/A-AID-01_Abdominal injection Traininer-Degree Stand 2.jpg",
      "/images/A-AID-01/A-AID-01_Abdominal injection Traininer-Degree Stand 3.jpg",
      "/images/A-AID-01/A-AID-01_Abdominal injection Traininer-Degree Stand 4.jpg",
      "/images/A-AID-01/A-AID-01_Abdominal injection Traininer-Degree Stand 5.jpg",
      "/images/A-AID-01/A-AID-01_Abdominal injection Traininer-Degree Stand 6.jpg",
      "/images/A-AID-01/A-AID-01_Abdominal injection Traininer-Degree Stand 7.jpg"
    ]
  },
  {
    id: "ss-11",
    slug: "abdominal-injection-trainer-crease",
    name: "Abdominal Injection Trainer – Crease",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Strap-wearable abdominal injection trainer with realistic skin texture and multi-layer silicone for subcutaneous injection depth, angle, and pressure practice.",
    description: `This trainer replicates the elasticity and tactile response of human abdominal tissue, providing a realistic platform for subcutaneous injection training. Its multi-layer design reflects the anatomical differences between skin and subcutaneous fat, allowing trainees to refine needle depth, angle, and pressure.

The strap-wearable construction enables practice in realistic clinical scenarios, including insulin, anticoagulant, and vaccine injections. The internal silicone system supports simulated fluid injection and absorption, ensuring effective repeated practice.`,
    features: [
      "Realistic skin texture and abdominal elasticity",
      "Durable material for repeated injection training",
      "Internal silicone layer supports fluid injection and absorption",
      "Strap-wearable design replicates real patient conditions"
    ],
    image: "/images/A-AIC-01/A-AIC-01_Abdominal Injection Trainer-Crease 1.jpg",
    gallery: [
      "/images/A-AIC-01/A-AIC-01_Abdominal Injection Trainer-Crease 1.jpg",
      "/images/A-AIC-01/A-AIC-01_Abdominal Injection Trainer-Crease 2.jpg",
      "/images/A-AIC-01/A-AIC-01_Abdominal Injection Trainer-Crease 3.jpg",
      "/images/A-AIC-01/A-AIC-01_Abdominal Injection Trainer-Crease 4.jpg"
    ]
  },
  {
    id: "ss-10",
    slug: "abdominal-injection-trainer-band-attachable",
    name: "Abdominal Injection Trainer – Band Attachable",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Band-attachable abdominal injection trainer for realistic subcutaneous injection practice with lifelike tissue resistance and internal fluid drainage system.",
    description: `This trainer replicates human abdominal tissue resistance and texture, enabling realistic training for subcutaneous injections. Its layered construction allows users to practice precise needle insertion depth, angle, and pressure.

The band-attachable structure supports simulation of real patient scenarios, while also allowing fixed-position training. The internal fluid system supports injection, absorption, and drainage of simulated liquid.

Durable and hygienic, this model is suitable for nursing schools, medical programs, and clinical simulation centers for repeated practice of insulin, anticoagulant, and vaccine injection techniques.`,
    features: [
      "Realistic skin texture and abdominal elasticity",
      "Band-attachable design for flexible training scenarios",
      "Durable material for repeated injection practice",
      "Internal layer supports liquid injection and drainage",
      "Can be worn or fixed in place"
    ],
    image: "/images/A-AIB-01/A-AIB-01_Abdominal Injection Trainer-Band Attachable 1.jpg",
    gallery: [
      "/images/A-AIB-01/A-AIB-01_Abdominal Injection Trainer-Band Attachable 1.jpg",
      "/images/A-AIB-01/A-AIB-01_Abdominal Injection Trainer-Band Attachable 2.jpg"
    ]
  },
  {
    id: "ss-9",
    slug: "abdominal-fascia-suture-trainer",
    name: "Abdominal Fascia Suture Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Multi-layer fascia suture trainer replicating skin, subcutaneous fat, fascia, and muscle for realistic abdominal closure and knot-tying practice.",
    description: `This fascia suture trainer replicates the layered anatomy of skin, subcutaneous tissue, fascia, and muscle to provide a highly realistic environment for abdominal closure training. Users can experience authentic incision resistance and the characteristic tensile response of fascia during suturing.

The fascia layer is engineered to close elastically under tension, enabling accurate needle placement and knot-tying practice. Its replaceable fascia pad supports repeated, hygienic training sessions.`,
    features: [
      "Multi-layer structure: skin, subcutaneous fat, fascia, muscle",
      "Realistic incision resistance and tissue tension",
      "High-elasticity material for repeated suturing without deformation",
      "Natural surface separation and elastic fascia closure",
      "Supports both basic closure and advanced layered repair techniques"
    ],
    image: "/images/A-AFS-01/A-AFS-01_Abdominal Fascia Suture Trainer 1.jpg",
    gallery: [
      "/images/A-AFS-01/A-AFS-01_Abdominal Fascia Suture Trainer 1.jpg",
      "/images/A-AFS-01/A-AFS-01_Abdominal Fascia Suture Trainer 2.jpg",
      "/images/A-AFS-01/A-AFS-01_Abdominal Fascia Suture Trainer 3.jpg",
      "/images/A-AFS-01/A-AFS-01_Abdominal Fascia Suture Trainer Replacement Fascia.jpg"
    ]
  },
  {
    id: "ss-8",
    slug: "abdominal-suture-trainer",
    name: "Abdominal Suture Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Multi-layer abdominal suture trainer simulating epidermis, fascia, and muscle layers for realistic incision and layered closure practice.",
    description: `This suture trainer features multilayer construction simulating realistic abdominal anatomy, providing lifelike resistance for incision and stitching. The elastic silicone pad faithfully reproduces human tissue tension, supporting practice from basic suturing to advanced layered closure.

The replaceable skin pad enables repeated and hygienic training sessions. The internal cavity also allows placement of organ models for deeper-layer suturing and protective techniques, making this trainer ideal for medical, nursing, and surgical training programs.`,
    features: [
      "Multi-layer silicone reproduction of epidermis, dermis, subcutaneous fat, fascia, muscle, abdominal wall",
      "Realistic resistance during cutting and suturing",
      "Supports both basic and advanced suturing techniques",
      "Replaceable pad structure for repeated hygienic use",
      "Suitable for skin closure, fascial repair, and organ protection suturing"
    ],
    image: "/images/A-ABS-01/A-ABS-01_Abdominal suture Trainer 1.jpg",
    gallery: [
      "/images/A-ABS-01/A-ABS-01_Abdominal suture Trainer 1.jpg",
      "/images/A-ABS-01/A-ABS-01_Abdominal suture Trainer 2.jpg",
      "/images/A-ABS-01/A-ABS-01_Abdominal suture Trainer 3.jpg",
      "/images/A-ABS-01/A-ABS-01_Abdominal suture Trainer 4.jpg"
    ]
  },
  {
    id: "ss-7",
    slug: "abdominal-plate-trainer-b",
    name: "Abdominal Plate Trainer B",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Multi-layer abdominal plate trainer with replaceable visceral peritoneum and organ blocks for realistic dissection, resection, and anastomosis training.",
    description: `This trainer provides comprehensive hands-on training for abdominal organ dissection, resection, and suturing. It accurately recreates stomach, liver, pancreas, gallbladder, bowel, vascular structures, and biliary pathways, offering a realistic surgical environment.

High-elasticity silicone replicates lifelike tissue resistance during cutting and suturing. The multi-layer structure includes a replaceable visceral peritoneum, enabling focused training in peritoneal opening and organ mobilization.

Ideal for medical schools, surgical residency programs, and simulation centers, this trainer supports realistic and repeated practice for mastering abdominal surgical skills.`,
    features: [
      "Includes stomach, liver, gallbladder, pancreas, and bowel",
      "Multi-layer design with anatomically precise visceral structures",
      "High-elasticity silicone provides lifelike resistance",
      "Supports resection, mobilization, anastomosis, hemostasis",
      "Replaceable organ blocks and visceral peritoneum for repeated hygienic use",
      "Suitable for open, laparoscopic, and robotic simulation"
    ],
    image: "/images/A-ABP-03/A-ABP-03_1.jpg",
    gallery: [
      "/images/A-ABP-03/A-ABP-03_1.jpg",
      "/images/A-ABP-03/A-ABP-03_2.jpg",
      "/images/A-ABP-03/A-ABP-03_3.jpg",
      "/images/A-ABP-03/A-ABP-03_4.jpg",
      "/images/A-ABP-03/A-ABP-03_5.jpg",
      "/images/A-ABP-03/A-ABP-03_6.jpg",
      "/images/A-ABP-03/A-ABP-03_7.jpg",
      "/images/A-ABP-03/A-ABP-03_8.jpg"
    ]
  },
  {
    id: "ss-6",
    slug: "abdominal-plate-trainer-a",
    name: "Abdominal Plate Trainer A",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Comprehensive abdominal plate trainer for dissection, resection, and suturing of major abdominal organs with anatomically accurate multi-layer silicone structure.",
    description: `This abdominal plate trainer is engineered for comprehensive surgical training in dissection, resection, and suturing of major abdominal organs. The model accurately recreates human abdominal anatomy, including the stomach, liver, pancreas, gallbladder, intestines, vascular structures, and biliary pathways. Users can practice sequential surgical steps—from abdominal incision to organ exposure, resection, and anastomosis—with realistic tactile response thanks to high-elasticity silicone material.

The model incorporates anatomically aligned vascular and biliary connections, enabling simulation of biliary-enteric and pancreato-enteric anastomosis. Replaceable organ blocks, including the gallbladder and pancreas, support repeated and hygienic practice sessions. Ideal for medical schools, surgical residency programs, and simulation centers, this trainer provides a realistic and structured environment for mastering abdominal surgery techniques.`,
    features: [
      "Includes major abdominal organs such as stomach, liver, gallbladder, pancreas, and intestines",
      "High-elasticity silicone provides lifelike resistance during cutting and suturing",
      "Multi-layer structure accurately reproduces peritoneum and organ relationships",
      "Supports a wide range of procedures including dissection, resection, suturing, and anastomosis",
      "Replaceable organ blocks allow repeated training without compromising hygiene",
      "Suitable for open, laparoscopic, and robotic surgery simulation"
    ],
    image: "/images/A-ABP-02/A-ABP-02_1.jpg",
    gallery: [
      "/images/A-ABP-02/A-ABP-02_1.jpg",
      "/images/A-ABP-02/A-ABP-02_2.jpg",
      "/images/A-ABP-02/A-ABP-02_3.jpg",
      "/images/A-ABP-02/A-ABP-02_4.jpg",
      "/images/A-ABP-02/A-ABP-02_5.jpg",
      "/images/A-ABP-02/A-ABP-02_6.jpg"
    ]
  },
  {
    id: "ss-3",
    slug: "abdominal-aorta-aneurysm-open-repair-trainer",
    name: "Abdominal Aorta Aneurysm Open Repair Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Advanced open repair trainer for abdominal aortic aneurysm surgery, featuring realistic silicone tissue, replaceable aneurysm module, and full procedural workflow.",
    description: `This advanced open AAA repair trainer provides a highly realistic environment for mastering abdominal aortic aneurysm surgery. The model accurately replicates the abdominal wall, internal organs, and the anatomical course of the aorta, allowing trainees to practice every step of open AAA repair—including incision, exposure, aneurysm opening, graft insertion, and vascular anastomosis.

When the abdominal wall is opened, users experience natural tissue resistance and realistic organ positioning. The aneurysm component is designed as a replaceable module, enabling repeated incision and suturing practice without compromising structural integrity.

Fully compatible with standard surgical instruments, this model supports comprehensive hands-on education for surgical residents and vascular fellows, providing a high‑fidelity platform that enhances procedural accuracy and confidence.`,
    features: [
      "Anatomically accurate abdominal cavity with visceral organs and aorta",
      "Realistic tissue resistance using advanced silicone formulation",
      "Enables complete open AAA workflow: incision, exposure, aneurysm opening, graft placement, suturing",
      "Replaceable aneurysm module for repeated anastomosis and grafting practice",
      "Compatible with real surgical instruments for a lifelike operative environment",
      "Ideal for vascular surgery residency programs and advanced hands-on training"
    ],
    image: "/images/A-AAA-01/A-AAA-01_1.jpg",
    gallery: [
      "/images/A-AAA-01/A-AAA-01_1.jpg",
      "/images/A-AAA-01/A-AAA-01_3.jpg",
      "/images/A-AAA-01/A-AAA-01_4.jpg",
      "/images/A-AAA-01/A-AAA-01_5.jpg",
      "/images/A-AAA-01/A-AAA-01_6.jpg"
    ]
  },
  {
    id: "ss-4",
    slug: "abdominal-colon-plate-trainer",
    name: "Abdominal Colon Plate Trainer",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "High-fidelity abdominal trainer for bowel resection, anastomosis, and multi-layer dissection with realistic tissue response and replaceable components.",
    description: `This trainer provides a comprehensive environment for practicing abdominal incision, bowel resection, and anastomosis procedures. The multi-layer structure—including peritoneum, small intestine, and colon—is arranged according to human anatomical positioning, enabling realistic dissection, exposure, and suturing.

Users can perform bowel resection and anastomosis with lifelike tissue tension and cutting resistance, closely simulating real operative environments. All components are replaceable, ensuring hygienic and repeated hands-on training. Ideal for medical schools, surgical simulation centers, and clinical training programs.`,
    features: [
      "Replicates human abdominal cavity with realistic organ positioning",
      "Multi-layer structure including colon, small intestine, and peritoneum",
      "Realistic tissue resistance and lifelike bowel surface texture",
      "Supports extensive procedures: resection, anastomosis, suturing, mobilization",
      "Compatible with actual surgical instruments for realistic procedural response"
    ],
    image: "/images/A-ABC-01/A-ABC-01_Abdominal Colon Plate 1.jpg",
    gallery: [
      "/images/A-ABC-01/A-ABC-01_Abdominal Colon Plate 1.jpg",
      "/images/A-ABC-01/A-ABC-01_Abdominal Colon Plate 2.jpg",
      "/images/A-ABC-01/A-ABC-01_Abdominal Colon Plate 3.jpg"
    ]
  },
  {
    id: "ss-5",
    slug: "abdominal-plate-trainer",
    name: "Abdominal Plate Trainer (Intuitive Surgical)",
    category: "Surgical Skills",
    categorySlug: "surgical-skills",
    shortDescription: "Anatomically accurate abdominal plate trainer for robotic surgery skill development, featuring real-size replaceable organs and realistic soft tissue response.",
    description: `This abdominal plate is designed for the skill training of robotic surgery in the abdominal cavity. It has major arteries, veins, pancreas, gall bladder, stomach, and liver at the anatomical location. The real-size organs provide a much more realistic learning experience. All the organ parts are replaceable to make repetitive practice available.`,
    features: [
      "Realistic soft tissue response",
      "High durability",
      "Anatomically accurate structure",
      "Real-size organs",
      "All organ parts are replaceable for repetitive practice"
    ],
    image: "/images/A-ABP-01(Discontinue)/A-ABP-01_Abdominal Plate Trainer 1.jpg",
    gallery: [
      "/images/A-ABP-01(Discontinue)/A-ABP-01_Abdominal Plate Trainer 1.jpg",
      "/images/A-ABP-01(Discontinue)/A-ABP-01_Abdominal Plate Trainer 2.jpg"
    ]
  }
];

const animalBehaviourProducts: Product[] = [
  {
    id: "ab-1",
    slug: "homecage-monitoring-system",
    name: "HomeCage Monitoring System",
    category: "Animal Behaviour",
    categorySlug: "animal-behaviour",
    shortDescription: "Continuous non-invasive home-cage observation system for tracking rodent behavior day and night.",
    description: "The HomeCage Monitoring System enables researchers to continuously observe and quantify rodent behavior in a naturalistic, undisturbed home-cage environment, including night-time activity. Using high-resolution, non-invasive video cameras with infrared capability, the system tracks a comprehensive range of behaviors, such as locomotor activity, sleep-wake cycles, feeding and drinking patterns, grooming, and social interactions.\n\nAdvanced video analysis algorithms automatically extract movement trajectories, zone occupancy, interaction networks, and temporal patterns for individual or group-housed animals. This approach delivers high-resolution, reproducible behavioral data while minimizing stress caused by manual handling or exposure to novel environments.\n\nThe system supports longitudinal studies, allowing researchers to monitor subtle behavioral changes over time, providing insights into circadian rhythms, pharmacological effects, neurodegenerative disease progression, and behavioral phenotyping.",
    features: [
      "24/7 continuous monitoring with day and night (infrared) video",
      "High-resolution, non-invasive video tracking",
      "Sleep-wake cycle and circadian rhythm analysis",
      "Locomotor, feeding, and drinking behavior quantification",
      "Social interaction tracking for individual or group-housed animals",
      "Cloud-based data storage and advanced analysis"
    ],
    image: "/images/HomeCage Monitoring System/Gemini_Generated_Image_new3p3new3p3new3.jpg",
    gallery: [
      "/images/HomeCage Monitoring System/Gemini_Generated_Image_new3p3new3p3new3.jpg",
      "/images/HomeCage Monitoring System/Gemini_Generated_Image_euyxzueuyxzueuyx.jpg",
      "/images/HomeCage Monitoring System/Gemini_Generated_Image_vx2iqlvx2iqlvx2i (1).jpg",
      "/images/HomeCage Monitoring System/Gemini_Generated_Image_rhp39qrhp39qrhp3.jpg"
    ]
  },
  {
    id: "ab-2",
    slug: "maze-video-tracking-system",
    name: "Maze Video Tracking System",
    category: "Animal Behaviour",
    categorySlug: "animal-behaviour",
    shortDescription: "Comprehensive video tracking system for automated rodent maze and behavioral analysis.",
    description: "The Maze Video Tracking System delivers precise, automated analysis of rodent behavior across a wide range of maze tests. It supports Morris Water Maze, Open Field, Elevated Plus Maze, T-Maze, Y-Maze, Novel Object Recognition, and Fear Conditioning, providing comprehensive behavioral insights.\n\nUsing advanced video tracking algorithms, the system captures movement, zone occupancy, distance traveled, velocity, latency, and time-in-zone. Researchers can analyze both live and previously recorded videos, making studies flexible and reproducible.\n\nThe system integrates multiple trusted open-source tools, allowing extraction of custom or study-specific data while maintaining reliability. Only well-validated open-source software is used, ensuring accurate and reproducible results.\n\nWith customizable protocols, automated reporting, and multi-format data export, the Maze Video Tracking System is a versatile platform for learning, memory, anxiety, social behavior, and locomotion studies, suitable for preclinical and longitudinal research.",
    features: [
      "Supports Morris Water Maze, Open Field, Elevated Plus Maze, and more",
      "Real-time and previously recorded video tracking",
      "Integration with open-source software for cost-effective analysis",
      "Automated zone detection and path mapping",
      "Distance, velocity, latency, and time-in-zone metrics",
      "Customizable experiment protocols with flexible data export"
    ],
    image: "/images/Maze Video Tracking System/Gemini_Generated_Image_jj6vrwjj6vrwjj6v.jpg",
    gallery: [
      "/images/Maze Video Tracking System/Gemini_Generated_Image_jj6vrwjj6vrwjj6v.jpg",
      "/images/Gemini_Generated_Image_nk9l3ank9l3ank9l.jpg",
      "/images/Maze Video Tracking System/Gemini_Generated_Image_a5elvma5elvma5el.jpg",
      "/images/Maze Video Tracking System/Gemini_Generated_Image_1p2b5n1p2b5n1p2b.jpg",
      "/images/Maze Video Tracking System/Gemini_Generated_Image_kgjmiikgjmiikgjm.jpg",
      "/images/Maze Video Tracking System/Gemini_Generated_Image_9q410x9q410x9q41.PNG",
      "/images/Maze Video Tracking System/Gemini_Generated_Image_epficgepficgepfi.jpg"
    ]
  }
];

export const categories: Category[] = [
  {
    id: "cat-2",
    slug: "surgical-skills",
    name: "Surgical Skills",
    description: "Comprehensive surgical training models designed to develop precision, confidence, and competence in general surgery, suturing, and laparoscopic procedures.",
    image: "/sklss.jpg",
    products: surgicalSkillsProducts
  },
  {
    id: "cat-1",
    slug: "veterinary-training",
    name: "Veterinary Training",
    description: "Advanced simulation models for veterinary clinical training, designed to support hands-on practice of essential procedures in a realistic learning environment.",
    image: "/images/ana-veteri.jpg",
    products: veterinaryTrainingProducts
  },
  {
    id: "cat-3",
    slug: "animal-behaviour",
    name: "Animal Behaviour",
    description: "Cutting-edge behaviour analysis and tracking systems for research in neuroscience, pharmacology, and animal welfare studies.",
    image: "/images/animal-ansa.jpg",
    products: animalBehaviourProducts
  },
  {
    id: "cat-4",
    slug: "anatomical-models",
    name: "Anatomical Models",
    description: "High-fidelity anatomical models for medical education, surgical training, and veterinary practice.",
    image: "/antm.jpg",
    products: [],
    path: "/anatomical-models"
  }
];

export const getAllProducts = (): Product[] => {
  return categories.flatMap((cat) => cat.products);
};

export const getProductBySlug = (slug: string): Product | undefined => {
  return getAllProducts().find((p) => p.slug === slug);
};

export const getCategoryBySlug = (slug: string): Category | undefined => {
  return categories.find((c) => c.slug === slug);
};
